import * as gui_model from './alan_api';
import * as application from '../../libraries/model_lib/types/application/alan_api';

type SymbolsFile = [string, SymbolsList];
type SymbolsList = Array<[string, string, string, [number, number, number], [number, number, number]]>;

const debug = false;
function isFunction(p: any): p is Function {
	return (typeof p === 'function');
}
function assert(predicate:boolean) {
	if (predicate === false) throw new Error(`Assertion error`);
}
function resolve<T>(context:T) {
	return {
		then: <RT>(callback: (context: T) => RT) => resolve(callback(context)),
		result: context
	};
}
export type dictionary_type<T> = {[key:string]:T};


enum ResolutionStatus {
	Resolved,
	Resolving,
	Unresolved,
	Detached,
}
function cache<T extends IAlanObject>(callback:(detach:boolean) => T) {
	let cached_value:T;
	let status:ResolutionStatus = ResolutionStatus.Unresolved;
	return (detach = false) => {
		switch (status) {
			case ResolutionStatus.Resolving:
				throw new Error(`Cyclic dependency detected!`);
			case ResolutionStatus.Detached: {
				if (detach) break;
				(cached_value as any) = undefined;
				status = ResolutionStatus.Resolving;
				cached_value = callback(detach);
				status = ResolutionStatus.Resolved;
			} break;
			case ResolutionStatus.Resolved: {
				if (!detach) break;
				callback(detach);
				status = ResolutionStatus.Detached;
			} break;
			case ResolutionStatus.Unresolved: {
				if (detach) break;
				status = ResolutionStatus.Resolving;
				cached_value = callback(detach);
				status = ResolutionStatus.Resolved;
			} break;
		}
		return cached_value;
	}
}

function maybe_cache<T extends IAlanObject>(callback:(detach:boolean) => T|undefined) {
	let cached_value:T|undefined;
	let status:ResolutionStatus = ResolutionStatus.Unresolved;
	return (detach = false) => {
		switch (status) {
			case ResolutionStatus.Resolving:
				throw new Error(`Cyclic dependency detected!`);
			case ResolutionStatus.Detached: {
				if (detach) break;
				cached_value = undefined;
				status = ResolutionStatus.Resolving;
				cached_value = callback(detach);
				status = ResolutionStatus.Resolved;
			} break;
			case ResolutionStatus.Resolved: {
				if (!detach) break;
				callback(detach);
				status = ResolutionStatus.Detached;
			} break;
			case ResolutionStatus.Unresolved: {
				if (detach) break;
				status = ResolutionStatus.Resolving;
				cached_value = callback(detach);
				status = ResolutionStatus.Resolved;
			} break;
		}
		return cached_value;
	}
}

/* number validation */
function number__is_positive(val:number) { assert(val > 0); }
function number__is_negative(val:number) { assert(val < 0); }
function number__is_zero(val:number) { assert(val === 0); }
function number__is_non_positive(val:number) { assert(val <= 0); }
function number__is_non_negative(val:number) { assert(val >= 0); }
function number__is_non_zero(val:number) { assert(val !== 0); }

/* complex value wrappers */
export abstract class AlanObject {
	public abstract get path(): string;
	private _destroyed?: true;
	get destroyed(): boolean {
		return this._destroyed ? true : false;
	}
}
export interface IAlanObject {
	readonly path: string;
	readonly destroyed: boolean;
};

export abstract class AlanStruct extends AlanObject {
	public abstract is(other:AlanStruct):boolean;
}
export interface Tree<T> {
	types: {[name:string]:T};
	subtrees: {[name:string]:Tree<T>};
}
export abstract class Reference<T extends IAlanObject, V extends (string|string[])> extends AlanObject {
	constructor(public readonly entry:V, private readonly resolve:() => T, protected readonly parent: AlanNode) { super(); }
	public get ref() { return this.resolve(); }

}
export abstract class AlanInteger extends AlanObject {
	constructor(public readonly value:number, protected readonly parent: AlanNode) { super(); }

}
export abstract class AlanDictionary<T extends {node: AlanDictionaryEntry, init: any }, P extends AlanNode> extends AlanObject {
	protected _entries:Map<string, () => T['node']>;

	constructor (entries:{[key:string]:T['init']}, protected parent:P) {
		super();
		this._entries = new Map(Object.keys(entries).map(entry_key => [entry_key, () => {
			const loaded_entry = this.initialize(this.parent, entry_key, entries[entry_key])
			this._entries.set(entry_key, () => loaded_entry);
			return loaded_entry;
		}]));
	}

	protected abstract initialize(parent: P, key: string, obj: T['init']): T['node'];
	protected abstract finalize(obj: T['node'], detach?: boolean): void;

	get size() { return this._entries.size; }
	[Symbol.iterator](): IterableIterator<[string, T['node']]> {
		const iterator = this._entries.entries();
		return {
			next() {
				const next = iterator.next();
				if (next.done) {
					return next;
				} else {
					return {
						value: [next.value[0], next.value[1]()]
					}
				}
			},
			[Symbol.iterator]() {
				return this;
			}
		};
	}
	entries(): IterableIterator<[string, T['node']]> {
		return this[Symbol.iterator]();
	}
	values(): IterableIterator<T['node']> {
		const iterator = this._entries.values();
		return {
			next() {
				const next = iterator.next();
				if (next.done) {
					return next;
				} else {
					return {
						value: next.value()
					}
				}
			},
			[Symbol.iterator]() {
				return this;
			}
		};
	}
	keys(): IterableIterator<string> {
		return this._entries.keys();
	}
	forEach(walk_function: ($:T['node']) => void) {
		for (const entry of this.values()) {
			walk_function(entry);
		}
	}
	toArray(): [string, T['node']][] {
		return Array.from(this.entries());
	}
	map<RT>(callback: (value: T['node']) => RT): Record<string, RT> {
		const result:Record<string, RT> = {};
		for (const [key, entry] of this.entries()) {
			result[key] = callback(entry);
		}
		return result;
	}
	get(key:string): T['node'] | undefined {
		return this._entries.get(key)?.();
	}
	has(key: string): boolean { return this._entries.has(key); }
	switchOnEntryExists<RT>(key: string, onExists: (($: T['node']) => RT) | Exclude<RT, Function>, onNotExists: (() => RT) | Exclude<RT, Function>): RT {
		const entry = this._entries.get(key);
		if (entry === undefined) {
			return isFunction(onNotExists) ? onNotExists() : onNotExists;
		} else {
			return isFunction(onExists) ? onExists(entry()) : onExists;
		}
	}
}

export abstract class AlanSet<T extends {node: AlanNode, init: any }, P extends AlanNode> extends AlanObject {
	private _entries:Set<T['node']>;
	constructor (
		entries:Array<T['init']>,
		protected parent:P) {
		super();
		this._entries = new Set(entries.map(entry => this.initialize(parent, entry)));
	}

	protected abstract initialize(parent:P, obj:T['init']):T['node'];
	protected abstract finalize(obj:T['node'],detach?:boolean):void;

	get size() { return this._entries.size; }
	[Symbol.iterator]() {
		return this._entries[Symbol.iterator]();
	}
	entries() {
		return this._entries.entries();
	}
	forEach(walk_function: ($:T['node']) => void) {
		this._entries.forEach(walk_function);
	}
	has(key:T['node']):boolean { return this._entries.has(key); }
}
type DistributiveOmit<T, K extends keyof any> = T extends any ? Omit<T, K> : never;
export abstract class StateGroup<T extends {name:string, node:AlanNode & {parent:AlanNode}, init:any}> extends AlanObject {
	public state: DistributiveOmit<T,'init'>;
	public readonly root:Cgui_model
	private init(state_name:T['name'], init:T['init'], parent:AlanNode) {
		this.state = {
			name: state_name,
			node: this.initializer(state_name)(init, parent),
		} as DistributiveOmit<T,'init'>;
	}
	constructor (s:[T['name'],T['init']]|T['name'], protected readonly parent: AlanNode, readonly name: string) {
		super();
		this.root = parent.root
		const state_name:T['name'] = (typeof s === 'string') ? s : s[0];
		this.init(state_name, typeof s === 'string' ? {} : s[1], parent);
	}

	protected abstract initializer(state_name:T['name']): ($:T['init'], parent:AlanNode) => T['node'];
	protected abstract finalizer(state:T['name']): ($:T['node'], detach?:boolean) => void;

	switch<TS> (cases:{[K in T['name']]:(($:Extract<T, {name:K}>['node']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		const handler = cases[this.state.name as T['name']];
		if (isFunction(handler)) {
			return handler(this.state.node);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	cast<S extends T['name']>(state:S):Extract<T, {name:S}>['node'] {
		if (this.destroyed) throw new Error("Trying to access destroyed property! " + this.path);
		if (this.state.name === state) {
			return this.state.node;
		} else {
			throw new Error(`Invalid cast to state '${state}'; actual state is '${this.state.name}' at ${this.state.node.path} .`);
		}
	}
}

export abstract class AlanCombinator extends AlanObject {}
export abstract class AlanNode extends AlanStruct {
	protected _root:Cgui_model|undefined;
	public abstract get root():Cgui_model;
	public abstract get entity():AlanNode;
	public is(other:AlanNode):boolean {
		return this === other;
	}
}

export abstract class AlanDictionaryEntry extends AlanNode {
	public abstract key:(string|Reference<IAlanObject,string>);
	public abstract get key_value():string;
};
export abstract class AlanTopology<T extends { node:AlanDictionaryEntry, init:any }, P extends AlanNode, G extends string> extends AlanDictionary<T,P> {
	protected abstract graph_iterator(key:string):(entry:T['node']) => T['node'];
	private findFirst(iterator:(entry:T['node']) => T['node']) {
		const key_of = (entry:T['node']) => typeof entry.key === 'string' ? entry.key : entry.key.entry;
		let done:Record<string, boolean> = {};
		let first:T['node']|undefined = undefined;
		for (let [entry_id, current] of this) {
			if (!done[entry_id]) {
				first = current;
				while (current && !done[entry_id]) {
					done[entry_id] = true;
					current = iterator(current);
					if (current !== undefined) {
						entry_id = key_of(current);
					}
				}
			}
		}
		return first;
	}
	topo_entries(graph:G, first?:T['node']):IterableIterator<T['node']> {
		let iterator = this.graph_iterator(graph);
		let current = first || this.findFirst(iterator);
		return {
			next() {
				if (current !== undefined) {
					const entry = current;
					current = iterator(current);
					return {
						value: entry
					};
				} else {
					return {
						done: true,
						value: undefined
					}
				}
			},
			[Symbol.iterator]() {
				return this;
			}
		};
	}
	topo_forEach(graph:G, walk_function: ($:T['node']) => void, first?:T['node']) {
		for (let entry of this.topo_entries(graph, first)) { walk_function(entry); }
	}
	topo_toArray(graph:G, first?:T['node']):T['node'][] {
		return Array.from(this.topo_entries(graph, first));
	}
}

/* alan objects */
type Vgui_context = { name: 'where rule', definition: gui_model.Crules}|{ name: 'gui node', definition: gui_model.Cgui_node}
export class Cgui_context extends AlanStruct {
	constructor(
		public readonly variant:Vgui_context, public input: {
			gui_entity: () => gui_model.Cgui_node
		}) { super(); }
	public readonly output:{
		gui_entity: () => gui_model.Cgui_node;
	} = {
		gui_entity: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.gui_entity()).result!)
	};
	public cast<K extends Vgui_context['name']>(_variant:K):Extract<Vgui_context, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vgui_context['name']]:(($:Extract<Vgui_context, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'gui context'`; }
	public is(other:Cgui_context):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export type Twhere_rules = {
	'rules':Record<string, Trules>;
};
export class Cwhere_rules extends AlanNode {
	public readonly properties:{
		readonly rules:Cwhere_rules.Drules
	};
	constructor(init:Twhere_rules, public location:AlanNode, public input: {
		definition: () => application.Cwhere_rules_definition
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			rules: new Cwhere_rules.Drules(init['rules'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export class Krules extends Reference<application.Crules, string> {
	public readonly inferences:{
		node: () => application.Cnode__application
	}
	constructor(key:string, $this:Crules) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.component_root.input.definition())
			.then(context => {
				const entry = context?.properties.rules.get(this.entry)!;
				return resolve(entry).result;
			}).result!), $this.parent)
		this.inferences = {
			node: cache((detach:boolean) => {
				const gui_model__where_rules__rules_key_nval = $this.key.ref;
				return resolve($this.key.ref)
					.then(context => gui_model__where_rules__rules_key_nval)
					.then(context => context?.properties.type?.state.node.output.referenced_node_type()).result!;
			})

		}
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'rules'::key`; }
}
export type Trules = {
	'referenced node':Tgui_node_type_path;
	'validate':'no'|['no', {}]|'yes'|['yes', {}];
};
export class Crules extends AlanDictionaryEntry {
	public key:Krules;
	public get key_value() { return this.key.entry; }
	public get inferences() { return this.key.inferences; }
	public definitions:{
		gui_context: Cgui_context;
	} = {
		gui_context: new Cgui_context({name:'where rule', definition: this}, {
			gui_entity: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.properties.referenced_node)
				.then(context => context?.component_root.output.result_gui_node())
				.then(context => context?.component_root.output.gui_entity()).result!)
		})
	}
	public readonly properties:{
		readonly referenced_node:Cgui_node_type_path & { readonly inferences: {
			constraint: () => application.Cnode__application;
		} },
		readonly validate:Crules.Dvalidate
	};
	constructor(key:string, init:Trules, public parent:Cwhere_rules) {
		super();
		const $this = this;
		this.key = new Krules(key, $this);
		this.properties = {
			referenced_node: new Crules.Dreferenced_node(init['referenced node'], $this),
			validate: new Crules.Dvalidate(init['validate'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'rules'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Tno__validate__rules = {
};
export class Cno__validate__rules extends AlanNode {
	constructor(init:Tno__validate__rules, public parent:Crules) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'validate'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__validate__rules = {
};
export class Cyes__validate__rules extends AlanNode {
	constructor(init:Tyes__validate__rules, public parent:Crules) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'validate'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Ttext_validation = {
	'regular expression':string;
};
export class Ctext_validation extends AlanNode {
	public readonly properties:{
		readonly regular_expression:string
	};
	constructor(init:Ttext_validation, public location:AlanNode, private key: string) {
		super();
		const $this = this;
		this.properties = {
			regular_expression: init['regular expression']
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
function evaluate__select_node_type(detach:boolean, gui_model__select_node_type_nval:gui_model.Cattributes, ):gui_model.Coptional_node {
	const self = { component_root: {} };
	return resolve(self)
		.then(context => gui_model__select_node_type_nval)
		.then(context => context?.properties.type)
		.then(context => {
			const state_group = context;
			switch (state_group?.state.name) {
				case 'action': {
					const gui_model__select_node_type___action_nval = state_group.cast('action');
					return resolve(context)
						.then(context => gui_model__select_node_type___action_nval)
						.then(context => context?.properties.parameters)
						.then(context => {
							const conv_context = resolve(context).result!;
							return new Coptional_node({name: 'node', definition: conv_context});
						}).result;
				}
				case 'command': {
					const gui_model__select_node_type___command_nval = state_group.cast('command');
					return resolve(context)
						.then(context => gui_model__select_node_type___command_nval)
						.then(context => context?.properties.type)
						.then(context => {
							const state_group = context;
							switch (state_group?.state.name) {
								case 'component': {
									return resolve(context)
										.then(() => self)
										.then(() => gui_model.Coptional_node.Pnone).result;
								}
								case 'global': {
									const gui_model__select_node_type___command___global_nval = state_group.cast('global');
									return resolve(context)
										.then(context => gui_model__select_node_type___command___global_nval)
										.then(context => context?.properties.parameters)
										.then(context => {
											const conv_context = resolve(context).result!;
											return new Coptional_node({name: 'node', definition: conv_context});
										}).result;
								}
								case undefined: return undefined;
								default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
							};}).result;
				}
				case 'property': {
					const gui_model__select_node_type___property_nval = state_group.cast('property');
					return resolve(context)
						.then(context => gui_model__select_node_type___property_nval)
						.then(context => context?.properties.type)
						.then(context => {
							const state_group = context;
							switch (state_group?.state.name) {
								case 'collection': {
									const gui_model__select_node_type___property___collection_nval = state_group.cast('collection');
									return resolve(context)
										.then(context => gui_model__select_node_type___property___collection_nval)
										.then(context => context?.properties.node)
										.then(context => {
											const conv_context = resolve(context).result!;
											return new Coptional_node({name: 'node', definition: conv_context});
										}).result;
								}
								case 'file': {
									return resolve(context)
										.then(() => self)
										.then(() => gui_model.Coptional_node.Pnone).result;
								}
								case 'group': {
									const gui_model__select_node_type___property___group_nval = state_group.cast('group');
									return resolve(context)
										.then(context => gui_model__select_node_type___property___group_nval)
										.then(context => context?.properties.gui_node)
										.then(context => {
											const conv_context = resolve(context).result!;
											return new Coptional_node({name: 'node', definition: conv_context});
										}).result;
								}
								case 'number': {
									return resolve(context)
										.then(() => self)
										.then(() => gui_model.Coptional_node.Pnone).result;
								}
								case 'state group': {
									return resolve(context)
										.then(() => self)
										.then(() => gui_model.Coptional_node.Pnone).result;
								}
								case 'text': {
									return resolve(context)
										.then(() => self)
										.then(() => gui_model.Coptional_node.Pnone).result;
								}
								case undefined: return undefined;
								default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
							};}).result;
				}
				case 'reference set': {
					return resolve(context)
						.then(() => self)
						.then(() => gui_model.Coptional_node.Pnone).result;
				}
				case undefined: return undefined;
				default: throw new Error(`Unexpected state '${(state_group!.state as any).name}'`);
			};}).result!
}
type Voptional_node = { name: 'none', definition: (typeof Coptional_node.Pnone)}|{ name: 'node', definition: gui_model.Cgui_node}
export class Coptional_node extends AlanStruct {
	public static Pnone:Coptional_node = new class PrimitiveInstance extends Coptional_node {
		constructor () {
			super({name: 'none', definition: undefined as unknown as Coptional_node})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Voptional_node) { super(); }
	public cast<K extends Voptional_node['name']>(_variant:K):Extract<Voptional_node, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Voptional_node['name']]:(($:Extract<Voptional_node, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'optional node'`; }
	public is(other:Coptional_node):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Cnone__optional_node = gui_model.Coptional_node;
type Vgui_node_location = { name: 'operation', definition: gui_model.Coperation}|{ name: 'group', definition: gui_model.Cgroup__type__property}|{ name: 'state', definition: gui_model.Cstates__state_group__type__property__type__attributes}|{ name: 'collection', definition: gui_model.Ccollection__type__property}|{ name: 'root', definition: gui_model.Cgui_model}
export class Cgui_node_location extends AlanStruct {
	constructor(
		public readonly variant:Vgui_node_location) { super(); }
	public cast<K extends Vgui_node_location['name']>(_variant:K):Extract<Vgui_node_location, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vgui_node_location['name']]:(($:Extract<Vgui_node_location, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'gui node location'`; }
	public is(other:Cgui_node_location):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
type Voperation = { name: 'command', definition: gui_model.Ccommand__type}|{ name: 'action', definition: gui_model.Caction__type}
export class Coperation extends AlanStruct {
	constructor(
		public readonly variant:Voperation) { super(); }
	public definitions:{
		gui_node_location: Cgui_node_location;
	} = {
		gui_node_location: new Cgui_node_location({name:'operation', definition: this})
	}
	public cast<K extends Voperation['name']>(_variant:K):Extract<Voperation, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Voperation['name']]:(($:Extract<Voperation, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'operation'`; }
	public is(other:Coperation):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export type Tobject_expression = {
	'properties':Record<string, Tproperties__object_expression>;
};
export class Cobject_expression extends AlanNode {
	public readonly properties:{
		readonly properties:Cobject_expression.Dproperties
	};
	constructor(init:Tobject_expression, public location:AlanNode, public input: {
		gui_action: () => gui_model.Caction__type,
		object_expression: () => application.Cui_object_expression
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			properties: new Cobject_expression.Dproperties(init['properties'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export class Kproperties__object_expression extends Reference<application.Cproperties__ui_object_expression, string> {
	constructor(key:string, $this:Cproperties__object_expression) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.component_root.input.object_expression())
			.then(context => {
				const entry = context?.properties.properties.get(this.entry)!;
				return resolve(entry).result;
			}).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'properties'::key`; }
}
export type Tproperties__object_expression = {
	'default':Tgui_action_expression;
};
export class Cproperties__object_expression extends AlanDictionaryEntry {
	public key:Kproperties__object_expression;
	public get key_value() { return this.key.entry; }
	public readonly properties:{
		readonly default_:Cgui_action_expression
	};
	constructor(key:string, init:Tproperties__object_expression, public parent:Cobject_expression) {
		super();
		const $this = this;
		this.key = new Kproperties__object_expression(key, $this);
		this.properties = {
			default_: new Cproperties__object_expression.Ddefault_(init['default'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'properties'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Tnumber_limit = {
	'type':['dynamic', Tdynamic]|['static', Tstatic__type__number_limit];
};
export class Cnumber_limit extends AlanNode {
	public readonly properties:{
		readonly type:Cnumber_limit.Dtype
	};
	constructor(init:Tnumber_limit, public location:AlanNode, public input: {
		numerical_type: () => gui_model.Cnumerical_types
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			type: new Cnumber_limit.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tdynamic = {
	'base':'now'|['now', {}]|'today'|['today', {}];
	'with offset':'no'|['no', {}]|['yes', Tyes__with_offset];
};
export class Cdynamic extends AlanNode {
	public readonly properties:{
		readonly base:Cdynamic.Dbase,
		readonly with_offset:Cdynamic.Dwith_offset
	};
	constructor(init:Tdynamic, public parent:Cnumber_limit) {
		super();
		const $this = this;
		this.properties = {
			base: new Cdynamic.Dbase(init['base'], $this),
			with_offset: new Cdynamic.Dwith_offset(init['with offset'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'dynamic'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnow__base = {
};
export class Cnow__base extends AlanNode {
	public readonly inferences:{
		date_and_time_type: () => gui_model.Cdate_and_time
	} = {
		date_and_time_type: cache((detach:boolean) => {
			const gui_model__number_limit__type__dynamic__base__now_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.numerical_type())
				.then(context => {
					if (context?.properties.representation_type.state.name === 'date and time') {
						return resolve(context.properties.representation_type.state.node as gui_model.Cdate_and_time).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tnow__base, public parent:Cdynamic) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'base'?'now'`; }
	public get entity() { return this.parent.entity; }
}
export type Ttoday__base = {
};
export class Ctoday__base extends AlanNode {
	public readonly inferences:{
		date_type: () => gui_model.Cdate
	} = {
		date_type: cache((detach:boolean) => {
			const gui_model__number_limit__type__dynamic__base__today_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.numerical_type())
				.then(context => {
					if (context?.properties.representation_type.state.name === 'date') {
						return resolve(context.properties.representation_type.state.node as gui_model.Cdate).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Ttoday__base, public parent:Cdynamic) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'base'?'today'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__with_offset = {
};
export class Cno__with_offset extends AlanNode {
	constructor(init:Tno__with_offset, public parent:Cdynamic) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'with offset'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__with_offset = {
	'offset':number;
};
export class Cyes__with_offset extends AlanNode {
	public readonly properties:{
		readonly offset:Cyes__with_offset.Doffset
	};
	constructor(init:Tyes__with_offset, public parent:Cdynamic) {
		super();
		const $this = this;
		this.properties = {
			offset: new Cyes__with_offset.Doffset(init['offset'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'with offset'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstatic__type__number_limit = {
	'limit':number;
};
export class Cstatic__type__number_limit extends AlanNode {
	public readonly properties:{
		readonly limit:Cstatic__type__number_limit.Dlimit
	};
	constructor(init:Tstatic__type__number_limit, public parent:Cnumber_limit) {
		super();
		const $this = this;
		this.properties = {
			limit: new Cstatic__type__number_limit.Dlimit(init['limit'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'static'`; }
	public get entity() { return this.parent.entity; }
}
type Vmember_parent = { name: 'gui node', definition: gui_model.Cgui_node}|{ name: 'none', definition: (typeof Cmember_parent.Pnone)}
export class Cmember_parent extends AlanStruct {
	public static Pnone:Cmember_parent = new class PrimitiveInstance extends Cmember_parent {
		constructor () {
			super({name: 'none', definition: undefined as unknown as Cmember_parent})
			this.variant.definition = this;
		}
	}
	constructor(
		public readonly variant:Vmember_parent) { super(); }
	public cast<K extends Vmember_parent['name']>(_variant:K):Extract<Vmember_parent, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vmember_parent['name']]:(($:Extract<Vmember_parent, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'member parent'`; }
	public is(other:Cmember_parent):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export import Cnone__member_parent = gui_model.Cmember_parent;
type Vmember = { name: 'root', definition: gui_model.Cgui_model}|{ name: 'attribute', definition: gui_model.Cattributes}
export class Cmember extends AlanStruct {
	constructor(
		public readonly variant:Vmember, public input: {
			parent: () => gui_model.Cmember_parent
		}) { super(); }
	public readonly output:{
		parent: () => gui_model.Cmember_parent;
	} = {
		parent: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.parent()).result!)
	};
	public cast<K extends Vmember['name']>(_variant:K):Extract<Vmember, {name:K}>['definition'] {
		return this.variant.definition as any;
	}
	switch<TS> (cases:{[K in Vmember['name']]:(($:Extract<Vmember, {name:K}>['definition']) => TS) | (() => TS) | Exclude<TS, Function>}):TS {
		const handler = cases[this.variant.name];
		if (isFunction(handler)) {
			return handler(this.variant.definition as any);
		} else {
			return handler as Exclude<TS, Function>;
		}
	}
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `.'member'`; }
	public is(other:Cmember):boolean {
		return this.variant.name === other.variant.name
			&& (this.variant.definition === other.variant.definition || this.variant.definition.is(other.variant.definition as any));
	}
}
export type Tgui_static_singular_path = {
	'has steps':'no'|['no', {}]|['yes', Tyes__has_steps__gui_static_singular_path];
	'head':Tancestor_node_path;
};
export class Cgui_static_singular_path extends AlanNode {
	public readonly properties:{
		readonly has_steps:Cgui_static_singular_path.Dhas_steps,
		readonly head:Cancestor_node_path
	};
	public readonly output:{
		result_node: () => gui_model.Cgui_node;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_steps?.state.node.output.result_node()).result!)
	};
	constructor(init:Tgui_static_singular_path, public location:AlanNode, public input: {
		gui_node: () => gui_model.Cgui_node
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_steps: new Cgui_static_singular_path.Dhas_steps(init['has steps'], $this),
			head: new Cgui_static_singular_path.Dhead(init['head'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_steps__gui_static_singular_path = {
};
export class Cno__has_steps__gui_static_singular_path extends AlanNode {
	public readonly output:{
		result_node: () => gui_model.Cgui_node;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.properties.head)
			.then(context => context?.component_root.output.node_type()).result!)
	}
	constructor(init:Tno__has_steps__gui_static_singular_path, public parent:Cgui_static_singular_path) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_steps__gui_static_singular_path = {
	'tail':Tgui_static_singular_path;
	'type':['group', Tgroup__type__yes];
};
export class Cyes__has_steps__gui_static_singular_path extends AlanNode {
	public readonly properties:{
		readonly tail:Cgui_static_singular_path,
		readonly type:Cyes__has_steps__gui_static_singular_path.Dtype
	};
	public readonly output:{
		result_node: () => gui_model.Cgui_node;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.result_node()).result!)
	}
	constructor(init:Tyes__has_steps__gui_static_singular_path, public parent:Cgui_static_singular_path) {
		super();
		const $this = this;
		this.properties = {
			tail: new Cyes__has_steps__gui_static_singular_path.Dtail(init['tail'], $this),
			type: new Cyes__has_steps__gui_static_singular_path.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tgroup__type__yes = {
	'group':string;
};
export class Cgroup__type__yes extends AlanNode {
	public readonly properties:{
		readonly group:Cgroup__type__yes.Dgroup
	};
	public readonly output:{
		result_node: () => gui_model.Cgui_node;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.group?.ref)
			.then(context => context?.properties.gui_node).result!)
	}
	constructor(init:Tgroup__type__yes, public parent:Cyes__has_steps__gui_static_singular_path) {
		super();
		const $this = this;
		this.properties = {
			group: new Cgroup__type__yes.Dgroup(init['group'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'group'`; }
	public get entity() { return this.parent.entity; }
}
export type Tgui_static_conditional_path = {
	'has steps':'no'|['no', {}]|['yes', Tyes__has_steps__gui_static_conditional_path];
};
export class Cgui_static_conditional_path extends AlanNode {
	public readonly properties:{
		readonly has_steps:Cgui_static_conditional_path.Dhas_steps
	};
	public readonly output:{
		node: () => gui_model.Cgui_node;
	} = {
		node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_steps?.state.node.output.node()).result!)
	};
	constructor(init:Tgui_static_conditional_path, public location:AlanNode, public input: {
		node: () => gui_model.Cgui_node
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_steps: new Cgui_static_conditional_path.Dhas_steps(init['has steps'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_steps__gui_static_conditional_path = {
};
export class Cno__has_steps__gui_static_conditional_path extends AlanNode {
	public readonly output:{
		node: () => gui_model.Cgui_node;
	} = {
		node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.node()).result!)
	}
	constructor(init:Tno__has_steps__gui_static_conditional_path, public parent:Cgui_static_conditional_path) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_steps__gui_static_conditional_path = {
	'step':['group', Tgroup__step]|['state', Tstate__step];
	'tail':Tgui_static_conditional_path;
};
export class Cyes__has_steps__gui_static_conditional_path extends AlanNode {
	public readonly properties:{
		readonly step:Cyes__has_steps__gui_static_conditional_path.Dstep,
		readonly tail:Cgui_static_conditional_path
	};
	public readonly output:{
		node: () => gui_model.Cgui_node;
	} = {
		node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.node()).result!)
	}
	constructor(init:Tyes__has_steps__gui_static_conditional_path, public parent:Cgui_static_conditional_path) {
		super();
		const $this = this;
		this.properties = {
			step: new Cyes__has_steps__gui_static_conditional_path.Dstep(init['step'], $this),
			tail: new Cyes__has_steps__gui_static_conditional_path.Dtail(init['tail'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tgroup__step = {
	'group':string;
};
export class Cgroup__step extends AlanNode {
	public readonly properties:{
		readonly group:Cgroup__step.Dgroup
	};
	public readonly output:{
		node: () => gui_model.Cgui_node;
	} = {
		node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.group?.ref)
			.then(context => context?.properties.gui_node).result!)
	}
	constructor(init:Tgroup__step, public parent:Cyes__has_steps__gui_static_conditional_path) {
		super();
		const $this = this;
		this.properties = {
			group: new Cgroup__step.Dgroup(init['group'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'step'?'group'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate__step = {
	'state':string;
	'stategroup':string;
};
export class Cstate__step extends AlanNode {
	public readonly properties:{
		readonly state:Cstate__step.Dstate,
		readonly stategroup:Cstate__step.Dstategroup
	};
	public readonly output:{
		node: () => gui_model.Cgui_node;
	} = {
		node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.state?.ref)
			.then(context => context?.properties.gui_node).result!)
	}
	constructor(init:Tstate__step, public parent:Cyes__has_steps__gui_static_conditional_path) {
		super();
		const $this = this;
		this.properties = {
			state: new Cstate__step.Dstate(init['state'], $this),
			stategroup: new Cstate__step.Dstategroup(init['stategroup'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'step'?'state'`; }
	public get entity() { return this.parent.entity; }
}
export type Tgui_node_type_path_step = {
	'has steps':'no'|['no', {}]|['yes', Tyes__has_steps__gui_node_type_path_step];
};
export class Cgui_node_type_path_step extends AlanNode {
	public readonly properties:{
		readonly has_steps:Cgui_node_type_path_step.Dhas_steps
	};
	public readonly output:{
		result_node: () => gui_model.Cgui_node;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_steps?.state.node.output.result_node()).result!)
	};
	constructor(init:Tgui_node_type_path_step, public location:AlanNode, public input: {
		context_node: () => gui_model.Cgui_node
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_steps: new Cgui_node_type_path_step.Dhas_steps(init['has steps'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_steps__gui_node_type_path_step = {
};
export class Cno__has_steps__gui_node_type_path_step extends AlanNode {
	public readonly output:{
		result_node: () => gui_model.Cgui_node;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.context_node()).result!)
	}
	constructor(init:Tno__has_steps__gui_node_type_path_step, public parent:Cgui_node_type_path_step) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_steps__gui_node_type_path_step = {
	'attribute':string;
	'tail':Tgui_node_type_path_step;
	'type':'node'|['node', {}]|['state', Tstate__type];
};
export class Cyes__has_steps__gui_node_type_path_step extends AlanNode {
	public readonly properties:{
		readonly attribute:Cyes__has_steps__gui_node_type_path_step.Dattribute,
		readonly tail:Cgui_node_type_path_step,
		readonly type:Cyes__has_steps__gui_node_type_path_step.Dtype
	};
	public readonly output:{
		result_node: () => gui_model.Cgui_node;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.result_node()).result!)
	}
	constructor(init:Tyes__has_steps__gui_node_type_path_step, public parent:Cgui_node_type_path_step) {
		super();
		const $this = this;
		this.properties = {
			attribute: new Cyes__has_steps__gui_node_type_path_step.Dattribute(init['attribute'], $this),
			tail: new Cyes__has_steps__gui_node_type_path_step.Dtail(init['tail'], $this),
			type: new Cyes__has_steps__gui_node_type_path_step.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnode__type = {
};
export class Cnode__type extends AlanNode {
	public readonly output:{
		result_node: () => gui_model.Cgui_node;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.inferences.node()).result!)
	}
	public readonly inferences:{
		node: () => gui_model.Cgui_node,
		optional_node: () => gui_model.Coptional_node
	} = {
		node: cache((detach:boolean) => {
			const gui_model__gui_node_type_path_step__has_steps__yes__type__node_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.optional_node())
				.then(context => context?.variant.name === 'node' ? context.variant.definition as gui_model.Cgui_node : undefined).result!;
		})
		,
		optional_node: cache((detach:boolean) => {
			const gui_model__gui_node_type_path_step__has_steps__yes__type__node_nval = this.parent;
			return resolve(this.parent)
				.then(context => evaluate__select_node_type(detach, 
					resolve(context)
						.then(() => this.parent)
						.then(context => context?.properties.attribute?.ref).result!, )).result!;
		})

	}
	constructor(init:Tnode__type, public parent:Cyes__has_steps__gui_node_type_path_step) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'node'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate__type = {
	'state':string;
};
export class Cstate__type extends AlanNode {
	public readonly properties:{
		readonly state:Cstate__type.Dstate
	};
	public readonly output:{
		result_node: () => gui_model.Cgui_node;
	} = {
		result_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.state?.ref)
			.then(context => context?.properties.gui_node).result!)
	}
	public readonly inferences:{
		state_group: () => gui_model.Cstate_group__type__property__type__attributes
	} = {
		state_group: cache((detach:boolean) => {
			const gui_model__gui_node_type_path_step__has_steps__yes__type__state_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.properties.attribute?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'property') {
						return resolve(context.properties.type.state.node as gui_model.Cproperty__type__attributes)
						.then(context => {
							if (context?.properties.type.state.name === 'state group') {
								return context.properties.type.state.node as gui_model.Cstate_group__type__property__type__attributes;
							} else {
								return undefined;
							}
						}).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tstate__type, public parent:Cyes__has_steps__gui_node_type_path_step) {
		super();
		const $this = this;
		this.properties = {
			state: new Cstate__type.Dstate(init['state'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state'`; }
	public get entity() { return this.parent.entity; }
}
export type Tgui_node_type_path = {
	'steps':Tgui_node_type_path_step;
};
export class Cgui_node_type_path extends AlanNode {
	public readonly properties:{
		readonly steps:Cgui_node_type_path_step
	};
	public readonly output:{
		result_application_node: () => application.Cnode__application;
		result_gui_node: () => gui_model.Cgui_node;
	} = {
		result_application_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.steps)
			.then(context => context?.component_root.output.result_node())
			.then(context => context?.component_root.output.application_node()).result!),
		result_gui_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.steps)
			.then(context => context?.component_root.output.result_node()).result!)
	};
	constructor(init:Tgui_node_type_path, public location:AlanNode, private key: string) {
		super();
		const $this = this;
		this.properties = {
			steps: new Cgui_node_type_path.Dsteps(init['steps'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tgui_node = {
	'attributes':Record<string, Tattributes>;
};
export class Cgui_node extends AlanNode {
	public definitions:{
		gui_context: Cgui_context;
		member_parent: Cmember_parent;
	} = {
		gui_context: new Cgui_context({name:'gui node', definition: this}, {
			gui_entity: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.component_root.input.gui_entity()).result!)
		}),
		member_parent: new Cmember_parent({name:'gui node', definition: this})
	}
	public readonly properties:{
		readonly attributes:Cgui_node.Dattributes
	};
	public readonly output:{
		application_node: () => application.Cnode__application;
		gui_entity: () => gui_model.Cgui_node;
		location: () => gui_model.Cgui_node_location;
		member: () => gui_model.Cmember;
	} = {
		application_node: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.application_node()).result!),
		gui_entity: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.gui_entity()).result!),
		location: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.location()).result!),
		member: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.member()).result!)
	};
	constructor(init:Tgui_node, public location:AlanNode, public input: {
		application_node: () => application.Cnode__application,
		gui_entity: () => gui_model.Cgui_node,
		location: () => gui_model.Cgui_node_location,
		member: () => gui_model.Cmember
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			attributes: new Cgui_node.Dattributes(init['attributes'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export class Kattributes extends Reference<application.Cattributes, string> {
	constructor(key:string, $this:Cattributes) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.component_root.input.application_node())
			.then(context => {
				const entry = context?.properties.attributes.get(this.entry)!;
				return resolve(entry).result;
			}).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'attributes'::key`; }
}
export type Tattributes = {
	'type':['action', Taction__type]|['command', Tcommand__type]|['property', Tproperty__type__attributes]|'reference set'|['reference set', {}];
};
export class Cattributes extends AlanDictionaryEntry {
	public key:Kattributes;
	public get key_value() { return this.key.entry; }
	public definitions:{
		member: Cmember;
	} = {
		member: new Cmember({name:'attribute', definition: this}, {
			parent: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(context => context?.parent)
				.then(context => context?.definitions.member_parent).result!)
		})
	}
	public readonly properties:{
		readonly type:Cattributes.Dtype
	};
	constructor(key:string, init:Tattributes, public parent:Cgui_node) {
		super();
		const $this = this;
		this.key = new Kattributes(key, $this);
		this.properties = {
			type: new Cattributes.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'attributes'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Taction__type = {
	'action':Tgui_action;
	'parameters':Tgui_node;
	'views':Record<string, Tviews>;
};
export class Caction__type extends AlanNode {
	public definitions:{
		operation: Coperation;
	} = {
		operation: new Coperation({name:'action', definition: this})
	}
	public readonly properties:{
		readonly action:Cgui_action,
		readonly parameters:Cgui_node,
		readonly views:Caction__type.Dviews
	};
	public readonly inferences:{
		action_type: () => application.Caction
	} = {
		action_type: cache((detach:boolean) => {
			const gui_model__gui_node__attributes__type__action_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'action') {
						return resolve(context.properties.type.state.node as application.Caction).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Taction__type, public parent:Cattributes) {
		super();
		const $this = this;
		this.properties = {
			action: new Caction__type.Daction(init['action'], $this),
			parameters: new Caction__type.Dparameters(init['parameters'], $this),
			views: new Caction__type.Dviews(init['views'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'action'`; }
	public get entity() { return this.parent.entity; }
}
export type Tviews = {
	'view context':Tgui_node_type_path;
};
export class Cviews extends AlanDictionaryEntry {
	public key:string;
	public get key_value() { return this.key; }
	public readonly properties:{
		readonly view_context:Cgui_node_type_path
	};
	constructor(key:string, init:Tviews, public parent:Caction__type) {
		super();
		const $this = this;
		this.key = key;
		this.properties = {
			view_context: new Cviews.Dview_context(init['view context'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'views'['${this.key}']`; }
	public get entity() { return this; }
}
export type Tcommand__type = {
	'type':'component'|['component', {}]|['global', Tglobal];
};
export class Ccommand__type extends AlanNode {
	public definitions:{
		operation: Coperation;
	} = {
		operation: new Coperation({name:'command', definition: this})
	}
	public readonly properties:{
		readonly type:Ccommand__type.Dtype
	};
	public readonly inferences:{
		command_type: () => application.Ccommand__type
	} = {
		command_type: cache((detach:boolean) => {
			const gui_model__gui_node__attributes__type__command_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'command') {
						return resolve(context.properties.type.state.node as application.Ccommand__type).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tcommand__type, public parent:Cattributes) {
		super();
		const $this = this;
		this.properties = {
			type: new Ccommand__type.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'command'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcomponent = {
};
export class Ccomponent extends AlanNode {
	public readonly inferences:{
		component_type: () => application.Ccomponent
	} = {
		component_type: cache((detach:boolean) => {
			const gui_model__gui_node__attributes__type__command__type__component_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.command_type())
				.then(context => {
					if (context?.properties.type.state.name === 'component') {
						return resolve(context.properties.type.state.node as application.Ccomponent).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tcomponent, public parent:Ccommand__type) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'component'`; }
	public get entity() { return this.parent.entity; }
}
export type Tglobal = {
	'parameters':Tgui_node;
};
export class Cglobal extends AlanNode {
	public readonly properties:{
		readonly parameters:Cgui_node
	};
	public readonly inferences:{
		global_type: () => application.Cglobal
	} = {
		global_type: cache((detach:boolean) => {
			const gui_model__gui_node__attributes__type__command__type__global_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.command_type())
				.then(context => {
					if (context?.properties.type.state.name === 'global') {
						return resolve(context.properties.type.state.node as application.Cglobal).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tglobal, public parent:Ccommand__type) {
		super();
		const $this = this;
		this.properties = {
			parameters: new Cglobal.Dparameters(init['parameters'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'global'`; }
	public get entity() { return this.parent.entity; }
}
export type Tproperty__type__attributes = {
	'type':['collection', Tcollection__type__property]|['file', Tfile__type__property]|['group', Tgroup__type__property]|['number', Tnumber__type__property__type__attributes]|['state group', Tstate_group__type__property__type__attributes]|['text', Ttext__type__property__type__attributes];
};
export class Cproperty__type__attributes extends AlanNode {
	public readonly properties:{
		readonly type:Cproperty__type__attributes.Dtype
	};
	public readonly inferences:{
		property_type: () => application.Cproperty__type__attributes
	} = {
		property_type: cache((detach:boolean) => {
			const gui_model__gui_node__attributes__type__property_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'property') {
						return resolve(context.properties.type.state.node as application.Cproperty__type__attributes).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tproperty__type__attributes, public parent:Cattributes) {
		super();
		const $this = this;
		this.properties = {
			type: new Cproperty__type__attributes.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'property'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcollection__type__property = {
	'default':'no'|['no', {}]|'yes'|['yes', {}];
	'duplication mapping':['from current context', Tfrom_current_context]|'none'|['none', {}];
	'has ordered graphs':'no'|['no', {}]|['yes', Tyes__has_ordered_graphs];
	'node':Tgui_node;
};
export class Ccollection__type__property extends AlanNode {
	public definitions:{
		gui_node_location: Cgui_node_location;
	} = {
		gui_node_location: new Cgui_node_location({name:'collection', definition: this})
	}
	public readonly properties:{
		readonly default_:Ccollection__type__property.Ddefault_,
		readonly duplication_mapping:Ccollection__type__property.Dduplication_mapping,
		readonly has_ordered_graphs:Ccollection__type__property.Dhas_ordered_graphs,
		readonly node:Cgui_node & { readonly inferences: {
			key_property: () => gui_model.Cattributes;
			text_key: () => gui_model.Ctext__type__property__type__attributes;
		} }
	};
	public readonly inferences:{
		collection: () => application.Ccollection__type__property
	} = {
		collection: cache((detach:boolean) => {
			const gui_model__gui_node__attributes__type__property__type__collection_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.property_type())
				.then(context => {
					if (context?.properties.type.state.name === 'collection') {
						return resolve(context.properties.type.state.node as application.Ccollection__type__property).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tcollection__type__property, public parent:Cproperty__type__attributes) {
		super();
		const $this = this;
		this.properties = {
			default_: new Ccollection__type__property.Ddefault_(init['default'], $this),
			duplication_mapping: new Ccollection__type__property.Dduplication_mapping(init['duplication mapping'], $this),
			has_ordered_graphs: new Ccollection__type__property.Dhas_ordered_graphs(init['has ordered graphs'], $this),
			node: new Ccollection__type__property.Dnode(init['node'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'collection'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__default__collection = {
};
export class Cno__default__collection extends AlanNode {
	constructor(init:Tno__default__collection, public parent:Ccollection__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'default'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__default__collection = {
};
export class Cyes__default__collection extends AlanNode {
	public readonly inferences:{
		model_expression: () => application.Cyes__default__ui_collection_property
	} = {
		model_expression: cache((detach:boolean) => {
			const gui_model__gui_node__attributes__type__property__type__collection__default__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.collection())
				.then(context => context?.properties.ui)
				.then(context => {
					if (context?.properties.default_.state.name === 'yes') {
						return resolve(context.properties.default_.state.node as application.Cyes__default__ui_collection_property).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tyes__default__collection, public parent:Ccollection__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'default'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tfrom_current_context = {
	'mapping':Tduplication_node_mapping;
};
export class Cfrom_current_context extends AlanNode {
	public readonly properties:{
		readonly mapping:Cduplication_node_mapping
	};
	public readonly inferences:{
		elementary_node: () => application.Cbase_value_source
	} = {
		elementary_node: cache((detach:boolean) => {
			const gui_model__gui_node__attributes__type__property__type__collection__duplication_mapping__from_current_context_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.application_node())
				.then(context => context?.component_root.output.value_source())
				.then(context => context?.variant.name === 'base' ? context.variant.definition as application.Cbase_value_source : undefined).result!;
		})

	}
	constructor(init:Tfrom_current_context, public parent:Ccollection__type__property) {
		super();
		const $this = this;
		this.properties = {
			mapping: new Cfrom_current_context.Dmapping(init['mapping'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'duplication mapping'?'from current context'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnone__duplication_mapping = {
};
export class Cnone__duplication_mapping extends AlanNode {
	constructor(init:Tnone__duplication_mapping, public parent:Ccollection__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'duplication mapping'?'none'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_ordered_graphs = {
};
export class Cno__has_ordered_graphs extends AlanNode {
	constructor(init:Tno__has_ordered_graphs, public parent:Ccollection__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has ordered graphs'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_ordered_graphs = {
	'ordered graphs':Record<string, {}>;
};
export class Cyes__has_ordered_graphs extends AlanNode {
	public readonly properties:{
		readonly ordered_graphs:Cyes__has_ordered_graphs.Dordered_graphs
	};
	constructor(init:Tyes__has_ordered_graphs, public parent:Ccollection__type__property) {
		super();
		const $this = this;
		this.properties = {
			ordered_graphs: new Cyes__has_ordered_graphs.Dordered_graphs(init['ordered graphs'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has ordered graphs'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export class Kordered_graphs extends Reference<application.Cgraphs, string> {
	public readonly inferences:{
		ordered_graph: () => application.Cordered
	}
	constructor(key:string, $this:Cordered_graphs) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.parent)
			.then(context => context?.inferences.collection())
			.then(context => context?.properties.graph_constraints)
			.then(context => {
				const entry = context?.properties.graphs.get(this.entry)!;
				return resolve(entry).result;
			}).result!), $this.parent)
		this.inferences = {
			ordered_graph: cache((detach:boolean) => {
				const gui_model__gui_node__attributes__type__property__type__collection__has_ordered_graphs__yes__ordered_graphs_key_nval = $this.key.ref;
				return resolve($this.key.ref)
					.then(context => gui_model__gui_node__attributes__type__property__type__collection__has_ordered_graphs__yes__ordered_graphs_key_nval)
					.then(context => {
						if (context?.properties.type.state.name === 'ordered') {
							return resolve(context.properties.type.state.node as application.Cordered).result;
						} else {
							return undefined;
						}
					}).result!;
			})

		}
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'ordered graphs'::key`; }
}
export type Tordered_graphs = {
};
export class Cordered_graphs extends AlanDictionaryEntry {
	public key:Kordered_graphs;
	public get key_value() { return this.key.entry; }
	public get inferences() { return this.key.inferences; }
	constructor(key:string, init:Tordered_graphs, public parent:Cyes__has_ordered_graphs) {
		super();
		const $this = this;
		this.key = new Kordered_graphs(key, $this);
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'ordered graphs'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Tfile__type__property = {
	'file name source':'expression'|['expression', {}]|['generated', Tgenerated];
	'has extension validation':'no'|['no', {}]|['yes', Tyes__has_extension_validation];
};
export class Cfile__type__property extends AlanNode {
	public readonly properties:{
		readonly file_name_source:Cfile__type__property.Dfile_name_source,
		readonly has_extension_validation:Cfile__type__property.Dhas_extension_validation
	};
	public readonly inferences:{
		file: () => application.Cfile
	} = {
		file: cache((detach:boolean) => {
			const gui_model__gui_node__attributes__type__property__type__file_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.property_type())
				.then(context => {
					if (context?.properties.type.state.name === 'file') {
						return resolve(context.properties.type.state.node as application.Cfile).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tfile__type__property, public parent:Cproperty__type__attributes) {
		super();
		const $this = this;
		this.properties = {
			file_name_source: new Cfile__type__property.Dfile_name_source(init['file name source'], $this),
			has_extension_validation: new Cfile__type__property.Dhas_extension_validation(init['has extension validation'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'file'`; }
	public get entity() { return this.parent.entity; }
}
export type Texpression__file_name_source = {
};
export class Cexpression__file_name_source extends AlanNode {
	public readonly inferences:{
		expression: () => application.Cyes__file_name_expression
	} = {
		expression: cache((detach:boolean) => {
			const gui_model__gui_node__attributes__type__property__type__file__file_name_source__expression_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.file())
				.then(context => context?.properties.ui)
				.then(context => {
					if (context?.properties.file_name_expression.state.name === 'yes') {
						return resolve(context.properties.file_name_expression.state.node as application.Cyes__file_name_expression).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Texpression__file_name_source, public parent:Cfile__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'file name source'?'expression'`; }
	public get entity() { return this.parent.entity; }
}
export type Tgenerated = {
	'file name expression':Tfile_name_expression;
};
export class Cgenerated extends AlanNode {
	public readonly properties:{
		readonly file_name_expression:Cfile_name_expression
	};
	constructor(init:Tgenerated, public parent:Cfile__type__property) {
		super();
		const $this = this;
		this.properties = {
			file_name_expression: new Cgenerated.Dfile_name_expression(init['file name expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'file name source'?'generated'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_extension_validation = {
};
export class Cno__has_extension_validation extends AlanNode {
	constructor(init:Tno__has_extension_validation, public parent:Cfile__type__property) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has extension validation'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_extension_validation = {
	'rules':Ttext_validation;
};
export class Cyes__has_extension_validation extends AlanNode {
	public readonly properties:{
		readonly rules:Ctext_validation
	};
	constructor(init:Tyes__has_extension_validation, public parent:Cfile__type__property) {
		super();
		const $this = this;
		this.properties = {
			rules: new Cyes__has_extension_validation.Drules(init['rules'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has extension validation'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tgroup__type__property = {
	'gui node':Tgui_node;
};
export class Cgroup__type__property extends AlanNode {
	public definitions:{
		gui_node_location: Cgui_node_location;
	} = {
		gui_node_location: new Cgui_node_location({name:'group', definition: this})
	}
	public readonly properties:{
		readonly gui_node:Cgui_node
	};
	public readonly inferences:{
		group: () => application.Cgroup__type__property
	} = {
		group: cache((detach:boolean) => {
			const gui_model__gui_node__attributes__type__property__type__group_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.property_type())
				.then(context => {
					if (context?.properties.type.state.name === 'group') {
						return resolve(context.properties.type.state.node as application.Cgroup__type__property).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tgroup__type__property, public parent:Cproperty__type__attributes) {
		super();
		const $this = this;
		this.properties = {
			gui_node: new Cgroup__type__property.Dgui_node(init['gui node'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'group'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumber__type__property__type__attributes = {
	'default':'no'|['no', {}]|['yes', Tyes__default__number];
	'numerical type':string;
	'summarize':'no'|['no', {}]|'total'|['total', {}];
	'validation':Tvalidation;
};
export class Cnumber__type__property__type__attributes extends AlanNode {
	public readonly properties:{
		readonly default_:Cnumber__type__property__type__attributes.Ddefault_,
		readonly numerical_type:Cnumber__type__property__type__attributes.Dnumerical_type,
		readonly summarize:Cnumber__type__property__type__attributes.Dsummarize,
		readonly validation:Cvalidation
	};
	public readonly inferences:{
		number: () => application.Cnumber__type__property
	} = {
		number: cache((detach:boolean) => {
			const gui_model__gui_node__attributes__type__property__type__number_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.property_type())
				.then(context => {
					if (context?.properties.type.state.name === 'number') {
						return resolve(context.properties.type.state.node as application.Cnumber__type__property).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tnumber__type__property__type__attributes, public parent:Cproperty__type__attributes) {
		super();
		const $this = this;
		this.properties = {
			default_: new Cnumber__type__property__type__attributes.Ddefault_(init['default'], $this),
			numerical_type: new Cnumber__type__property__type__attributes.Dnumerical_type(init['numerical type'], $this),
			summarize: new Cnumber__type__property__type__attributes.Dsummarize(init['summarize'], $this),
			validation: new Cvalidation(init['validation'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'number'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__default__number = {
};
export class Cno__default__number extends AlanNode {
	constructor(init:Tno__default__number, public parent:Cnumber__type__property__type__attributes) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'default'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__default__number = {
	'value':'expression'|['expression', {}]|'now'|['now', {}]|'one'|['one', {}]|'today'|['today', {}]|'zero'|['zero', {}];
};
export class Cyes__default__number extends AlanNode {
	public readonly properties:{
		readonly value:Cyes__default__number.Dvalue
	};
	constructor(init:Tyes__default__number, public parent:Cnumber__type__property__type__attributes) {
		super();
		const $this = this;
		this.properties = {
			value: new Cyes__default__number.Dvalue(init['value'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'default'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Texpression__value = {
};
export class Cexpression__value extends AlanNode {
	public readonly inferences:{
		model_expression: () => application.Cyes__default__ui_number_property
	} = {
		model_expression: cache((detach:boolean) => {
			const gui_model__gui_node__attributes__type__property__type__number__default__yes__value__expression_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.inferences.number())
				.then(context => context?.properties.ui)
				.then(context => {
					if (context?.properties.default_.state.name === 'yes') {
						return resolve(context.properties.default_.state.node as application.Cyes__default__ui_number_property).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Texpression__value, public parent:Cyes__default__number) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'value'?'expression'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnow__value = {
};
export class Cnow__value extends AlanNode {
	constructor(init:Tnow__value, public parent:Cyes__default__number) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'value'?'now'`; }
	public get entity() { return this.parent.entity; }
}
export type Tone = {
};
export class Cone extends AlanNode {
	public readonly inferences:{
		positive: () => application.Cpositive__set_type
	} = {
		positive: cache((detach:boolean) => {
			const gui_model__gui_node__attributes__type__property__type__number__default__yes__value__one_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.inferences.number())
				.then(context => {
					if (context?.properties.set_type.state.name === 'positive') {
						return resolve(context.properties.set_type.state.node as application.Cpositive__set_type).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tone, public parent:Cyes__default__number) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'value'?'one'`; }
	public get entity() { return this.parent.entity; }
}
export type Ttoday__value = {
};
export class Ctoday__value extends AlanNode {
	constructor(init:Ttoday__value, public parent:Cyes__default__number) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'value'?'today'`; }
	public get entity() { return this.parent.entity; }
}
export type Tzero = {
};
export class Czero extends AlanNode {
	public readonly inferences:{
		integer: () => application.Cinteger__set_type
	} = {
		integer: cache((detach:boolean) => {
			const gui_model__gui_node__attributes__type__property__type__number__default__yes__value__zero_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.inferences.number())
				.then(context => {
					if (context?.properties.set_type.state.name === 'integer') {
						return resolve(context.properties.set_type.state.node as application.Cinteger__set_type).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tzero, public parent:Cyes__default__number) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'value'?'zero'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__summarize = {
};
export class Cno__summarize extends AlanNode {
	constructor(init:Tno__summarize, public parent:Cnumber__type__property__type__attributes) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'summarize'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Ttotal = {
};
export class Ctotal extends AlanNode {
	constructor(init:Ttotal, public parent:Cnumber__type__property__type__attributes) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'summarize'?'total'`; }
	public get entity() { return this.parent.entity; }
}
export type Tvalidation = {
	'has maximum':'expression'|['expression', {}]|'no'|['no', {}]|['yes', Tyes__has_maximum];
	'has minimum':'expression'|['expression', {}]|'no'|['no', {}]|['yes', Tyes__has_minimum];
};
export class Cvalidation extends AlanNode {
	public readonly properties:{
		readonly has_maximum:Cvalidation.Dhas_maximum,
		readonly has_minimum:Cvalidation.Dhas_minimum
	};
	constructor(init:Tvalidation, public parent:Cnumber__type__property__type__attributes) {
		super();
		const $this = this;
		this.properties = {
			has_maximum: new Cvalidation.Dhas_maximum(init['has maximum'], $this),
			has_minimum: new Cvalidation.Dhas_minimum(init['has minimum'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'validation'`; }
	public get entity() { return this.parent.entity; }
}
export type Texpression__has_maximum = {
};
export class Cexpression__has_maximum extends AlanNode {
	public readonly inferences:{
		model_expression: () => application.Cyes__has_maximum
	} = {
		model_expression: cache((detach:boolean) => {
			const gui_model__gui_node__attributes__type__property__type__number__validation__has_maximum__expression_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.inferences.number())
				.then(context => context?.properties.ui)
				.then(context => context?.properties.validation)
				.then(context => {
					if (context?.properties.has_maximum.state.name === 'yes') {
						return resolve(context.properties.has_maximum.state.node as application.Cyes__has_maximum).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Texpression__has_maximum, public parent:Cvalidation) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has maximum'?'expression'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_maximum = {
};
export class Cno__has_maximum extends AlanNode {
	constructor(init:Tno__has_maximum, public parent:Cvalidation) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has maximum'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_maximum = {
	'maximum':Tnumber_limit;
};
export class Cyes__has_maximum extends AlanNode {
	public readonly properties:{
		readonly maximum:Cnumber_limit
	};
	public readonly inferences:{
		constrain: () => application.Cbase__type__number
	} = {
		constrain: cache((detach:boolean) => {
			const gui_model__gui_node__attributes__type__property__type__number__validation__has_maximum__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.inferences.number())
				.then(context => {
					if (context?.properties.type.state.name === 'base') {
						return resolve(context.properties.type.state.node as application.Cbase__type__number).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tyes__has_maximum, public parent:Cvalidation) {
		super();
		const $this = this;
		this.properties = {
			maximum: new Cyes__has_maximum.Dmaximum(init['maximum'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has maximum'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Texpression__has_minimum = {
};
export class Cexpression__has_minimum extends AlanNode {
	public readonly inferences:{
		model_expression: () => application.Cyes__has_minimum
	} = {
		model_expression: cache((detach:boolean) => {
			const gui_model__gui_node__attributes__type__property__type__number__validation__has_minimum__expression_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.inferences.number())
				.then(context => context?.properties.ui)
				.then(context => context?.properties.validation)
				.then(context => {
					if (context?.properties.has_minimum.state.name === 'yes') {
						return resolve(context.properties.has_minimum.state.node as application.Cyes__has_minimum).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Texpression__has_minimum, public parent:Cvalidation) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has minimum'?'expression'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_minimum = {
};
export class Cno__has_minimum extends AlanNode {
	constructor(init:Tno__has_minimum, public parent:Cvalidation) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has minimum'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_minimum = {
	'minimum':Tnumber_limit;
};
export class Cyes__has_minimum extends AlanNode {
	public readonly properties:{
		readonly minimum:Cnumber_limit
	};
	public readonly inferences:{
		constrain: () => application.Cbase__type__number
	} = {
		constrain: cache((detach:boolean) => {
			const gui_model__gui_node__attributes__type__property__type__number__validation__has_minimum__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.inferences.number())
				.then(context => {
					if (context?.properties.type.state.name === 'base') {
						return resolve(context.properties.type.state.node as application.Cbase__type__number).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tyes__has_minimum, public parent:Cvalidation) {
		super();
		const $this = this;
		this.properties = {
			minimum: new Cyes__has_minimum.Dminimum(init['minimum'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has minimum'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate_group__type__property__type__attributes = {
	'default':'no'|['no', {}]|['yes', Tyes__default__state_group];
	'has states':'no'|['no', {}]|['yes', Tyes__has_states];
	'states':Record<string, Tstates__state_group__type__property__type__attributes>;
};
export class Cstate_group__type__property__type__attributes extends AlanNode {
	public readonly properties:{
		readonly default_:Cstate_group__type__property__type__attributes.Ddefault_,
		readonly has_states:Cstate_group__type__property__type__attributes.Dhas_states,
		readonly states:Cstate_group__type__property__type__attributes.Dstates
	};
	public readonly inferences:{
		state_group: () => application.Cstate_group
	} = {
		state_group: cache((detach:boolean) => {
			const gui_model__gui_node__attributes__type__property__type__state_group_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.property_type())
				.then(context => {
					if (context?.properties.type.state.name === 'state group') {
						return resolve(context.properties.type.state.node as application.Cstate_group).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tstate_group__type__property__type__attributes, public parent:Cproperty__type__attributes) {
		super();
		const $this = this;
		this.properties = {
			default_: new Cstate_group__type__property__type__attributes.Ddefault_(init['default'], $this),
			has_states: new Cstate_group__type__property__type__attributes.Dhas_states(init['has states'], $this),
			states: new Cstate_group__type__property__type__attributes.Dstates(init['states'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state group'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__default__state_group = {
};
export class Cno__default__state_group extends AlanNode {
	constructor(init:Tno__default__state_group, public parent:Cstate_group__type__property__type__attributes) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'default'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__default__state_group = {
	'source':['state', Tstate__source]|'state switch'|['state switch', {}];
};
export class Cyes__default__state_group extends AlanNode {
	public readonly properties:{
		readonly source:Cyes__default__state_group.Dsource
	};
	constructor(init:Tyes__default__state_group, public parent:Cstate_group__type__property__type__attributes) {
		super();
		const $this = this;
		this.properties = {
			source: new Cyes__default__state_group.Dsource(init['source'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'default'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate__source = {
	'state':string;
};
export class Cstate__source extends AlanNode {
	public readonly properties:{
		readonly state:Cstate__source.Dstate
	};
	constructor(init:Tstate__source, public parent:Cyes__default__state_group) {
		super();
		const $this = this;
		this.properties = {
			state: new Cstate__source.Dstate(init['state'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'source'?'state'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate_switch__source = {
};
export class Cstate_switch__source extends AlanNode {
	public readonly inferences:{
		model_state_switch: () => application.Cyes__default_state
	} = {
		model_state_switch: cache((detach:boolean) => {
			const gui_model__gui_node__attributes__type__property__type__state_group__default__yes__source__state_switch_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.inferences.state_group())
				.then(context => context?.properties.ui)
				.then(context => {
					if (context?.properties.default_state.state.name === 'yes') {
						return resolve(context.properties.default_state.state.node as application.Cyes__default_state).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tstate_switch__source, public parent:Cyes__default__state_group) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'source'?'state switch'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__has_states = {
};
export class Cno__has_states extends AlanNode {
	constructor(init:Tno__has_states, public parent:Cstate_group__type__property__type__attributes) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has states'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_states = {
	'first':string;
};
export class Cyes__has_states extends AlanNode {
	public readonly properties:{
		readonly first:Cyes__has_states.Dfirst
	};
	constructor(init:Tyes__has_states, public parent:Cstate_group__type__property__type__attributes) {
		super();
		const $this = this;
		this.properties = {
			first: new Cyes__has_states.Dfirst(init['first'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has states'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export class Kstates__state_group__type__property__type__attributes extends Reference<application.Cstates__state_group, string> {
	constructor(key:string, $this:Cstates__state_group__type__property__type__attributes) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.inferences.state_group())
			.then(context => {
				const entry = context?.properties.states.get(this.entry)!;
				return resolve(entry).result;
			}).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'states'::key`; }
}
export type Tstates__state_group__type__property__type__attributes = {
	'gui node':Tgui_node;
	'has successor':'no'|['no', {}]|['yes', Tyes__has_successor];
	'rules':Twhere_rules;
};
export class Cstates__state_group__type__property__type__attributes extends AlanDictionaryEntry {
	public key:Kstates__state_group__type__property__type__attributes;
	public get key_value() { return this.key.entry; }
	public definitions:{
		gui_node_location: Cgui_node_location;
	} = {
		gui_node_location: new Cgui_node_location({name:'state', definition: this})
	}
	public readonly properties:{
		readonly gui_node:Cgui_node,
		readonly has_successor:Cstates__state_group__type__property__type__attributes.Dhas_successor,
		readonly rules:Cwhere_rules
	};
	constructor(key:string, init:Tstates__state_group__type__property__type__attributes, public parent:Cstate_group__type__property__type__attributes) {
		super();
		const $this = this;
		this.key = new Kstates__state_group__type__property__type__attributes(key, $this);
		this.properties = {
			gui_node: new Cstates__state_group__type__property__type__attributes.Dgui_node(init['gui node'], $this),
			has_successor: new Cstates__state_group__type__property__type__attributes.Dhas_successor(init['has successor'], $this),
			rules: new Cstates__state_group__type__property__type__attributes.Drules(init['rules'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'states'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Tno__has_successor = {
};
export class Cno__has_successor extends AlanNode {
	constructor(init:Tno__has_successor, public parent:Cstates__state_group__type__property__type__attributes) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has successor'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_successor = {
	'successor':string;
};
export class Cyes__has_successor extends AlanNode {
	public readonly properties:{
		readonly successor:Cyes__has_successor.Dsuccessor
	};
	constructor(init:Tyes__has_successor, public parent:Cstates__state_group__type__property__type__attributes) {
		super();
		const $this = this;
		this.properties = {
			successor: new Cyes__has_successor.Dsuccessor(init['successor'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has successor'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Ttext__type__property__type__attributes = {
	'default':'no'|['no', {}]|['yes', Tyes__default__text];
	'navigable':['no', Tno__navigable]|['yes', Tyes__navigable];
	'validation':['custom', Tcustom]|'model'|['model', {}]|'none'|['none', {}];
};
export class Ctext__type__property__type__attributes extends AlanNode {
	public readonly properties:{
		readonly default_:Ctext__type__property__type__attributes.Ddefault_,
		readonly navigable:Ctext__type__property__type__attributes.Dnavigable,
		readonly validation:Ctext__type__property__type__attributes.Dvalidation
	};
	public readonly inferences:{
		text: () => application.Ctext__type__property
	} = {
		text: cache((detach:boolean) => {
			const gui_model__gui_node__attributes__type__property__type__text_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.property_type())
				.then(context => {
					if (context?.properties.type.state.name === 'text') {
						return resolve(context.properties.type.state.node as application.Ctext__type__property).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Ttext__type__property__type__attributes, public parent:Cproperty__type__attributes) {
		super();
		const $this = this;
		this.properties = {
			default_: new Ctext__type__property__type__attributes.Ddefault_(init['default'], $this),
			navigable: new Ctext__type__property__type__attributes.Dnavigable(init['navigable'], $this),
			validation: new Ctext__type__property__type__attributes.Dvalidation(init['validation'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'text'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__default__text = {
};
export class Cno__default__text extends AlanNode {
	constructor(init:Tno__default__text, public parent:Ctext__type__property__type__attributes) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'default'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__default__text = {
	'source':'auto select only candidate'|['auto select only candidate', {}]|'current user'|['current user', {}]|'expression'|['expression', {}]|'guid'|['guid', {}]|['select source', Tselect_source];
};
export class Cyes__default__text extends AlanNode {
	public readonly properties:{
		readonly source:Cyes__default__text.Dsource
	};
	constructor(init:Tyes__default__text, public parent:Ctext__type__property__type__attributes) {
		super();
		const $this = this;
		this.properties = {
			source: new Cyes__default__text.Dsource(init['source'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'default'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tauto_select_only_candidate = {
};
export class Cauto_select_only_candidate extends AlanNode {
	public readonly inferences:{
		reference: () => gui_model.Cyes__navigable
	} = {
		reference: cache((detach:boolean) => {
			const gui_model__gui_node__attributes__type__property__type__text__default__yes__source__auto_select_only_candidate_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => {
					if (context?.properties.navigable.state.name === 'yes') {
						return resolve(context.properties.navigable.state.node as gui_model.Cyes__navigable).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tauto_select_only_candidate, public parent:Cyes__default__text) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'source'?'auto select only candidate'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcurrent_user = {
};
export class Ccurrent_user extends AlanNode {
	public readonly inferences:{
		has_dynamic_users: () => application.Cyes__has_dynamic_users
	} = {
		has_dynamic_users: cache((detach:boolean) => {
			const gui_model__gui_node__attributes__type__property__type__text__default__yes__source__current_user_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.root.input.application)
				.then(context => {
					if (context?.properties.has_dynamic_users.state.name === 'yes') {
						return resolve(context.properties.has_dynamic_users.state.node as application.Cyes__has_dynamic_users).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tcurrent_user, public parent:Cyes__default__text) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'source'?'current user'`; }
	public get entity() { return this.parent.entity; }
}
export type Texpression__source = {
};
export class Cexpression__source extends AlanNode {
	public readonly inferences:{
		text_with_default: () => application.Cyes__default_value
	} = {
		text_with_default: cache((detach:boolean) => {
			const gui_model__gui_node__attributes__type__property__type__text__default__yes__source__expression_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.inferences.text())
				.then(context => context?.properties.ui)
				.then(context => {
					if (context?.properties.default_value.state.name === 'yes') {
						return resolve(context.properties.default_value.state.node as application.Cyes__default_value).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Texpression__source, public parent:Cyes__default__text) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'source'?'expression'`; }
	public get entity() { return this.parent.entity; }
}
export type Tguid = {
};
export class Cguid extends AlanNode {
	public readonly inferences:{
		no_constraint: () => gui_model.Cno__navigable
	} = {
		no_constraint: cache((detach:boolean) => {
			const gui_model__gui_node__attributes__type__property__type__text__default__yes__source__guid_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => {
					if (context?.properties.navigable.state.name === 'no') {
						return resolve(context.properties.navigable.state.node as gui_model.Cno__navigable).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tguid, public parent:Cyes__default__text) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'source'?'guid'`; }
	public get entity() { return this.parent.entity; }
}
export type Tselect_source = {
	'graph':string;
};
export class Cselect_source extends AlanNode {
	public readonly properties:{
		readonly graph:Cselect_source.Dgraph
	};
	public readonly inferences:{
		collection_entity: () => gui_model.Ccollection__type__property,
		has_ordered_graphs: () => gui_model.Cyes__has_ordered_graphs,
		reference: () => gui_model.Cyes__navigable
	} = {
		collection_entity: cache((detach:boolean) => {
			const gui_model__gui_node__attributes__type__property__type__text__default__yes__source__select_source_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.gui_entity())
				.then(context => context?.component_root.output.location())
				.then(context => context?.variant.name === 'collection' ? context.variant.definition as gui_model.Ccollection__type__property : undefined).result!;
		})
		,
		has_ordered_graphs: cache((detach:boolean) => {
			const gui_model__gui_node__attributes__type__property__type__text__default__yes__source__select_source_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.inferences.collection_entity())
				.then(context => {
					if (context?.properties.has_ordered_graphs.state.name === 'yes') {
						return resolve(context.properties.has_ordered_graphs.state.node as gui_model.Cyes__has_ordered_graphs).result;
					} else {
						return undefined;
					}
				}).result!;
		})
		,
		reference: cache((detach:boolean) => {
			const gui_model__gui_node__attributes__type__property__type__text__default__yes__source__select_source_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => {
					if (context?.properties.navigable.state.name === 'yes') {
						return resolve(context.properties.navigable.state.node as gui_model.Cyes__navigable).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tselect_source, public parent:Cyes__default__text) {
		super();
		const $this = this;
		this.properties = {
			graph: new Cselect_source.Dgraph(init['graph'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'source'?'select source'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__navigable = {
	'password property':'no'|['no', {}]|'yes'|['yes', {}];
};
export class Cno__navigable extends AlanNode {
	public readonly properties:{
		readonly password_property:Cno__navigable.Dpassword_property
	};
	public readonly output:{
		context: () => gui_model.Cgui_context;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.parent)
			.then(context => context?.parent)
			.then(context => context?.parent)
			.then(context => context?.parent)
			.then(context => context?.definitions.gui_context).result!)
	}
	constructor(init:Tno__navigable, public parent:Ctext__type__property__type__attributes) {
		super();
		const $this = this;
		this.properties = {
			password_property: new Cno__navigable.Dpassword_property(init['password property'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'navigable'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__password_property = {
};
export class Cno__password_property extends AlanNode {
	constructor(init:Tno__password_property, public parent:Cno__navigable) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'password property'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__password_property = {
};
export class Cyes__password_property extends AlanNode {
	public readonly inferences:{
		password: () => application.Cyes__has_password_authentication,
		valid_property: () => application.Cattributes
	} = {
		password: cache((detach:boolean) => {
			const gui_model__gui_node__attributes__type__property__type__text__navigable__no__password_property__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.root.input.application)
				.then(context => {
					if (context?.properties.has_dynamic_users.state.name === 'yes') {
						return resolve(context.properties.has_dynamic_users.state.node as application.Cyes__has_dynamic_users)
						.then(context => {
							if (context?.properties.has_password_authentication.state.name === 'yes') {
								return context.properties.has_password_authentication.state.node as application.Cyes__has_password_authentication;
							} else {
								return undefined;
							}
						}).result;
					} else {
						return undefined;
					}
				}).result!;
		})
		,
		valid_property: cache((detach:boolean) => {
			const gui_model__gui_node__attributes__type__property__type__text__navigable__no__password_property__yes_nval = this.parent;
			return resolve(this.parent)
				.then(context => {
					const left = resolve(context)
						.then(() => this.inferences.password())
						.then(context => context?.properties.password_text_path)
						.then(context => context?.component_root.output.property())
						.then(context => context?.parent).result;
					const right = resolve(context)
						.then(() => this.parent)
						.then(context => context?.parent)
						.then(context => context?.parent)
						.then(context => context?.parent)
						.then(context => context?.key?.ref).result;
					return left?.is(right!) ? left : undefined
				}).result!;
		})

	}
	constructor(init:Tyes__password_property, public parent:Cno__navigable) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'password property'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__navigable = {
	'referenced node':Tgui_node_type_path;
	'rules':Twhere_rules;
	'type':'mandatory'|['mandatory', {}]|'optional'|['optional', {}];
	'validate':'no'|['no', {}]|'yes'|['yes', {}];
};
export class Cyes__navigable extends AlanNode {
	public readonly properties:{
		readonly referenced_node:Cgui_node_type_path & { readonly inferences: {
			referenced_collection: () => gui_model.Ccollection__type__property;
			referenced_node_type: () => application.Cnode__application;
		} },
		readonly rules:Cwhere_rules,
		readonly type:Cyes__navigable.Dtype,
		readonly validate:Cyes__navigable.Dvalidate
	};
	public readonly output:{
		context: () => gui_model.Cgui_context;
	} = {
		context: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.referenced_node)
			.then(context => context?.component_root.output.result_gui_node())
			.then(context => context?.component_root.output.gui_entity())
			.then(context => context?.definitions.gui_context).result!)
	}
	public readonly inferences:{
		reference: () => application.Cyes__has_reference
	} = {
		reference: cache((detach:boolean) => {
			const gui_model__gui_node__attributes__type__property__type__text__navigable__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.text())
				.then(context => {
					if (context?.properties.has_reference.state.name === 'yes') {
						return resolve(context.properties.has_reference.state.node as application.Cyes__has_reference).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tyes__navigable, public parent:Ctext__type__property__type__attributes) {
		super();
		const $this = this;
		this.properties = {
			referenced_node: new Cyes__navigable.Dreferenced_node(init['referenced node'], $this),
			rules: new Cyes__navigable.Drules(init['rules'], $this),
			type: new Cyes__navigable.Dtype(init['type'], $this),
			validate: new Cyes__navigable.Dvalidate(init['validate'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'navigable'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tmandatory = {
};
export class Cmandatory extends AlanNode {
	public readonly inferences:{
		mandatory: () => application.Cmandatory__type
	} = {
		mandatory: cache((detach:boolean) => {
			const gui_model__gui_node__attributes__type__property__type__text__navigable__yes__type__mandatory_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.reference())
				.then(context => context?.properties.behaviour)
				.then(context => {
					if (context?.properties.type.state.name === 'mandatory') {
						return resolve(context.properties.type.state.node as application.Cmandatory__type).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tmandatory, public parent:Cyes__navigable) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'mandatory'`; }
	public get entity() { return this.parent.entity; }
}
export type Toptional = {
};
export class Coptional extends AlanNode {
	public readonly inferences:{
		optional: () => application.Coptional__type
	} = {
		optional: cache((detach:boolean) => {
			const gui_model__gui_node__attributes__type__property__type__text__navigable__yes__type__optional_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.reference())
				.then(context => context?.properties.behaviour)
				.then(context => {
					if (context?.properties.type.state.name === 'optional') {
						return resolve(context.properties.type.state.node as application.Coptional__type).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Toptional, public parent:Cyes__navigable) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'optional'`; }
	public get entity() { return this.parent.entity; }
}
export type Tno__validate__yes = {
};
export class Cno__validate__yes extends AlanNode {
	constructor(init:Tno__validate__yes, public parent:Cyes__navigable) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'validate'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__validate__yes = {
};
export class Cyes__validate__yes extends AlanNode {
	constructor(init:Tyes__validate__yes, public parent:Cyes__navigable) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'validate'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcustom = {
	'rules':Ttext_validation;
};
export class Ccustom extends AlanNode {
	public readonly properties:{
		readonly rules:Ctext_validation
	};
	public readonly inferences:{
		elementary: () => application.Cbase__type__text,
		unconstrained: () => application.Cno__has_reference
	} = {
		elementary: cache((detach:boolean) => {
			const gui_model__gui_node__attributes__type__property__type__text__validation__custom_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.text())
				.then(context => {
					if (context?.properties.type.state.name === 'base') {
						return resolve(context.properties.type.state.node as application.Cbase__type__text).result;
					} else {
						return undefined;
					}
				}).result!;
		})
		,
		unconstrained: cache((detach:boolean) => {
			const gui_model__gui_node__attributes__type__property__type__text__validation__custom_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.text())
				.then(context => {
					if (context?.properties.has_reference.state.name === 'no') {
						return resolve(context.properties.has_reference.state.node as application.Cno__has_reference).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tcustom, public parent:Ctext__type__property__type__attributes) {
		super();
		const $this = this;
		this.properties = {
			rules: new Ccustom.Drules(init['rules'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'validation'?'custom'`; }
	public get entity() { return this.parent.entity; }
}
export type Tmodel__validation = {
};
export class Cmodel__validation extends AlanNode {
	public readonly inferences:{
		text_with_validation: () => application.Cyes__has_validation__ui_text_property
	} = {
		text_with_validation: cache((detach:boolean) => {
			const gui_model__gui_node__attributes__type__property__type__text__validation__model_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.text())
				.then(context => context?.properties.ui)
				.then(context => {
					if (context?.properties.has_validation.state.name === 'yes') {
						return resolve(context.properties.has_validation.state.node as application.Cyes__has_validation__ui_text_property).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tmodel__validation, public parent:Ctext__type__property__type__attributes) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'validation'?'model'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnone__validation = {
};
export class Cnone__validation extends AlanNode {
	constructor(init:Tnone__validation, public parent:Ctext__type__property__type__attributes) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'validation'?'none'`; }
	public get entity() { return this.parent.entity; }
}
export type Treference_set = {
};
export class Creference_set extends AlanNode {
	public readonly inferences:{
		reference_set_type: () => application.Creference_set__type__attributes
	} = {
		reference_set_type: cache((detach:boolean) => {
			const gui_model__gui_node__attributes__type__reference_set_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.key?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'reference set') {
						return resolve(context.properties.type.state.node as application.Creference_set__type__attributes).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Treference_set, public parent:Cattributes) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'reference set'`; }
	public get entity() { return this.parent.entity; }
}
export type Tgui_action_expression = {
	'operation':['entry', Tentry]|['entry list', Tentry_list]|['execute operation', Texecute_operation]|'ignore'|['ignore', {}]|['node switch', Tnode_switch]|['produce value', Tproduce_value]|['state switch', Tstate_switch__operation]|['update properties', Tupdate_properties]|['walk', Twalk];
};
export class Cgui_action_expression extends AlanNode {
	public readonly properties:{
		readonly operation:Cgui_action_expression.Doperation
	};
	constructor(init:Tgui_action_expression, public location:AlanNode, public input: {
		action: () => application.Cui_expression,
		gui_action: () => gui_model.Caction__type
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			operation: new Cgui_action_expression.Doperation(init['operation'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tentry = {
	'type':['create', Tcreate]|['delete', Tdelete];
};
export class Centry extends AlanNode {
	public readonly properties:{
		readonly type:Centry.Dtype
	};
	public readonly inferences:{
		entry: () => application.Centry__operation__ui_expression
	} = {
		entry: cache((detach:boolean) => {
			const gui_model__gui_action_expression__operation__entry_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.action())
				.then(context => {
					if (context?.properties.operation.state.name === 'entry') {
						return resolve(context.properties.operation.state.node as application.Centry__operation__ui_expression).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tentry, public parent:Cgui_action_expression) {
		super();
		const $this = this;
		this.properties = {
			type: new Centry.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'entry'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcreate = {
	'collection type':['node', Tnode__collection_type]|'parameter'|['parameter', {}];
	'expression':Tobject_expression;
};
export class Ccreate extends AlanNode {
	public readonly properties:{
		readonly collection_type:Ccreate.Dcollection_type,
		readonly expression:Cobject_expression
	};
	public readonly inferences:{
		create: () => application.Ccreate__type__entry__operation__ui_expression
	} = {
		create: cache((detach:boolean) => {
			const gui_model__gui_action_expression__operation__entry__type__create_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.entry())
				.then(context => {
					if (context?.properties.type.state.name === 'create') {
						return resolve(context.properties.type.state.node as application.Ccreate__type__entry__operation__ui_expression).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tcreate, public parent:Centry) {
		super();
		const $this = this;
		this.properties = {
			collection_type: new Ccreate.Dcollection_type(init['collection type'], $this),
			expression: new Ccreate.Dexpression(init['expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'create'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnode__collection_type = {
	'bind view':string;
};
export class Cnode__collection_type extends AlanNode {
	public readonly properties:{
		readonly bind_view:Cnode__collection_type.Dbind_view
	};
	public readonly inferences:{
		node_collection: () => application.Ccollection__type__property
	} = {
		node_collection: cache((detach:boolean) => {
			const gui_model__gui_action_expression__operation__entry__type__create__collection_type__node_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.inferences.entry())
				.then(context => context?.inferences.target_collection())
				.then(context => context?.variant.name === 'collection property' ? context.variant.definition as application.Ccollection__type__property : undefined).result!;
		})

	}
	constructor(init:Tnode__collection_type, public parent:Ccreate) {
		super();
		const $this = this;
		this.properties = {
			bind_view: new Cnode__collection_type.Dbind_view(init['bind view'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'collection type'?'node'`; }
	public get entity() { return this.parent.entity; }
}
export type Tparameter = {
};
export class Cparameter extends AlanNode {
	public readonly inferences:{
		parameter_collection: () => application.Ccollection__type__property
	} = {
		parameter_collection: cache((detach:boolean) => {
			const gui_model__gui_action_expression__operation__entry__type__create__collection_type__parameter_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.parent)
				.then(context => context?.inferences.entry())
				.then(context => context?.inferences.target_collection())
				.then(context => context?.variant.name === 'collection property' ? context.variant.definition as application.Ccollection__type__property : undefined).result!;
		})

	}
	constructor(init:Tparameter, public parent:Ccreate) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'collection type'?'parameter'`; }
	public get entity() { return this.parent.entity; }
}
export type Tdelete = {
	'bind view':string;
};
export class Cdelete extends AlanNode {
	public readonly properties:{
		readonly bind_view:Cdelete.Dbind_view
	};
	public readonly inferences:{
		delete_: () => application.Cdelete__type__entry__operation__ui_expression,
		entry_node: () => application.Cnode__application
	} = {
		delete_: cache((detach:boolean) => {
			const gui_model__gui_action_expression__operation__entry__type__delete_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.entry())
				.then(context => {
					if (context?.properties.type.state.name === 'delete') {
						return resolve(context.properties.type.state.node as application.Cdelete__type__entry__operation__ui_expression).result;
					} else {
						return undefined;
					}
				}).result!;
		})
		,
		entry_node: cache((detach:boolean) => {
			const gui_model__gui_action_expression__operation__entry__type__delete_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.entry())
				.then(context => context?.inferences.target_collection())
				.then(context => context?.component_root.output.entry_object())
				.then(context => context?.variant.name === 'node' ? context.variant.definition as application.Cnode__application : undefined).result!;
		})

	}
	constructor(init:Tdelete, public parent:Centry) {
		super();
		const $this = this;
		this.properties = {
			bind_view: new Cdelete.Dbind_view(init['bind view'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'delete'`; }
	public get entity() { return this.parent.entity; }
}
export type Tentry_list = {
	'entries':Tentry_expression_list;
};
export class Centry_list extends AlanNode {
	public readonly properties:{
		readonly entries:Centry_expression_list
	};
	public readonly inferences:{
		entry_list: () => application.Centry_list
	} = {
		entry_list: cache((detach:boolean) => {
			const gui_model__gui_action_expression__operation__entry_list_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.action())
				.then(context => {
					if (context?.properties.operation.state.name === 'entry list') {
						return resolve(context.properties.operation.state.node as application.Centry_list).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tentry_list, public parent:Cgui_action_expression) {
		super();
		const $this = this;
		this.properties = {
			entries: new Centry_list.Dentries(init['entries'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'entry list'`; }
	public get entity() { return this.parent.entity; }
}
export type Texecute_operation = {
	'bind view':string;
	'expression':Tobject_expression;
	'operation':['action', Taction__operation]|'command'|['command', {}];
};
export class Cexecute_operation extends AlanNode {
	public readonly properties:{
		readonly bind_view:Cexecute_operation.Dbind_view,
		readonly expression:Cobject_expression,
		readonly operation:Cexecute_operation.Doperation
	};
	public readonly inferences:{
		execute_operation: () => application.Cexecute_operation__operation__ui_expression
	} = {
		execute_operation: cache((detach:boolean) => {
			const gui_model__gui_action_expression__operation__execute_operation_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.action())
				.then(context => {
					if (context?.properties.operation.state.name === 'execute operation') {
						return resolve(context.properties.operation.state.node as application.Cexecute_operation__operation__ui_expression).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Texecute_operation, public parent:Cgui_action_expression) {
		super();
		const $this = this;
		this.properties = {
			bind_view: new Cexecute_operation.Dbind_view(init['bind view'], $this),
			expression: new Cexecute_operation.Dexpression(init['expression'], $this),
			operation: new Cexecute_operation.Doperation(init['operation'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'execute operation'`; }
	public get entity() { return this.parent.entity; }
}
export type Taction__operation = {
	'sub action':Tgui_action;
};
export class Caction__operation extends AlanNode {
	public readonly properties:{
		readonly sub_action:Cgui_action
	};
	public readonly inferences:{
		action: () => application.Caction
	} = {
		action: cache((detach:boolean) => {
			const gui_model__gui_action_expression__operation__execute_operation__operation__action_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.execute_operation())
				.then(context => context?.properties.operation?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'action') {
						return resolve(context.properties.type.state.node as application.Caction).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Taction__operation, public parent:Cexecute_operation) {
		super();
		const $this = this;
		this.properties = {
			sub_action: new Caction__operation.Dsub_action(init['sub action'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'action'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcommand__operation = {
};
export class Ccommand__operation extends AlanNode {
	public readonly inferences:{
		command: () => application.Ccommand__type
	} = {
		command: cache((detach:boolean) => {
			const gui_model__gui_action_expression__operation__execute_operation__operation__command_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.execute_operation())
				.then(context => context?.properties.operation?.ref)
				.then(context => {
					if (context?.properties.type.state.name === 'command') {
						return resolve(context.properties.type.state.node as application.Ccommand__type).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tcommand__operation, public parent:Cexecute_operation) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'command'`; }
	public get entity() { return this.parent.entity; }
}
export type Tignore = {
};
export class Cignore extends AlanNode {
	public readonly inferences:{
		ignore: () => application.Cignore__operation__ui_expression
	} = {
		ignore: cache((detach:boolean) => {
			const gui_model__gui_action_expression__operation__ignore_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.action())
				.then(context => {
					if (context?.properties.operation.state.name === 'ignore') {
						return resolve(context.properties.operation.state.node as application.Cignore__operation__ui_expression).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tignore, public parent:Cgui_action_expression) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'ignore'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnode_switch = {
	'cases':Tcases;
};
export class Cnode_switch extends AlanNode {
	public readonly properties:{
		readonly cases:Ccases
	};
	public readonly inferences:{
		node_switch: () => application.Cnode_switch__operation__ui_expression
	} = {
		node_switch: cache((detach:boolean) => {
			const gui_model__gui_action_expression__operation__node_switch_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.action())
				.then(context => {
					if (context?.properties.operation.state.name === 'node switch') {
						return resolve(context.properties.operation.state.node as application.Cnode_switch__operation__ui_expression).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tnode_switch, public parent:Cgui_action_expression) {
		super();
		const $this = this;
		this.properties = {
			cases: new Ccases(init['cases'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'node switch'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcases = {
	'false case':Tfalse_case;
	'true case':Ttrue_case;
};
export class Ccases extends AlanNode {
	public readonly properties:{
		readonly false_case:Cfalse_case,
		readonly true_case:Ctrue_case
	};
	constructor(init:Tcases, public parent:Cnode_switch) {
		super();
		const $this = this;
		this.properties = {
			false_case: new Cfalse_case(init['false case'], $this),
			true_case: new Ctrue_case(init['true case'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'cases'`; }
	public get entity() { return this.parent.entity; }
}
export type Tfalse_case = {
	'expression':Tgui_action_expression;
};
export class Cfalse_case extends AlanNode {
	public readonly properties:{
		readonly expression:Cgui_action_expression
	};
	constructor(init:Tfalse_case, public parent:Ccases) {
		super();
		const $this = this;
		this.properties = {
			expression: new Cfalse_case.Dexpression(init['expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'false case'`; }
	public get entity() { return this.parent.entity; }
}
export type Ttrue_case = {
	'expression':Tgui_action_expression;
};
export class Ctrue_case extends AlanNode {
	public readonly properties:{
		readonly expression:Cgui_action_expression
	};
	constructor(init:Ttrue_case, public parent:Ccases) {
		super();
		const $this = this;
		this.properties = {
			expression: new Ctrue_case.Dexpression(init['expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'true case'`; }
	public get entity() { return this.parent.entity; }
}
export type Tproduce_value = {
	'value':['object', Tobject]|'scalar'|['scalar', {}]|['state group', Tstate_group__value];
};
export class Cproduce_value extends AlanNode {
	public readonly properties:{
		readonly value:Cproduce_value.Dvalue
	};
	public readonly inferences:{
		produce_value: () => application.Cproduce_value__operation__ui_expression
	} = {
		produce_value: cache((detach:boolean) => {
			const gui_model__gui_action_expression__operation__produce_value_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.action())
				.then(context => {
					if (context?.properties.operation.state.name === 'produce value') {
						return resolve(context.properties.operation.state.node as application.Cproduce_value__operation__ui_expression).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tproduce_value, public parent:Cgui_action_expression) {
		super();
		const $this = this;
		this.properties = {
			value: new Cproduce_value.Dvalue(init['value'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'produce value'`; }
	public get entity() { return this.parent.entity; }
}
export type Tobject = {
	'expression':Tobject_expression;
};
export class Cobject extends AlanNode {
	public readonly properties:{
		readonly expression:Cobject_expression
	};
	public readonly inferences:{
		object: () => application.Cobject__value__produce_value__operation__ui_expression
	} = {
		object: cache((detach:boolean) => {
			const gui_model__gui_action_expression__operation__produce_value__value__object_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.produce_value())
				.then(context => {
					if (context?.properties.value.state.name === 'object') {
						return resolve(context.properties.value.state.node as application.Cobject__value__produce_value__operation__ui_expression).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tobject, public parent:Cproduce_value) {
		super();
		const $this = this;
		this.properties = {
			expression: new Cobject.Dexpression(init['expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'value'?'object'`; }
	public get entity() { return this.parent.entity; }
}
export type Tscalar = {
};
export class Cscalar extends AlanNode {
	public readonly inferences:{
		scalar: () => application.Cscalar__value__produce_value__operation__ui_expression
	} = {
		scalar: cache((detach:boolean) => {
			const gui_model__gui_action_expression__operation__produce_value__value__scalar_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.produce_value())
				.then(context => {
					if (context?.properties.value.state.name === 'scalar') {
						return resolve(context.properties.value.state.node as application.Cscalar__value__produce_value__operation__ui_expression).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tscalar, public parent:Cproduce_value) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'value'?'scalar'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate_group__value = {
	'expression':Tobject_expression;
};
export class Cstate_group__value extends AlanNode {
	public readonly properties:{
		readonly expression:Cobject_expression
	};
	public readonly inferences:{
		state_group: () => application.Cstate__value__produce_value__operation__ui_expression
	} = {
		state_group: cache((detach:boolean) => {
			const gui_model__gui_action_expression__operation__produce_value__value__state_group_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.produce_value())
				.then(context => {
					if (context?.properties.value.state.name === 'state') {
						return resolve(context.properties.value.state.node as application.Cstate__value__produce_value__operation__ui_expression).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tstate_group__value, public parent:Cproduce_value) {
		super();
		const $this = this;
		this.properties = {
			expression: new Cstate_group__value.Dexpression(init['expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'value'?'state group'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate_switch__operation = {
	'states':Record<string, Tstates__state_switch>;
};
export class Cstate_switch__operation extends AlanNode {
	public readonly properties:{
		readonly states:Cstate_switch__operation.Dstates
	};
	public readonly inferences:{
		state_switch: () => application.Cstate_switch__operation__ui_expression
	} = {
		state_switch: cache((detach:boolean) => {
			const gui_model__gui_action_expression__operation__state_switch_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.action())
				.then(context => {
					if (context?.properties.operation.state.name === 'state switch') {
						return resolve(context.properties.operation.state.node as application.Cstate_switch__operation__ui_expression).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tstate_switch__operation, public parent:Cgui_action_expression) {
		super();
		const $this = this;
		this.properties = {
			states: new Cstate_switch__operation.Dstates(init['states'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'state switch'`; }
	public get entity() { return this.parent.entity; }
}
export class Kstates__state_switch extends Reference<application.Cstates__state_switch__operation__ui_expression, string> {
	constructor(key:string, $this:Cstates__state_switch) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.inferences.state_switch())
			.then(context => {
				const entry = context?.properties.states.get(this.entry)!;
				return resolve(entry).result;
			}).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'states'::key`; }
}
export type Tstates__state_switch = {
	'expression':Tgui_action_expression;
};
export class Cstates__state_switch extends AlanDictionaryEntry {
	public key:Kstates__state_switch;
	public get key_value() { return this.key.entry; }
	public readonly properties:{
		readonly expression:Cgui_action_expression
	};
	constructor(key:string, init:Tstates__state_switch, public parent:Cstate_switch__operation) {
		super();
		const $this = this;
		this.key = new Kstates__state_switch(key, $this);
		this.properties = {
			expression: new Cstates__state_switch.Dexpression(init['expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'states'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Tupdate_properties = {
	'target':['node', Tnode__target]|['property', Tproperty__target];
};
export class Cupdate_properties extends AlanNode {
	public readonly properties:{
		readonly target:Cupdate_properties.Dtarget
	};
	public readonly inferences:{
		update_properties: () => application.Cupdate_properties__operation__ui_expression
	} = {
		update_properties: cache((detach:boolean) => {
			const gui_model__gui_action_expression__operation__update_properties_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.action())
				.then(context => {
					if (context?.properties.operation.state.name === 'update properties') {
						return resolve(context.properties.operation.state.node as application.Cupdate_properties__operation__ui_expression).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tupdate_properties, public parent:Cgui_action_expression) {
		super();
		const $this = this;
		this.properties = {
			target: new Cupdate_properties.Dtarget(init['target'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'update properties'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnode__target = {
	'bind view':string;
	'expression':Tobject_expression;
};
export class Cnode__target extends AlanNode {
	public readonly properties:{
		readonly bind_view:Cnode__target.Dbind_view,
		readonly expression:Cobject_expression
	};
	public readonly inferences:{
		node: () => application.Cnode__target__update_properties__operation__ui_expression
	} = {
		node: cache((detach:boolean) => {
			const gui_model__gui_action_expression__operation__update_properties__target__node_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.update_properties())
				.then(context => {
					if (context?.properties.target.state.name === 'node') {
						return resolve(context.properties.target.state.node as application.Cnode__target__update_properties__operation__ui_expression).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tnode__target, public parent:Cupdate_properties) {
		super();
		const $this = this;
		this.properties = {
			bind_view: new Cnode__target.Dbind_view(init['bind view'], $this),
			expression: new Cnode__target.Dexpression(init['expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'target'?'node'`; }
	public get entity() { return this.parent.entity; }
}
export type Tproperty__target = {
	'action view':['available', Tavailable]|'unavailable'|['unavailable', {}];
	'expression':Tgui_action_expression;
};
export class Cproperty__target extends AlanNode {
	public readonly properties:{
		readonly action_view:Cproperty__target.Daction_view,
		readonly expression:Cgui_action_expression
	};
	public readonly inferences:{
		property: () => application.Cproperty__target__update_properties__operation__ui_expression
	} = {
		property: cache((detach:boolean) => {
			const gui_model__gui_action_expression__operation__update_properties__target__property_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.update_properties())
				.then(context => {
					if (context?.properties.target.state.name === 'property') {
						return resolve(context.properties.target.state.node as application.Cproperty__target__update_properties__operation__ui_expression).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tproperty__target, public parent:Cupdate_properties) {
		super();
		const $this = this;
		this.properties = {
			action_view: new Cproperty__target.Daction_view(init['action view'], $this),
			expression: new Cproperty__target.Dexpression(init['expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'target'?'property'`; }
	public get entity() { return this.parent.entity; }
}
export type Tavailable = {
	'bind view':string;
};
export class Cavailable extends AlanNode {
	public readonly properties:{
		readonly bind_view:Cavailable.Dbind_view
	};
	constructor(init:Tavailable, public parent:Cproperty__target) {
		super();
		const $this = this;
		this.properties = {
			bind_view: new Cavailable.Dbind_view(init['bind view'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'action view'?'available'`; }
	public get entity() { return this.parent.entity; }
}
export type Tunavailable = {
};
export class Cunavailable extends AlanNode {
	constructor(init:Tunavailable, public parent:Cproperty__target) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'action view'?'unavailable'`; }
	public get entity() { return this.parent.entity; }
}
export type Twalk = {
	'expression':Tgui_action_expression;
};
export class Cwalk extends AlanNode {
	public readonly properties:{
		readonly expression:Cgui_action_expression
	};
	public readonly inferences:{
		walk: () => application.Cwalk__operation__ui_expression
	} = {
		walk: cache((detach:boolean) => {
			const gui_model__gui_action_expression__operation__walk_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.action())
				.then(context => {
					if (context?.properties.operation.state.name === 'walk') {
						return resolve(context.properties.operation.state.node as application.Cwalk__operation__ui_expression).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Twalk, public parent:Cgui_action_expression) {
		super();
		const $this = this;
		this.properties = {
			expression: new Cwalk.Dexpression(init['expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'operation'?'walk'`; }
	public get entity() { return this.parent.entity; }
}
export type Tgui_action = {
	'expression':Tgui_action_expression;
	'has next action':'no'|['no', {}]|['yes', Tyes__has_next_action];
};
export class Cgui_action extends AlanNode {
	public readonly properties:{
		readonly expression:Cgui_action_expression,
		readonly has_next_action:Cgui_action.Dhas_next_action
	};
	constructor(init:Tgui_action, public location:AlanNode, public input: {
		action: () => application.Cui_action,
		gui_action: () => gui_model.Caction__type
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			expression: new Cgui_action.Dexpression(init['expression'], $this),
			has_next_action: new Cgui_action.Dhas_next_action(init['has next action'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_next_action = {
};
export class Cno__has_next_action extends AlanNode {
	public readonly inferences:{
		no: () => application.Cno__has_next_action
	} = {
		no: cache((detach:boolean) => {
			const gui_model__gui_action__has_next_action__no_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.action())
				.then(context => {
					if (context?.properties.has_next_action.state.name === 'no') {
						return resolve(context.properties.has_next_action.state.node as application.Cno__has_next_action).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tno__has_next_action, public parent:Cgui_action) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has next action'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_next_action = {
	'action':Tgui_action;
};
export class Cyes__has_next_action extends AlanNode {
	public readonly properties:{
		readonly action:Cgui_action
	};
	public readonly inferences:{
		yes: () => application.Cyes__has_next_action
	} = {
		yes: cache((detach:boolean) => {
			const gui_model__gui_action__has_next_action__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.action())
				.then(context => {
					if (context?.properties.has_next_action.state.name === 'yes') {
						return resolve(context.properties.has_next_action.state.node as application.Cyes__has_next_action).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tyes__has_next_action, public parent:Cgui_action) {
		super();
		const $this = this;
		this.properties = {
			action: new Cyes__has_next_action.Daction(init['action'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has next action'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tfile_name_expression = {
	'has steps':'no'|['no', {}]|['yes', Tyes__has_steps__file_name_expression];
};
export class Cfile_name_expression extends AlanNode {
	public readonly properties:{
		readonly has_steps:Cfile_name_expression.Dhas_steps
	};
	constructor(init:Tfile_name_expression, public location:AlanNode, public input: {
		gui_node: () => gui_model.Cgui_node
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_steps: new Cfile_name_expression.Dhas_steps(init['has steps'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_steps__file_name_expression = {
};
export class Cno__has_steps__file_name_expression extends AlanNode {
	constructor(init:Tno__has_steps__file_name_expression, public parent:Cfile_name_expression) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_steps__file_name_expression = {
	'tail':Tfile_name_expression;
	'type':['property', Tproperty__type__yes]|['static', Tstatic__type__yes];
};
export class Cyes__has_steps__file_name_expression extends AlanNode {
	public readonly properties:{
		readonly tail:Cfile_name_expression,
		readonly type:Cyes__has_steps__file_name_expression.Dtype
	};
	constructor(init:Tyes__has_steps__file_name_expression, public parent:Cfile_name_expression) {
		super();
		const $this = this;
		this.properties = {
			tail: new Cyes__has_steps__file_name_expression.Dtail(init['tail'], $this),
			type: new Cyes__has_steps__file_name_expression.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tproperty__type__yes = {
	'path':Tgui_static_singular_path;
	'type':['number', Tnumber__type__property__type__yes]|['state group', Tstate_group__type__property__type__yes]|['text', Ttext__type__property__type__yes];
};
export class Cproperty__type__yes extends AlanNode {
	public readonly properties:{
		readonly path:Cgui_static_singular_path,
		readonly type:Cproperty__type__yes.Dtype
	};
	constructor(init:Tproperty__type__yes, public parent:Cyes__has_steps__file_name_expression) {
		super();
		const $this = this;
		this.properties = {
			path: new Cproperty__type__yes.Dpath(init['path'], $this),
			type: new Cproperty__type__yes.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'property'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumber__type__property__type__yes = {
	'number':string;
};
export class Cnumber__type__property__type__yes extends AlanNode {
	public readonly properties:{
		readonly number:Cnumber__type__property__type__yes.Dnumber
	};
	constructor(init:Tnumber__type__property__type__yes, public parent:Cproperty__type__yes) {
		super();
		const $this = this;
		this.properties = {
			number: new Cnumber__type__property__type__yes.Dnumber(init['number'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'number'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate_group__type__property__type__yes = {
	'state group':string;
	'states':Record<string, Tstates__state_group__type__property__type__yes>;
};
export class Cstate_group__type__property__type__yes extends AlanNode {
	public readonly properties:{
		readonly state_group:Cstate_group__type__property__type__yes.Dstate_group,
		readonly states:Cstate_group__type__property__type__yes.Dstates
	};
	constructor(init:Tstate_group__type__property__type__yes, public parent:Cproperty__type__yes) {
		super();
		const $this = this;
		this.properties = {
			state_group: new Cstate_group__type__property__type__yes.Dstate_group(init['state group'], $this),
			states: new Cstate_group__type__property__type__yes.Dstates(init['states'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state group'`; }
	public get entity() { return this.parent.entity; }
}
export class Kstates__state_group__type__property__type__yes extends Reference<gui_model.Cstates__state_group__type__property__type__attributes, string> {
	constructor(key:string, $this:Cstates__state_group__type__property__type__yes) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.properties.state_group?.ref)
			.then(context => {
				const entry = context?.properties.states.get(this.entry)!;
				return resolve(entry).result;
			}).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'states'::key`; }
}
export type Tstates__state_group__type__property__type__yes = {
	'state file name expression':Tfile_name_expression;
};
export class Cstates__state_group__type__property__type__yes extends AlanDictionaryEntry {
	public key:Kstates__state_group__type__property__type__yes;
	public get key_value() { return this.key.entry; }
	public readonly properties:{
		readonly state_file_name_expression:Cfile_name_expression
	};
	constructor(key:string, init:Tstates__state_group__type__property__type__yes, public parent:Cstate_group__type__property__type__yes) {
		super();
		const $this = this;
		this.key = new Kstates__state_group__type__property__type__yes(key, $this);
		this.properties = {
			state_file_name_expression: new Cstates__state_group__type__property__type__yes.Dstate_file_name_expression(init['state file name expression'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'states'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Ttext__type__property__type__yes = {
	'text':string;
};
export class Ctext__type__property__type__yes extends AlanNode {
	public readonly properties:{
		readonly text:Ctext__type__property__type__yes.Dtext
	};
	constructor(init:Ttext__type__property__type__yes, public parent:Cproperty__type__yes) {
		super();
		const $this = this;
		this.properties = {
			text: new Ctext__type__property__type__yes.Dtext(init['text'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'text'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstatic__type__yes = {
	'text':string;
};
export class Cstatic__type__yes extends AlanNode {
	public readonly properties:{
		readonly text:string
	};
	constructor(init:Tstatic__type__yes, public parent:Cyes__has_steps__file_name_expression) {
		super();
		const $this = this;
		this.properties = {
			text: init['text']
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'static'`; }
	public get entity() { return this.parent.entity; }
}
export type Tentry_expression_list = {
	'expression':Tgui_action_expression;
	'more entries':'no'|['no', {}]|['yes', Tyes__more_entries];
};
export class Centry_expression_list extends AlanNode {
	public readonly properties:{
		readonly expression:Cgui_action_expression,
		readonly more_entries:Centry_expression_list.Dmore_entries
	};
	constructor(init:Tentry_expression_list, public location:AlanNode, public input: {
		entry_list: () => application.Cui_entry_expression_list,
		gui_action: () => gui_model.Caction__type
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			expression: new Centry_expression_list.Dexpression(init['expression'], $this),
			more_entries: new Centry_expression_list.Dmore_entries(init['more entries'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__more_entries = {
};
export class Cno__more_entries extends AlanNode {
	public readonly inferences:{
		no: () => application.Cno__more_entries
	} = {
		no: cache((detach:boolean) => {
			const gui_model__entry_expression_list__more_entries__no_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.entry_list())
				.then(context => {
					if (context?.properties.more_entries.state.name === 'no') {
						return resolve(context.properties.more_entries.state.node as application.Cno__more_entries).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tno__more_entries, public parent:Centry_expression_list) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'more entries'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__more_entries = {
	'tail':Tentry_expression_list;
};
export class Cyes__more_entries extends AlanNode {
	public readonly properties:{
		readonly tail:Centry_expression_list
	};
	public readonly inferences:{
		yes: () => application.Cyes__more_entries
	} = {
		yes: cache((detach:boolean) => {
			const gui_model__entry_expression_list__more_entries__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.entry_list())
				.then(context => {
					if (context?.properties.more_entries.state.name === 'yes') {
						return resolve(context.properties.more_entries.state.node as application.Cyes__more_entries).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tyes__more_entries, public parent:Centry_expression_list) {
		super();
		const $this = this;
		this.properties = {
			tail: new Cyes__more_entries.Dtail(init['tail'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'more entries'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tduplication_node_mapping = {
	'properties':Record<string, Tproperties__duplication_node_mapping>;
};
export class Cduplication_node_mapping extends AlanNode {
	public readonly properties:{
		readonly properties:Cduplication_node_mapping.Dproperties
	};
	constructor(init:Tduplication_node_mapping, public location:AlanNode, public input: {
		context_node: () => gui_model.Cgui_node
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			properties: new Cduplication_node_mapping.Dproperties(init['properties'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export class Kproperties__duplication_node_mapping extends Reference<gui_model.Cattributes, string> {
	public readonly inferences:{
		property: () => gui_model.Cproperty__type__attributes
	}
	constructor(key:string, $this:Cproperties__duplication_node_mapping) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.component_root.input.context_node())
			.then(context => {
				const entry = context?.properties.attributes.get(this.entry)!;
				return resolve(entry).result;
			}).result!), $this.parent)
		this.inferences = {
			property: cache((detach:boolean) => {
				const gui_model__duplication_node_mapping__properties_key_nval = $this.key.ref;
				return resolve($this.key.ref)
					.then(context => gui_model__duplication_node_mapping__properties_key_nval)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return resolve(context.properties.type.state.node as gui_model.Cproperty__type__attributes).result;
						} else {
							return undefined;
						}
					}).result!;
			})

		}
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'properties'::key`; }
}
export type Tproperties__duplication_node_mapping = {
	'type':'collection'|['collection', {}]|'file'|['file', {}]|['group', Tgroup__type__properties]|'number'|['number', {}]|['state group', Tstate_group__type__properties]|'text'|['text', {}];
};
export class Cproperties__duplication_node_mapping extends AlanDictionaryEntry {
	public key:Kproperties__duplication_node_mapping;
	public get key_value() { return this.key.entry; }
	public get inferences() { return this.key.inferences; }
	public readonly properties:{
		readonly type:Cproperties__duplication_node_mapping.Dtype
	};
	constructor(key:string, init:Tproperties__duplication_node_mapping, public parent:Cduplication_node_mapping) {
		super();
		const $this = this;
		this.key = new Kproperties__duplication_node_mapping(key, $this);
		this.properties = {
			type: new Cproperties__duplication_node_mapping.Dtype(init['type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'properties'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Tcollection__type__properties = {
};
export class Ccollection__type__properties extends AlanNode {
	public readonly inferences:{
		collection: () => gui_model.Ccollection__type__property
	} = {
		collection: cache((detach:boolean) => {
			const gui_model__duplication_node_mapping__properties__type__collection_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.property())
				.then(context => {
					if (context?.properties.type.state.name === 'collection') {
						return resolve(context.properties.type.state.node as gui_model.Ccollection__type__property).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tcollection__type__properties, public parent:Cproperties__duplication_node_mapping) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'collection'`; }
	public get entity() { return this.parent.entity; }
}
export type Tfile__type__properties = {
};
export class Cfile__type__properties extends AlanNode {
	public readonly inferences:{
		file: () => gui_model.Cfile__type__property
	} = {
		file: cache((detach:boolean) => {
			const gui_model__duplication_node_mapping__properties__type__file_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.property())
				.then(context => {
					if (context?.properties.type.state.name === 'file') {
						return resolve(context.properties.type.state.node as gui_model.Cfile__type__property).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tfile__type__properties, public parent:Cproperties__duplication_node_mapping) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'file'`; }
	public get entity() { return this.parent.entity; }
}
export type Tgroup__type__properties = {
	'mapping':Tduplication_node_mapping;
};
export class Cgroup__type__properties extends AlanNode {
	public readonly properties:{
		readonly mapping:Cduplication_node_mapping
	};
	public readonly inferences:{
		group: () => gui_model.Cgroup__type__property
	} = {
		group: cache((detach:boolean) => {
			const gui_model__duplication_node_mapping__properties__type__group_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.property())
				.then(context => {
					if (context?.properties.type.state.name === 'group') {
						return resolve(context.properties.type.state.node as gui_model.Cgroup__type__property).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tgroup__type__properties, public parent:Cproperties__duplication_node_mapping) {
		super();
		const $this = this;
		this.properties = {
			mapping: new Cgroup__type__properties.Dmapping(init['mapping'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'group'`; }
	public get entity() { return this.parent.entity; }
}
export type Tnumber__type__properties = {
};
export class Cnumber__type__properties extends AlanNode {
	public readonly inferences:{
		number: () => gui_model.Cnumber__type__property__type__attributes
	} = {
		number: cache((detach:boolean) => {
			const gui_model__duplication_node_mapping__properties__type__number_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.property())
				.then(context => {
					if (context?.properties.type.state.name === 'number') {
						return resolve(context.properties.type.state.node as gui_model.Cnumber__type__property__type__attributes).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tnumber__type__properties, public parent:Cproperties__duplication_node_mapping) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'number'`; }
	public get entity() { return this.parent.entity; }
}
export type Tstate_group__type__properties = {
	'states':Record<string, Tstates__state_group__type__properties>;
};
export class Cstate_group__type__properties extends AlanNode {
	public readonly properties:{
		readonly states:Cstate_group__type__properties.Dstates
	};
	public readonly inferences:{
		non_verifiable: () => gui_model.Cstate_group__type__property__type__attributes,
		state_group: () => gui_model.Cstate_group__type__property__type__attributes
	} = {
		non_verifiable: cache((detach:boolean) => {
			const gui_model__duplication_node_mapping__properties__type__state_group_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.property())
				.then(context => {
					if (context?.properties.type.state.name === 'state group') {
						return resolve(context.properties.type.state.node as gui_model.Cstate_group__type__property__type__attributes).result;
					} else {
						return undefined;
					}
				}).result!;
		})
		,
		state_group: cache((detach:boolean) => {
			const gui_model__duplication_node_mapping__properties__type__state_group_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.property())
				.then(context => {
					if (context?.properties.type.state.name === 'state group') {
						return resolve(context.properties.type.state.node as gui_model.Cstate_group__type__property__type__attributes).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tstate_group__type__properties, public parent:Cproperties__duplication_node_mapping) {
		super();
		const $this = this;
		this.properties = {
			states: new Cstate_group__type__properties.Dstates(init['states'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'state group'`; }
	public get entity() { return this.parent.entity; }
}
export class Kstates__state_group__type__properties extends Reference<gui_model.Cstates__state_group__type__property__type__attributes, string> {
	constructor(key:string, $this:Cstates__state_group__type__properties) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.inferences.state_group())
			.then(context => {
				const entry = context?.properties.states.get(this.entry)!;
				return resolve(entry).result;
			}).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'states'::key`; }
}
export type Tstates__state_group__type__properties = {
	'mapping':Tduplication_node_mapping;
};
export class Cstates__state_group__type__properties extends AlanDictionaryEntry {
	public key:Kstates__state_group__type__properties;
	public get key_value() { return this.key.entry; }
	public readonly properties:{
		readonly mapping:Cduplication_node_mapping
	};
	constructor(key:string, init:Tstates__state_group__type__properties, public parent:Cstate_group__type__properties) {
		super();
		const $this = this;
		this.key = new Kstates__state_group__type__properties(key, $this);
		this.properties = {
			mapping: new Cstates__state_group__type__properties.Dmapping(init['mapping'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'states'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type Ttext__type__properties = {
};
export class Ctext__type__properties extends AlanNode {
	public readonly inferences:{
		text: () => gui_model.Ctext__type__property__type__attributes
	} = {
		text: cache((detach:boolean) => {
			const gui_model__duplication_node_mapping__properties__type__text_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.inferences.property())
				.then(context => {
					if (context?.properties.type.state.name === 'text') {
						return resolve(context.properties.type.state.node as gui_model.Ctext__type__property__type__attributes).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Ttext__type__properties, public parent:Cproperties__duplication_node_mapping) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'type'?'text'`; }
	public get entity() { return this.parent.entity; }
}
export type Tancestor_node_path = {
	'has steps':'no'|['no', {}]|['yes', Tyes__has_steps__ancestor_node_path];
};
export class Cancestor_node_path extends AlanNode {
	public readonly properties:{
		readonly has_steps:Cancestor_node_path.Dhas_steps
	};
	public readonly output:{
		entity: () => gui_model.Cgui_node;
		node_type: () => gui_model.Cgui_node;
	} = {
		entity: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_steps?.state.node.output.node_type())
			.then(context => context?.component_root.output.gui_entity()).result!),
		node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.has_steps?.state.node.output.node_type()).result!)
	};
	constructor(init:Tancestor_node_path, public location:AlanNode, public input: {
		node_type: () => gui_model.Cgui_node
	}, private key: string) {
		super();
		const $this = this;
		this.properties = {
			has_steps: new Cancestor_node_path.Dhas_steps(init['has steps'], $this)
		};
	}
	public get root() { return this._root ??= this.location.root; }
	public get component_root() { return this; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.location.path}.'${this.key}'`; }
	public get entity() { return this.location.entity; }
}
export type Tno__has_steps__ancestor_node_path = {
};
export class Cno__has_steps__ancestor_node_path extends AlanNode {
	public readonly output:{
		node_type: () => gui_model.Cgui_node;
	} = {
		node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.component_root.input.node_type()).result!)
	}
	constructor(init:Tno__has_steps__ancestor_node_path, public parent:Cancestor_node_path) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_steps__ancestor_node_path = {
	'tail':Tancestor_node_path;
};
export class Cyes__has_steps__ancestor_node_path extends AlanNode {
	public readonly properties:{
		readonly tail:Cancestor_node_path
	};
	public readonly output:{
		node_type: () => gui_model.Cgui_node;
	} = {
		node_type: cache((detach:boolean) => resolve(this)
			.then(() => this)
			.then(context => context?.properties.tail)
			.then(context => context?.component_root.output.node_type()).result!)
	}
	public readonly inferences:{
		parent_node_type: () => gui_model.Cgui_node
	} = {
		parent_node_type: cache((detach:boolean) => {
			const gui_model__ancestor_node_path__has_steps__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.component_root.input.node_type())
				.then(context => context?.component_root.output.member())
				.then(context => context?.component_root.output.parent())
				.then(context => context?.variant.name === 'gui node' ? context.variant.definition as gui_model.Cgui_node : undefined).result!;
		})

	}
	constructor(init:Tyes__has_steps__ancestor_node_path, public parent:Cancestor_node_path) {
		super();
		const $this = this;
		this.properties = {
			tail: new Cyes__has_steps__ancestor_node_path.Dtail(init['tail'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has steps'?'yes'`; }
	public get entity() { return this.parent.entity; }
}

export type Tgui_model = {
	'has user node':'no'|['no', {}]|['yes', Tyes__has_user_node];
	'numerical types':Record<string, Tnumerical_types>;
	'root':Tgui_node;
};
export class Cgui_model extends AlanNode {
	public key?:string;
	public get root() { return this; }
	public definitions:{
		gui_node_location: Cgui_node_location;
		member: Cmember;
	} = {
		gui_node_location: new Cgui_node_location({name:'root', definition: this}),
		member: new Cmember({name:'root', definition: this}, {
			parent: cache((detach:boolean) => resolve(this)
				.then(() => this)
				.then(() => gui_model.Cmember_parent.Pnone).result!)
		})
	}
	public readonly properties:{
		readonly has_user_node:Cgui_model.Dhas_user_node,
		readonly numerical_types:Cgui_model.Dnumerical_types,
		readonly root:Cgui_node
	};
	constructor(init:Tgui_model, public readonly input: {
	'application':application.Capplication}, symbols_file?: SymbolsFile) {
		super();
		const $this = this;
		this.properties = {
			has_user_node: new Cgui_model.Dhas_user_node(init['has user node'], $this),
			numerical_types: new Cgui_model.Dnumerical_types(init['numerical types'], $this),
			root: new Cgui_model.Droot(init['root'], $this)
		};
	}
	public get path() { return ``; }
	public get entity() { return this; }
}
export type Tno__has_user_node = {
};
export class Cno__has_user_node extends AlanNode {
	constructor(init:Tno__has_user_node, public parent:Cgui_model) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has user node'?'no'`; }
	public get entity() { return this.parent.entity; }
}
export type Tyes__has_user_node = {
	'context':Tgui_static_conditional_path;
	'users':string;
};
export class Cyes__has_user_node extends AlanNode {
	public readonly properties:{
		readonly context:Cgui_static_conditional_path,
		readonly users:Cyes__has_user_node.Dusers
	};
	public readonly inferences:{
		dynamic_users: () => application.Cyes__has_dynamic_users
	} = {
		dynamic_users: cache((detach:boolean) => {
			const gui_model__has_user_node__yes_nval = this.parent;
			return resolve(this.parent)
				.then(() => this.parent)
				.then(context => context?.root.input.application)
				.then(context => {
					if (context?.properties.has_dynamic_users.state.name === 'yes') {
						return resolve(context.properties.has_dynamic_users.state.node as application.Cyes__has_dynamic_users).result;
					} else {
						return undefined;
					}
				}).result!;
		})

	}
	constructor(init:Tyes__has_user_node, public parent:Cgui_model) {
		super();
		const $this = this;
		this.properties = {
			context: new Cyes__has_user_node.Dcontext(init['context'], $this),
			users: new Cyes__has_user_node.Dusers(init['users'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'has user node'?'yes'`; }
	public get entity() { return this.parent.entity; }
}
export class Knumerical_types extends Reference<application.Cnumerical_types, string> {
	constructor(key:string, $this:Cnumerical_types) {
		super(key, cache((detach:boolean) => resolve($this.parent)
			.then(() => $this.parent)
			.then(context => context?.root.input.application)
			.then(context => {
				const entry = context?.properties.numerical_types.get(this.entry)!;
				return resolve(entry).result;
			}).result!), $this.parent)
	}
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'numerical types'::key`; }
}
export type Tnumerical_types = {
	'representation type':'HTML date and time'|['HTML date and time', {}]|'date'|['date', {}]|'date and time'|['date and time', {}]|['duration', Tduration]|['model', Tmodel__representation_type];
};
export class Cnumerical_types extends AlanDictionaryEntry {
	public key:Knumerical_types;
	public get key_value() { return this.key.entry; }
	public readonly properties:{
		readonly representation_type:Cnumerical_types.Drepresentation_type
	};
	constructor(key:string, init:Tnumerical_types, public parent:Cgui_model) {
		super();
		const $this = this;
		this.key = new Knumerical_types(key, $this);
		this.properties = {
			representation_type: new Cnumerical_types.Drepresentation_type(init['representation type'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'numerical types'['${this.key.entry}']`; }
	public get entity() { return this; }
}
export type THTML_date_and_time = {
};
export class CHTML_date_and_time extends AlanNode {
	constructor(init:THTML_date_and_time, public parent:Cnumerical_types) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'representation type'?'HTML date and time'`; }
	public get entity() { return this.parent.entity; }
}
export type Tdate = {
};
export class Cdate extends AlanNode {
	constructor(init:Tdate, public parent:Cnumerical_types) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'representation type'?'date'`; }
	public get entity() { return this.parent.entity; }
}
export type Tdate_and_time = {
};
export class Cdate_and_time extends AlanNode {
	constructor(init:Tdate_and_time, public parent:Cnumerical_types) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'representation type'?'date and time'`; }
	public get entity() { return this.parent.entity; }
}
export type Tduration = {
	'unit':'hours'|['hours', {}]|'minutes'|['minutes', {}]|'seconds'|['seconds', {}];
};
export class Cduration extends AlanNode {
	public readonly properties:{
		readonly unit:Cduration.Dunit
	};
	constructor(init:Tduration, public parent:Cnumerical_types) {
		super();
		const $this = this;
		this.properties = {
			unit: new Cduration.Dunit(init['unit'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'representation type'?'duration'`; }
	public get entity() { return this.parent.entity; }
}
export type Thours = {
};
export class Chours extends AlanNode {
	constructor(init:Thours, public parent:Cduration) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'unit'?'hours'`; }
	public get entity() { return this.parent.entity; }
}
export type Tminutes = {
};
export class Cminutes extends AlanNode {
	constructor(init:Tminutes, public parent:Cduration) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'unit'?'minutes'`; }
	public get entity() { return this.parent.entity; }
}
export type Tseconds = {
};
export class Cseconds extends AlanNode {
	constructor(init:Tseconds, public parent:Cduration) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'unit'?'seconds'`; }
	public get entity() { return this.parent.entity; }
}
export type Tmodel__representation_type = {
	'style':'currency'|['currency', {}]|'decimal'|['decimal', {}]|'engineering'|['engineering', {}]|'scientific'|['scientific', {}];
};
export class Cmodel__representation_type extends AlanNode {
	public readonly properties:{
		readonly style:Cmodel__representation_type.Dstyle
	};
	constructor(init:Tmodel__representation_type, public parent:Cnumerical_types) {
		super();
		const $this = this;
		this.properties = {
			style: new Cmodel__representation_type.Dstyle(init['style'], $this)
		};
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'representation type'?'model'`; }
	public get entity() { return this.parent.entity; }
}
export type Tcurrency = {
};
export class Ccurrency extends AlanNode {
	constructor(init:Tcurrency, public parent:Cmodel__representation_type) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'style'?'currency'`; }
	public get entity() { return this.parent.entity; }
}
export type Tdecimal = {
};
export class Cdecimal extends AlanNode {
	constructor(init:Tdecimal, public parent:Cmodel__representation_type) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'style'?'decimal'`; }
	public get entity() { return this.parent.entity; }
}
export type Tengineering = {
};
export class Cengineering extends AlanNode {
	constructor(init:Tengineering, public parent:Cmodel__representation_type) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'style'?'engineering'`; }
	public get entity() { return this.parent.entity; }
}
export type Tscientific = {
};
export class Cscientific extends AlanNode {
	constructor(init:Tscientific, public parent:Cmodel__representation_type) {
		super();
	}
	public get root() { return this._root ??= this.component_root.root; }
	public get component_root() { return this.parent.parent.parent; }
	private _path: string | undefined;
	public get path() { return this._path ??= `${this.parent.path}.'style'?'scientific'`; }
	public get entity() { return this.parent.entity; }
}

/* property classes */
export namespace Cancestor_node_path {
	export class Dhas_steps extends StateGroup<
		{ name: 'no', node:Cno__has_steps__ancestor_node_path, init:Tno__has_steps__ancestor_node_path}|
		{ name: 'yes', node:Cyes__has_steps__ancestor_node_path, init:Tyes__has_steps__ancestor_node_path}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_steps__ancestor_node_path, parent:Cancestor_node_path) => new Cno__has_steps__ancestor_node_path(init, parent);
				case 'yes': return (init:Tyes__has_steps__ancestor_node_path, parent:Cancestor_node_path) => new Cyes__has_steps__ancestor_node_path(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_steps__ancestor_node_path;
				case 'yes': return finalize_yes__has_steps__ancestor_node_path;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tancestor_node_path['has steps'], parent:Cancestor_node_path) {
			super(data, parent, 'has steps');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has steps'`; }
	}
}
export namespace Cyes__has_steps__ancestor_node_path {
	export class Dtail extends Cancestor_node_path {
		constructor(data:Tyes__has_steps__ancestor_node_path['tail'], parent:Cyes__has_steps__ancestor_node_path) {
			super(data, parent, {
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.parent_node_type()).result!)
			}, 'tail')
		}
	}
}
export namespace Cduplication_node_mapping {
	export class Dproperties extends AlanDictionary<{ node:Cproperties__duplication_node_mapping, init:Tproperties__duplication_node_mapping},Cduplication_node_mapping> {
		protected initialize(parent:Cduplication_node_mapping, key:string, entry_init:Tproperties__duplication_node_mapping) { return new Cproperties__duplication_node_mapping(key, entry_init, parent); }
		protected finalize = finalize_properties__duplication_node_mapping
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'properties'`; }
		constructor(data:Tduplication_node_mapping['properties'], parent:Cduplication_node_mapping) {
			super(data, parent);
		}
	}
}
export namespace Cproperties__duplication_node_mapping {
	export class Dtype extends StateGroup<
		{ name: 'collection', node:Ccollection__type__properties, init:Tcollection__type__properties}|
		{ name: 'file', node:Cfile__type__properties, init:Tfile__type__properties}|
		{ name: 'group', node:Cgroup__type__properties, init:Tgroup__type__properties}|
		{ name: 'number', node:Cnumber__type__properties, init:Tnumber__type__properties}|
		{ name: 'state group', node:Cstate_group__type__properties, init:Tstate_group__type__properties}|
		{ name: 'text', node:Ctext__type__properties, init:Ttext__type__properties}> {
		protected initializer(state: 'collection'|'file'|'group'|'number'|'state group'|'text') {
			switch (state) {
				case 'collection': return (init:Tcollection__type__properties, parent:Cproperties__duplication_node_mapping) => new Ccollection__type__properties(init, parent);
				case 'file': return (init:Tfile__type__properties, parent:Cproperties__duplication_node_mapping) => new Cfile__type__properties(init, parent);
				case 'group': return (init:Tgroup__type__properties, parent:Cproperties__duplication_node_mapping) => new Cgroup__type__properties(init, parent);
				case 'number': return (init:Tnumber__type__properties, parent:Cproperties__duplication_node_mapping) => new Cnumber__type__properties(init, parent);
				case 'state group': return (init:Tstate_group__type__properties, parent:Cproperties__duplication_node_mapping) => new Cstate_group__type__properties(init, parent);
				case 'text': return (init:Ttext__type__properties, parent:Cproperties__duplication_node_mapping) => new Ctext__type__properties(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'collection'|'file'|'group'|'number'|'state group'|'text') {
			switch (state) {
				case 'collection': return finalize_collection__type__properties;
				case 'file': return finalize_file__type__properties;
				case 'group': return finalize_group__type__properties;
				case 'number': return finalize_number__type__properties;
				case 'state group': return finalize_state_group__type__properties;
				case 'text': return finalize_text__type__properties;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tproperties__duplication_node_mapping['type'], parent:Cproperties__duplication_node_mapping) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cgroup__type__properties {
	export class Dmapping extends Cduplication_node_mapping {
		constructor(data:Tgroup__type__properties['mapping'], parent:Cgroup__type__properties) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.group())
					.then(context => context?.properties.gui_node).result!)
			}, 'mapping')
		}
	}
}
export namespace Cstate_group__type__properties {
	export class Dstates extends AlanDictionary<{ node:Cstates__state_group__type__properties, init:Tstates__state_group__type__properties},Cstate_group__type__properties> {
		protected initialize(parent:Cstate_group__type__properties, key:string, entry_init:Tstates__state_group__type__properties) { return new Cstates__state_group__type__properties(key, entry_init, parent); }
		protected finalize = finalize_states__state_group__type__properties
		protected eval_required_keys(detach:boolean = false):void {
			let this_obj = this.parent;
			function do_include(gui_model__duplication_node_mapping__properties__type__state_group__states_key_nval:gui_model.Cstates__state_group__type__property__type__attributes):boolean {
				return true;
			};
			resolve(this.parent)
			.then(() => this.parent)
			.then(context => context?.inferences.state_group())
			.then(context => {
				for (let [key,val] of context?.properties.states) {
					let tail_obj = resolve(val).result;
					if (tail_obj !== undefined && do_include(tail_obj)) {
						assert(this.get(key) !== undefined);
					}
				}
				return undefined;
			});
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'states'`; }
		constructor(data:Tstate_group__type__properties['states'], parent:Cstate_group__type__properties) {
			super(data, parent);
		}
	}
}
export namespace Cstates__state_group__type__properties {
	export class Dmapping extends Cduplication_node_mapping {
		constructor(data:Tstates__state_group__type__properties['mapping'], parent:Cstates__state_group__type__properties) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.key?.ref)
					.then(context => context?.properties.gui_node).result!)
			}, 'mapping')
		}
	}
}
export namespace Centry_expression_list {
	export class Dexpression extends Cgui_action_expression {
		constructor(data:Tentry_expression_list['expression'], parent:Centry_expression_list) {
			super(data, parent, {
				action: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.entry_list())
					.then(context => context?.properties.entry_expression).result!),
				gui_action: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.gui_action()).result!)
			}, 'expression')
		}
	}
	export class Dmore_entries extends StateGroup<
		{ name: 'no', node:Cno__more_entries, init:Tno__more_entries}|
		{ name: 'yes', node:Cyes__more_entries, init:Tyes__more_entries}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__more_entries, parent:Centry_expression_list) => new Cno__more_entries(init, parent);
				case 'yes': return (init:Tyes__more_entries, parent:Centry_expression_list) => new Cyes__more_entries(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__more_entries;
				case 'yes': return finalize_yes__more_entries;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tentry_expression_list['more entries'], parent:Centry_expression_list) {
			super(data, parent, 'more entries');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'more entries'`; }
	}
}
export namespace Cyes__more_entries {
	export class Dtail extends Centry_expression_list {
		constructor(data:Tyes__more_entries['tail'], parent:Cyes__more_entries) {
			super(data, parent, {
				entry_list: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.yes())
					.then(context => context?.properties.tail).result!),
				gui_action: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.gui_action()).result!)
			}, 'tail')
		}
	}
}
export namespace Cfile_name_expression {
	export class Dhas_steps extends StateGroup<
		{ name: 'no', node:Cno__has_steps__file_name_expression, init:Tno__has_steps__file_name_expression}|
		{ name: 'yes', node:Cyes__has_steps__file_name_expression, init:Tyes__has_steps__file_name_expression}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_steps__file_name_expression, parent:Cfile_name_expression) => new Cno__has_steps__file_name_expression(init, parent);
				case 'yes': return (init:Tyes__has_steps__file_name_expression, parent:Cfile_name_expression) => new Cyes__has_steps__file_name_expression(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_steps__file_name_expression;
				case 'yes': return finalize_yes__has_steps__file_name_expression;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tfile_name_expression['has steps'], parent:Cfile_name_expression) {
			super(data, parent, 'has steps');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has steps'`; }
	}
}
export namespace Cyes__has_steps__file_name_expression {
	export class Dtail extends Cfile_name_expression {
		constructor(data:Tyes__has_steps__file_name_expression['tail'], parent:Cyes__has_steps__file_name_expression) {
			super(data, parent, {
				gui_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.gui_node()).result!)
			}, 'tail')
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'property', node:Cproperty__type__yes, init:Tproperty__type__yes}|
		{ name: 'static', node:Cstatic__type__yes, init:Tstatic__type__yes}> {
		protected initializer(state: 'property'|'static') {
			switch (state) {
				case 'property': return (init:Tproperty__type__yes, parent:Cyes__has_steps__file_name_expression) => new Cproperty__type__yes(init, parent);
				case 'static': return (init:Tstatic__type__yes, parent:Cyes__has_steps__file_name_expression) => new Cstatic__type__yes(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'property'|'static') {
			switch (state) {
				case 'property': return finalize_property__type__yes;
				case 'static': return finalize_static__type__yes;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__has_steps__file_name_expression['type'], parent:Cyes__has_steps__file_name_expression) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cproperty__type__yes {
	export class Dpath extends Cgui_static_singular_path {
		constructor(data:Tproperty__type__yes['path'], parent:Cproperty__type__yes) {
			super(data, parent, {
				gui_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.gui_node()).result!)
			}, 'path')
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'number', node:Cnumber__type__property__type__yes, init:Tnumber__type__property__type__yes}|
		{ name: 'state group', node:Cstate_group__type__property__type__yes, init:Tstate_group__type__property__type__yes}|
		{ name: 'text', node:Ctext__type__property__type__yes, init:Ttext__type__property__type__yes}> {
		protected initializer(state: 'number'|'state group'|'text') {
			switch (state) {
				case 'number': return (init:Tnumber__type__property__type__yes, parent:Cproperty__type__yes) => new Cnumber__type__property__type__yes(init, parent);
				case 'state group': return (init:Tstate_group__type__property__type__yes, parent:Cproperty__type__yes) => new Cstate_group__type__property__type__yes(init, parent);
				case 'text': return (init:Ttext__type__property__type__yes, parent:Cproperty__type__yes) => new Ctext__type__property__type__yes(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'number'|'state group'|'text') {
			switch (state) {
				case 'number': return finalize_number__type__property__type__yes;
				case 'state group': return finalize_state_group__type__property__type__yes;
				case 'text': return finalize_text__type__property__type__yes;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tproperty__type__yes['type'], parent:Cproperty__type__yes) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cnumber__type__property__type__yes {
	export class Dnumber extends Reference<gui_model.Cnumber__type__property__type__attributes,string> {

		constructor(data:string, $this:Cnumber__type__property__type__yes) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => context?.properties.path)
				.then(context => context?.component_root.output.result_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as gui_model.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'number') {
							return context.properties.type.state.node as gui_model.Cnumber__type__property__type__attributes;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'number'`; }
	}
}
export namespace Cstate_group__type__property__type__yes {
	export class Dstate_group extends Reference<gui_model.Cstate_group__type__property__type__attributes,string> {

		constructor(data:string, $this:Cstate_group__type__property__type__yes) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => context?.properties.path)
				.then(context => context?.component_root.output.result_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as gui_model.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'state group') {
							return context.properties.type.state.node as gui_model.Cstate_group__type__property__type__attributes;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state group'`; }
	}
	export class Dstates extends AlanDictionary<{ node:Cstates__state_group__type__property__type__yes, init:Tstates__state_group__type__property__type__yes},Cstate_group__type__property__type__yes> {
		protected initialize(parent:Cstate_group__type__property__type__yes, key:string, entry_init:Tstates__state_group__type__property__type__yes) { return new Cstates__state_group__type__property__type__yes(key, entry_init, parent); }
		protected finalize = finalize_states__state_group__type__property__type__yes
		protected eval_required_keys(detach:boolean = false):void {
			let this_obj = this.parent;
			function do_include(gui_model__file_name_expression__has_steps__yes__type__property__type__state_group__states_key_nval:gui_model.Cstates__state_group__type__property__type__attributes):boolean {
				return true;
			};
			resolve(this.parent)
			.then(() => this.parent)
			.then(context => context?.properties.state_group?.ref)
			.then(context => {
				for (let [key,val] of context?.properties.states) {
					let tail_obj = resolve(val).result;
					if (tail_obj !== undefined && do_include(tail_obj)) {
						assert(this.get(key) !== undefined);
					}
				}
				return undefined;
			});
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'states'`; }
		constructor(data:Tstate_group__type__property__type__yes['states'], parent:Cstate_group__type__property__type__yes) {
			super(data, parent);
		}
	}
}
export namespace Cstates__state_group__type__property__type__yes {
	export class Dstate_file_name_expression extends Cfile_name_expression {
		constructor(data:Tstates__state_group__type__property__type__yes['state file name expression'], parent:Cstates__state_group__type__property__type__yes) {
			super(data, parent, {
				gui_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.key?.ref)
					.then(context => context?.properties.gui_node).result!)
			}, 'state file name expression')
		}
	}
}
export namespace Ctext__type__property__type__yes {
	export class Dtext extends Reference<gui_model.Ctext__type__property__type__attributes,string> {

		constructor(data:string, $this:Ctext__type__property__type__yes) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => context?.properties.path)
				.then(context => context?.component_root.output.result_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as gui_model.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'text') {
							return context.properties.type.state.node as gui_model.Ctext__type__property__type__attributes;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'text'`; }
	}
}
export namespace Cstatic__type__yes {
}
export namespace Cgui_action {
	export class Dexpression extends Cgui_action_expression {
		constructor(data:Tgui_action['expression'], parent:Cgui_action) {
			super(data, parent, {
				action: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.action())
					.then(context => context?.properties.expression).result!),
				gui_action: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.gui_action()).result!)
			}, 'expression')
		}
	}
	export class Dhas_next_action extends StateGroup<
		{ name: 'no', node:Cno__has_next_action, init:Tno__has_next_action}|
		{ name: 'yes', node:Cyes__has_next_action, init:Tyes__has_next_action}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_next_action, parent:Cgui_action) => new Cno__has_next_action(init, parent);
				case 'yes': return (init:Tyes__has_next_action, parent:Cgui_action) => new Cyes__has_next_action(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_next_action;
				case 'yes': return finalize_yes__has_next_action;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tgui_action['has next action'], parent:Cgui_action) {
			super(data, parent, 'has next action');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has next action'`; }
	}
}
export namespace Cyes__has_next_action {
	export class Daction extends Cgui_action {
		constructor(data:Tyes__has_next_action['action'], parent:Cyes__has_next_action) {
			super(data, parent, {
				action: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.yes())
					.then(context => context?.properties.action).result!),
				gui_action: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.gui_action()).result!)
			}, 'action')
		}
	}
}
export namespace Cgui_action_expression {
	export class Doperation extends StateGroup<
		{ name: 'entry', node:Centry, init:Tentry}|
		{ name: 'entry list', node:Centry_list, init:Tentry_list}|
		{ name: 'execute operation', node:Cexecute_operation, init:Texecute_operation}|
		{ name: 'ignore', node:Cignore, init:Tignore}|
		{ name: 'node switch', node:Cnode_switch, init:Tnode_switch}|
		{ name: 'produce value', node:Cproduce_value, init:Tproduce_value}|
		{ name: 'state switch', node:Cstate_switch__operation, init:Tstate_switch__operation}|
		{ name: 'update properties', node:Cupdate_properties, init:Tupdate_properties}|
		{ name: 'walk', node:Cwalk, init:Twalk}> {
		protected initializer(state: 'entry'|'entry list'|'execute operation'|'ignore'|'node switch'|'produce value'|'state switch'|'update properties'|'walk') {
			switch (state) {
				case 'entry': return (init:Tentry, parent:Cgui_action_expression) => new Centry(init, parent);
				case 'entry list': return (init:Tentry_list, parent:Cgui_action_expression) => new Centry_list(init, parent);
				case 'execute operation': return (init:Texecute_operation, parent:Cgui_action_expression) => new Cexecute_operation(init, parent);
				case 'ignore': return (init:Tignore, parent:Cgui_action_expression) => new Cignore(init, parent);
				case 'node switch': return (init:Tnode_switch, parent:Cgui_action_expression) => new Cnode_switch(init, parent);
				case 'produce value': return (init:Tproduce_value, parent:Cgui_action_expression) => new Cproduce_value(init, parent);
				case 'state switch': return (init:Tstate_switch__operation, parent:Cgui_action_expression) => new Cstate_switch__operation(init, parent);
				case 'update properties': return (init:Tupdate_properties, parent:Cgui_action_expression) => new Cupdate_properties(init, parent);
				case 'walk': return (init:Twalk, parent:Cgui_action_expression) => new Cwalk(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'entry'|'entry list'|'execute operation'|'ignore'|'node switch'|'produce value'|'state switch'|'update properties'|'walk') {
			switch (state) {
				case 'entry': return finalize_entry;
				case 'entry list': return finalize_entry_list;
				case 'execute operation': return finalize_execute_operation;
				case 'ignore': return finalize_ignore;
				case 'node switch': return finalize_node_switch;
				case 'produce value': return finalize_produce_value;
				case 'state switch': return finalize_state_switch__operation;
				case 'update properties': return finalize_update_properties;
				case 'walk': return finalize_walk;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tgui_action_expression['operation'], parent:Cgui_action_expression) {
			super(data, parent, 'operation');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'operation'`; }
	}
}
export namespace Centry {
	export class Dtype extends StateGroup<
		{ name: 'create', node:Ccreate, init:Tcreate}|
		{ name: 'delete', node:Cdelete, init:Tdelete}> {
		protected initializer(state: 'create'|'delete') {
			switch (state) {
				case 'create': return (init:Tcreate, parent:Centry) => new Ccreate(init, parent);
				case 'delete': return (init:Tdelete, parent:Centry) => new Cdelete(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'create'|'delete') {
			switch (state) {
				case 'create': return finalize_create;
				case 'delete': return finalize_delete;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tentry['type'], parent:Centry) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Ccreate {
	export class Dcollection_type extends StateGroup<
		{ name: 'node', node:Cnode__collection_type, init:Tnode__collection_type}|
		{ name: 'parameter', node:Cparameter, init:Tparameter}> {
		protected initializer(state: 'node'|'parameter') {
			switch (state) {
				case 'node': return (init:Tnode__collection_type, parent:Ccreate) => new Cnode__collection_type(init, parent);
				case 'parameter': return (init:Tparameter, parent:Ccreate) => new Cparameter(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'node'|'parameter') {
			switch (state) {
				case 'node': return finalize_node__collection_type;
				case 'parameter': return finalize_parameter;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcreate['collection type'], parent:Ccreate) {
			super(data, parent, 'collection type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'collection type'`; }
	}
	export class Dexpression extends Cobject_expression {
		constructor(data:Tcreate['expression'], parent:Ccreate) {
			super(data, parent, {
				gui_action: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.gui_action()).result!),
				object_expression: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.create())
					.then(context => context?.properties.expression).result!)
			}, 'expression')
		}
	}
}
export namespace Cnode__collection_type {
	export class Dbind_view extends Reference<gui_model.Cviews,string> {
		public readonly inferences:{
			constraint: () => application.Cnode__application
		}

		constructor(data:string, $this:Cnode__collection_type) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.gui_action())
				.then(context => {
					const entry = context?.properties.views.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				constraint: cache((detach:boolean) => {
					const gui_model__gui_action_expression__operation__entry__type__create__collection_type__node__bind_view_nval = $this.properties.bind_view.ref;
					return resolve($this.properties.bind_view.ref)
						.then(context => {
							const left = resolve(context)
								.then(context => gui_model__gui_action_expression__operation__entry__type__create__collection_type__node__bind_view_nval)
								.then(context => context?.properties.view_context)
								.then(context => context?.component_root.output.result_application_node()).result;
							const right = resolve(context)
								.then(() => $this)
								.then(context => context?.inferences.node_collection())
								.then(context => context?.properties.node).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'bind view'`; }
	}
}
export namespace Cdelete {
	export class Dbind_view extends Reference<gui_model.Cviews,string> {
		public readonly inferences:{
			constraint: () => application.Cnode__application,
			parent_node: () => application.Cnode__application
		}

		constructor(data:string, $this:Cdelete) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.gui_action())
				.then(context => {
					const entry = context?.properties.views.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				constraint: cache((detach:boolean) => {
					const gui_model__gui_action_expression__operation__entry__type__delete__bind_view_nval = $this.properties.bind_view.ref;
					return resolve($this.properties.bind_view.ref)
						.then(context => {
							const left = resolve(context)
								.then(context => gui_model__gui_action_expression__operation__entry__type__delete__bind_view_nval)
								.then(context => context?.properties.view_context)
								.then(context => context?.component_root.output.result_application_node()).result;
							const right = resolve(context)
								.then(() => $this.properties.bind_view.inferences.parent_node()).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})
				,
				parent_node: cache((detach:boolean) => {
					const gui_model__gui_action_expression__operation__entry__type__delete__bind_view_nval = $this.properties.bind_view.ref;
					return resolve($this.properties.bind_view.ref)
						.then(() => $this)
						.then(context => context?.inferences.entry_node())
						.then(context => context?.component_root.output.location())
						.then(context => context?.component_root.output.node())
						.then(context => context?.variant.name === 'node' ? context.variant.definition as application.Cnode__application : undefined).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'bind view'`; }
	}
}
export namespace Centry_list {
	export class Dentries extends Centry_expression_list {
		constructor(data:Tentry_list['entries'], parent:Centry_list) {
			super(data, parent, {
				entry_list: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.entry_list())
					.then(context => context?.properties.entries).result!),
				gui_action: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.gui_action()).result!)
			}, 'entries')
		}
	}
}
export namespace Cexecute_operation {
	export class Dbind_view extends Reference<gui_model.Cviews,string> {
		public readonly inferences:{
			constraint: () => application.Cnode__application
		}

		constructor(data:string, $this:Cexecute_operation) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.gui_action())
				.then(context => {
					const entry = context?.properties.views.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				constraint: cache((detach:boolean) => {
					const gui_model__gui_action_expression__operation__execute_operation__bind_view_nval = $this.properties.bind_view.ref;
					return resolve($this.properties.bind_view.ref)
						.then(context => {
							const left = resolve(context)
								.then(context => gui_model__gui_action_expression__operation__execute_operation__bind_view_nval)
								.then(context => context?.properties.view_context)
								.then(context => context?.component_root.output.result_application_node()).result;
							const right = resolve(context)
								.then(() => $this)
								.then(context => context?.inferences.execute_operation())
								.then(context => context?.properties.path)
								.then(context => context?.component_root.output.node_type()).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'bind view'`; }
	}
	export class Dexpression extends Cobject_expression {
		constructor(data:Texecute_operation['expression'], parent:Cexecute_operation) {
			super(data, parent, {
				gui_action: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.gui_action()).result!),
				object_expression: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.execute_operation())
					.then(context => context?.properties.expression).result!)
			}, 'expression')
		}
	}
	export class Doperation extends StateGroup<
		{ name: 'action', node:Caction__operation, init:Taction__operation}|
		{ name: 'command', node:Ccommand__operation, init:Tcommand__operation}> {
		protected initializer(state: 'action'|'command') {
			switch (state) {
				case 'action': return (init:Taction__operation, parent:Cexecute_operation) => new Caction__operation(init, parent);
				case 'command': return (init:Tcommand__operation, parent:Cexecute_operation) => new Ccommand__operation(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'action'|'command') {
			switch (state) {
				case 'action': return finalize_action__operation;
				case 'command': return finalize_command__operation;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Texecute_operation['operation'], parent:Cexecute_operation) {
			super(data, parent, 'operation');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'operation'`; }
	}
}
export namespace Caction__operation {
	export class Dsub_action extends Cgui_action {
		constructor(data:Taction__operation['sub action'], parent:Caction__operation) {
			super(data, parent, {
				action: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.action())
					.then(context => context?.properties.action).result!),
				gui_action: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.gui_action()).result!)
			}, 'sub action')
		}
	}
}
export namespace Cnode_switch {
}
export namespace Ccases {
}
export namespace Cfalse_case {
	export class Dexpression extends Cgui_action_expression {
		constructor(data:Tfalse_case['expression'], parent:Cfalse_case) {
			super(data, parent, {
				action: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.node_switch())
					.then(context => context?.properties.cases)
					.then(context => context?.properties.none_case)
					.then(context => context?.properties.expression).result!),
				gui_action: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.gui_action()).result!)
			}, 'expression')
		}
	}
}
export namespace Ctrue_case {
	export class Dexpression extends Cgui_action_expression {
		constructor(data:Ttrue_case['expression'], parent:Ctrue_case) {
			super(data, parent, {
				action: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.node_switch())
					.then(context => context?.properties.cases)
					.then(context => context?.properties.node_case)
					.then(context => context?.properties.expression).result!),
				gui_action: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.gui_action()).result!)
			}, 'expression')
		}
	}
}
export namespace Cproduce_value {
	export class Dvalue extends StateGroup<
		{ name: 'object', node:Cobject, init:Tobject}|
		{ name: 'scalar', node:Cscalar, init:Tscalar}|
		{ name: 'state group', node:Cstate_group__value, init:Tstate_group__value}> {
		protected initializer(state: 'object'|'scalar'|'state group') {
			switch (state) {
				case 'object': return (init:Tobject, parent:Cproduce_value) => new Cobject(init, parent);
				case 'scalar': return (init:Tscalar, parent:Cproduce_value) => new Cscalar(init, parent);
				case 'state group': return (init:Tstate_group__value, parent:Cproduce_value) => new Cstate_group__value(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'object'|'scalar'|'state group') {
			switch (state) {
				case 'object': return finalize_object;
				case 'scalar': return finalize_scalar;
				case 'state group': return finalize_state_group__value;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tproduce_value['value'], parent:Cproduce_value) {
			super(data, parent, 'value');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'value'`; }
	}
}
export namespace Cobject {
	export class Dexpression extends Cobject_expression {
		constructor(data:Tobject['expression'], parent:Cobject) {
			super(data, parent, {
				gui_action: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.gui_action()).result!),
				object_expression: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.object())
					.then(context => context?.properties.expression).result!)
			}, 'expression')
		}
	}
}
export namespace Cstate_group__value {
	export class Dexpression extends Cobject_expression {
		constructor(data:Tstate_group__value['expression'], parent:Cstate_group__value) {
			super(data, parent, {
				gui_action: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.gui_action()).result!),
				object_expression: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.state_group())
					.then(context => context?.properties.expression).result!)
			}, 'expression')
		}
	}
}
export namespace Cstate_switch__operation {
	export class Dstates extends AlanDictionary<{ node:Cstates__state_switch, init:Tstates__state_switch},Cstate_switch__operation> {
		protected initialize(parent:Cstate_switch__operation, key:string, entry_init:Tstates__state_switch) { return new Cstates__state_switch(key, entry_init, parent); }
		protected finalize = finalize_states__state_switch
		protected eval_required_keys(detach:boolean = false):void {
			let this_obj = this.parent;
			function do_include(gui_model__gui_action_expression__operation__state_switch__states_key_nval:application.Cstates__state_switch__operation__ui_expression):boolean {
				return true;
			};
			resolve(this.parent)
			.then(() => this.parent)
			.then(context => context?.inferences.state_switch())
			.then(context => {
				for (let [key,val] of context?.properties.states) {
					let tail_obj = resolve(val).result;
					if (tail_obj !== undefined && do_include(tail_obj)) {
						assert(this.get(key) !== undefined);
					}
				}
				return undefined;
			});
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'states'`; }
		constructor(data:Tstate_switch__operation['states'], parent:Cstate_switch__operation) {
			super(data, parent);
		}
	}
}
export namespace Cstates__state_switch {
	export class Dexpression extends Cgui_action_expression {
		constructor(data:Tstates__state_switch['expression'], parent:Cstates__state_switch) {
			super(data, parent, {
				action: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.key?.ref)
					.then(context => context?.properties.expression).result!),
				gui_action: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.gui_action()).result!)
			}, 'expression')
		}
	}
}
export namespace Cupdate_properties {
	export class Dtarget extends StateGroup<
		{ name: 'node', node:Cnode__target, init:Tnode__target}|
		{ name: 'property', node:Cproperty__target, init:Tproperty__target}> {
		protected initializer(state: 'node'|'property') {
			switch (state) {
				case 'node': return (init:Tnode__target, parent:Cupdate_properties) => new Cnode__target(init, parent);
				case 'property': return (init:Tproperty__target, parent:Cupdate_properties) => new Cproperty__target(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'node'|'property') {
			switch (state) {
				case 'node': return finalize_node__target;
				case 'property': return finalize_property__target;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tupdate_properties['target'], parent:Cupdate_properties) {
			super(data, parent, 'target');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'target'`; }
	}
}
export namespace Cnode__target {
	export class Dbind_view extends Reference<gui_model.Cviews,string> {
		public readonly inferences:{
			constraint: () => application.Cnode__application
		}

		constructor(data:string, $this:Cnode__target) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.gui_action())
				.then(context => {
					const entry = context?.properties.views.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				constraint: cache((detach:boolean) => {
					const gui_model__gui_action_expression__operation__update_properties__target__node__bind_view_nval = $this.properties.bind_view.ref;
					return resolve($this.properties.bind_view.ref)
						.then(context => {
							const left = resolve(context)
								.then(context => gui_model__gui_action_expression__operation__update_properties__target__node__bind_view_nval)
								.then(context => context?.properties.view_context)
								.then(context => context?.component_root.output.result_application_node()).result;
							const right = resolve(context)
								.then(() => $this)
								.then(context => context?.parent)
								.then(context => context?.inferences.update_properties())
								.then(context => context?.properties.path)
								.then(context => context?.component_root.output.node_type()).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'bind view'`; }
	}
	export class Dexpression extends Cobject_expression {
		constructor(data:Tnode__target['expression'], parent:Cnode__target) {
			super(data, parent, {
				gui_action: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.gui_action()).result!),
				object_expression: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.node())
					.then(context => context?.properties.expression).result!)
			}, 'expression')
		}
	}
}
export namespace Cproperty__target {
	export class Daction_view extends StateGroup<
		{ name: 'available', node:Cavailable, init:Tavailable}|
		{ name: 'unavailable', node:Cunavailable, init:Tunavailable}> {
		protected initializer(state: 'available'|'unavailable') {
			switch (state) {
				case 'available': return (init:Tavailable, parent:Cproperty__target) => new Cavailable(init, parent);
				case 'unavailable': return (init:Tunavailable, parent:Cproperty__target) => new Cunavailable(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'available'|'unavailable') {
			switch (state) {
				case 'available': return finalize_available;
				case 'unavailable': return finalize_unavailable;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tproperty__target['action view'], parent:Cproperty__target) {
			super(data, parent, 'action view');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'action view'`; }
	}
	export class Dexpression extends Cgui_action_expression {
		constructor(data:Tproperty__target['expression'], parent:Cproperty__target) {
			super(data, parent, {
				action: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.property())
					.then(context => context?.properties.expression).result!),
				gui_action: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.gui_action()).result!)
			}, 'expression')
		}
	}
}
export namespace Cavailable {
	export class Dbind_view extends Reference<gui_model.Cviews,string> {
		public readonly inferences:{
			constraint: () => application.Cnode__application
		}

		constructor(data:string, $this:Cavailable) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.gui_action())
				.then(context => {
					const entry = context?.properties.views.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				constraint: cache((detach:boolean) => {
					const gui_model__gui_action_expression__operation__update_properties__target__property__action_view__available__bind_view_nval = $this.properties.bind_view.ref;
					return resolve($this.properties.bind_view.ref)
						.then(context => {
							const left = resolve(context)
								.then(context => gui_model__gui_action_expression__operation__update_properties__target__property__action_view__available__bind_view_nval)
								.then(context => context?.properties.view_context)
								.then(context => context?.component_root.output.result_application_node()).result;
							const right = resolve(context)
								.then(() => $this)
								.then(context => context?.parent)
								.then(context => context?.parent)
								.then(context => context?.inferences.update_properties())
								.then(context => context?.properties.path)
								.then(context => context?.component_root.output.node_type()).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'bind view'`; }
	}
}
export namespace Cwalk {
	export class Dexpression extends Cgui_action_expression {
		constructor(data:Twalk['expression'], parent:Cwalk) {
			super(data, parent, {
				action: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.walk())
					.then(context => context?.properties.expression).result!),
				gui_action: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.gui_action()).result!)
			}, 'expression')
		}
	}
}
export namespace Cgui_node {
	export class Dattributes extends AlanDictionary<{ node:Cattributes, init:Tattributes},Cgui_node> {
		protected initialize(parent:Cgui_node, key:string, entry_init:Tattributes) { return new Cattributes(key, entry_init, parent); }
		protected finalize = finalize_attributes
		protected eval_required_keys(detach:boolean = false):void {
			let this_obj = this.parent;
			function do_include(gui_model__gui_node__attributes_key_nval:application.Cattributes):boolean {
				const gui_model__gui_node__attributes_cond_nval = gui_model__gui_node__attributes_key_nval;
				const condition_context = resolve(gui_model__gui_node__attributes_cond_nval)
				.then(() => this.parent)
				.then(context => context?.component_root.input.application_node())
				.then(context => context?.component_root.output.location())
				.then(context => context?.component_root.output.root())
				.then(context => context?.variant.name === 'operation' ? context.variant.definition as application.Coperation : undefined).result;
				if (condition_context === undefined) {
					return false;
				}
				return true;
			};
			resolve(this.parent)
			.then(() => this.parent)
			.then(context => context?.component_root.input.application_node())
			.then(context => {
				for (let [key,val] of context?.properties.attributes) {
					let tail_obj = resolve(val).result;
					if (tail_obj !== undefined && do_include(tail_obj)) {
						assert(this.get(key) !== undefined);
					}
				}
				return undefined;
			});
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'attributes'`; }
		constructor(data:Tgui_node['attributes'], parent:Cgui_node) {
			super(data, parent);
		}
	}
}
export namespace Cattributes {
	export class Dtype extends StateGroup<
		{ name: 'action', node:Caction__type, init:Taction__type}|
		{ name: 'command', node:Ccommand__type, init:Tcommand__type}|
		{ name: 'property', node:Cproperty__type__attributes, init:Tproperty__type__attributes}|
		{ name: 'reference set', node:Creference_set, init:Treference_set}> {
		protected initializer(state: 'action'|'command'|'property'|'reference set') {
			switch (state) {
				case 'action': return (init:Taction__type, parent:Cattributes) => new Caction__type(init, parent);
				case 'command': return (init:Tcommand__type, parent:Cattributes) => new Ccommand__type(init, parent);
				case 'property': return (init:Tproperty__type__attributes, parent:Cattributes) => new Cproperty__type__attributes(init, parent);
				case 'reference set': return (init:Treference_set, parent:Cattributes) => new Creference_set(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'action'|'command'|'property'|'reference set') {
			switch (state) {
				case 'action': return finalize_action__type;
				case 'command': return finalize_command__type;
				case 'property': return finalize_property__type__attributes;
				case 'reference set': return finalize_reference_set;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tattributes['type'], parent:Cattributes) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Caction__type {
	export class Daction extends Cgui_action {
		constructor(data:Taction__type['action'], parent:Caction__type) {
			super(data, parent, {
				action: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.action_type())
					.then(context => context?.properties.action).result!),
				gui_action: cache((detach:boolean) => resolve(this)
					.then(() => parent).result!)
			}, 'action')
		}
	}
	export class Dparameters extends Cgui_node {
		constructor(data:Taction__type['parameters'], parent:Caction__type) {
			super(data, parent, {
				application_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.action_type())
					.then(context => context?.properties.parameters).result!),
				gui_entity: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.gui_entity()).result!),
				location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.operation)
					.then(context => context?.definitions.gui_node_location).result!),
				member: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.definitions.member).result!)
			}, 'parameters')
		}
	}
	export class Dviews extends AlanDictionary<{ node:Cviews, init:Tviews},Caction__type> {
		protected initialize(parent:Caction__type, key:string, entry_init:Tviews) { return new Cviews(key, entry_init, parent); }
		protected finalize = finalize_views
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'views'`; }
		constructor(data:Taction__type['views'], parent:Caction__type) {
			super(data, parent);
		}
	}
}
export namespace Cviews {
	export class Dview_context extends Cgui_node_type_path {
		constructor(data:Tviews['view context'], parent:Cviews) {
			super(data, parent, 'view context')
		}
	}
}
export namespace Ccommand__type {
	export class Dtype extends StateGroup<
		{ name: 'component', node:Ccomponent, init:Tcomponent}|
		{ name: 'global', node:Cglobal, init:Tglobal}> {
		protected initializer(state: 'component'|'global') {
			switch (state) {
				case 'component': return (init:Tcomponent, parent:Ccommand__type) => new Ccomponent(init, parent);
				case 'global': return (init:Tglobal, parent:Ccommand__type) => new Cglobal(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'component'|'global') {
			switch (state) {
				case 'component': return finalize_component;
				case 'global': return finalize_global;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcommand__type['type'], parent:Ccommand__type) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cglobal {
	export class Dparameters extends Cgui_node {
		constructor(data:Tglobal['parameters'], parent:Cglobal) {
			super(data, parent, {
				application_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.inferences.command_type())
					.then(context => context?.properties.parameters).result!),
				gui_entity: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.gui_entity()).result!),
				location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.definitions.operation)
					.then(context => context?.definitions.gui_node_location).result!),
				member: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.definitions.member).result!)
			}, 'parameters')
		}
	}
}
export namespace Cproperty__type__attributes {
	export class Dtype extends StateGroup<
		{ name: 'collection', node:Ccollection__type__property, init:Tcollection__type__property}|
		{ name: 'file', node:Cfile__type__property, init:Tfile__type__property}|
		{ name: 'group', node:Cgroup__type__property, init:Tgroup__type__property}|
		{ name: 'number', node:Cnumber__type__property__type__attributes, init:Tnumber__type__property__type__attributes}|
		{ name: 'state group', node:Cstate_group__type__property__type__attributes, init:Tstate_group__type__property__type__attributes}|
		{ name: 'text', node:Ctext__type__property__type__attributes, init:Ttext__type__property__type__attributes}> {
		protected initializer(state: 'collection'|'file'|'group'|'number'|'state group'|'text') {
			switch (state) {
				case 'collection': return (init:Tcollection__type__property, parent:Cproperty__type__attributes) => new Ccollection__type__property(init, parent);
				case 'file': return (init:Tfile__type__property, parent:Cproperty__type__attributes) => new Cfile__type__property(init, parent);
				case 'group': return (init:Tgroup__type__property, parent:Cproperty__type__attributes) => new Cgroup__type__property(init, parent);
				case 'number': return (init:Tnumber__type__property__type__attributes, parent:Cproperty__type__attributes) => new Cnumber__type__property__type__attributes(init, parent);
				case 'state group': return (init:Tstate_group__type__property__type__attributes, parent:Cproperty__type__attributes) => new Cstate_group__type__property__type__attributes(init, parent);
				case 'text': return (init:Ttext__type__property__type__attributes, parent:Cproperty__type__attributes) => new Ctext__type__property__type__attributes(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'collection'|'file'|'group'|'number'|'state group'|'text') {
			switch (state) {
				case 'collection': return finalize_collection__type__property;
				case 'file': return finalize_file__type__property;
				case 'group': return finalize_group__type__property;
				case 'number': return finalize_number__type__property__type__attributes;
				case 'state group': return finalize_state_group__type__property__type__attributes;
				case 'text': return finalize_text__type__property__type__attributes;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tproperty__type__attributes['type'], parent:Cproperty__type__attributes) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Ccollection__type__property {
	export class Ddefault_ extends StateGroup<
		{ name: 'no', node:Cno__default__collection, init:Tno__default__collection}|
		{ name: 'yes', node:Cyes__default__collection, init:Tyes__default__collection}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__default__collection, parent:Ccollection__type__property) => new Cno__default__collection(init, parent);
				case 'yes': return (init:Tyes__default__collection, parent:Ccollection__type__property) => new Cyes__default__collection(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__default__collection;
				case 'yes': return finalize_yes__default__collection;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcollection__type__property['default'], parent:Ccollection__type__property) {
			super(data, parent, 'default');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'default'`; }
	}
	export class Dduplication_mapping extends StateGroup<
		{ name: 'from current context', node:Cfrom_current_context, init:Tfrom_current_context}|
		{ name: 'none', node:Cnone__duplication_mapping, init:Tnone__duplication_mapping}> {
		protected initializer(state: 'from current context'|'none') {
			switch (state) {
				case 'from current context': return (init:Tfrom_current_context, parent:Ccollection__type__property) => new Cfrom_current_context(init, parent);
				case 'none': return (init:Tnone__duplication_mapping, parent:Ccollection__type__property) => new Cnone__duplication_mapping(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'from current context'|'none') {
			switch (state) {
				case 'from current context': return finalize_from_current_context;
				case 'none': return finalize_none__duplication_mapping;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcollection__type__property['duplication mapping'], parent:Ccollection__type__property) {
			super(data, parent, 'duplication mapping');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'duplication mapping'`; }
	}
	export class Dhas_ordered_graphs extends StateGroup<
		{ name: 'no', node:Cno__has_ordered_graphs, init:Tno__has_ordered_graphs}|
		{ name: 'yes', node:Cyes__has_ordered_graphs, init:Tyes__has_ordered_graphs}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_ordered_graphs, parent:Ccollection__type__property) => new Cno__has_ordered_graphs(init, parent);
				case 'yes': return (init:Tyes__has_ordered_graphs, parent:Ccollection__type__property) => new Cyes__has_ordered_graphs(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_ordered_graphs;
				case 'yes': return finalize_yes__has_ordered_graphs;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tcollection__type__property['has ordered graphs'], parent:Ccollection__type__property) {
			super(data, parent, 'has ordered graphs');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has ordered graphs'`; }
	}
	export class Dnode extends Cgui_node {
		public readonly inferences:{
			key_property: () => gui_model.Cattributes,
			text_key: () => gui_model.Ctext__type__property__type__attributes
		}
		constructor(data:Tcollection__type__property['node'], parent:Ccollection__type__property) {
			super(data, parent, {
				application_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.collection())
					.then(context => context?.properties.node).result!),
				gui_entity: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.node).result!),
				location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.gui_node_location).result!),
				member: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.definitions.member).result!)
			}, 'node')
			this.inferences = {
				key_property: cache((detach:boolean) => {
					const gui_model__gui_node__attributes__type__property__type__collection__node_nval = this;
					return resolve(this)
						.then(context => {
							const col_object = resolve(context)
								.then(context => gui_model__gui_node__attributes__type__property__type__collection__node_nval).result;
							const key_object = resolve(context)
								.then(() => parent)
								.then(context => context?.inferences.collection())
								.then(context => context?.properties.key_property?.ref)
								.then(context => context?.parent)
								.then(context => context?.parent).result;
							return resolve(col_object.properties.attributes.get(key_object?.entity.key)).result;
						}).result!;
				})
				,
				text_key: cache((detach:boolean) => {
					const gui_model__gui_node__attributes__type__property__type__collection__node_nval = this;
					return resolve(this)
						.then(() => parent.properties.node.inferences.key_property())
						.then(context => {
							if (context?.properties.type.state.name === 'property') {
								return resolve(context.properties.type.state.node as gui_model.Cproperty__type__attributes)
								.then(context => {
									if (context?.properties.type.state.name === 'text') {
										return context.properties.type.state.node as gui_model.Ctext__type__property__type__attributes;
									} else {
										return undefined;
									}
								}).result;
							} else {
								return undefined;
							}
						}).result!;
				})

			}
		}
	}
}
export namespace Cfrom_current_context {
	export class Dmapping extends Cduplication_node_mapping {
		constructor(data:Tfrom_current_context['mapping'], parent:Cfrom_current_context) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.node).result!)
			}, 'mapping')
		}
	}
}
export namespace Cyes__has_ordered_graphs {
	export class Dordered_graphs extends AlanDictionary<{ node:Cordered_graphs, init:Tordered_graphs},Cyes__has_ordered_graphs> {
		protected initialize(parent:Cyes__has_ordered_graphs, key:string) { return new Cordered_graphs(key, {}, parent); }
		protected finalize = finalize_ordered_graphs
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'ordered graphs'`; }
		constructor(data:Tyes__has_ordered_graphs['ordered graphs'], parent:Cyes__has_ordered_graphs) {
			super(data, parent);
		}
	}
}
export namespace Cfile__type__property {
	export class Dfile_name_source extends StateGroup<
		{ name: 'expression', node:Cexpression__file_name_source, init:Texpression__file_name_source}|
		{ name: 'generated', node:Cgenerated, init:Tgenerated}> {
		protected initializer(state: 'expression'|'generated') {
			switch (state) {
				case 'expression': return (init:Texpression__file_name_source, parent:Cfile__type__property) => new Cexpression__file_name_source(init, parent);
				case 'generated': return (init:Tgenerated, parent:Cfile__type__property) => new Cgenerated(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'expression'|'generated') {
			switch (state) {
				case 'expression': return finalize_expression__file_name_source;
				case 'generated': return finalize_generated;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tfile__type__property['file name source'], parent:Cfile__type__property) {
			super(data, parent, 'file name source');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'file name source'`; }
	}
	export class Dhas_extension_validation extends StateGroup<
		{ name: 'no', node:Cno__has_extension_validation, init:Tno__has_extension_validation}|
		{ name: 'yes', node:Cyes__has_extension_validation, init:Tyes__has_extension_validation}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_extension_validation, parent:Cfile__type__property) => new Cno__has_extension_validation(init, parent);
				case 'yes': return (init:Tyes__has_extension_validation, parent:Cfile__type__property) => new Cyes__has_extension_validation(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_extension_validation;
				case 'yes': return finalize_yes__has_extension_validation;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tfile__type__property['has extension validation'], parent:Cfile__type__property) {
			super(data, parent, 'has extension validation');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has extension validation'`; }
	}
}
export namespace Cgenerated {
	export class Dfile_name_expression extends Cfile_name_expression {
		constructor(data:Tgenerated['file name expression'], parent:Cgenerated) {
			super(data, parent, {
				gui_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent).result!)
			}, 'file name expression')
		}
	}
}
export namespace Cyes__has_extension_validation {
	export class Drules extends Ctext_validation {
		constructor(data:Tyes__has_extension_validation['rules'], parent:Cyes__has_extension_validation) {
			super(data, parent, 'rules')
		}
	}
}
export namespace Cgroup__type__property {
	export class Dgui_node extends Cgui_node {
		constructor(data:Tgroup__type__property['gui node'], parent:Cgroup__type__property) {
			super(data, parent, {
				application_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.group())
					.then(context => context?.properties.node).result!),
				gui_entity: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.gui_entity()).result!),
				location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.gui_node_location).result!),
				member: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.definitions.member).result!)
			}, 'gui node')
		}
	}
}
export namespace Cnumber__type__property__type__attributes {
	export class Ddefault_ extends StateGroup<
		{ name: 'no', node:Cno__default__number, init:Tno__default__number}|
		{ name: 'yes', node:Cyes__default__number, init:Tyes__default__number}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__default__number, parent:Cnumber__type__property__type__attributes) => new Cno__default__number(init, parent);
				case 'yes': return (init:Tyes__default__number, parent:Cnumber__type__property__type__attributes) => new Cyes__default__number(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__default__number;
				case 'yes': return finalize_yes__default__number;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnumber__type__property__type__attributes['default'], parent:Cnumber__type__property__type__attributes) {
			super(data, parent, 'default');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'default'`; }
	}
	export class Dnumerical_type extends Reference<gui_model.Cnumerical_types,string> {
		public readonly inferences:{
			constraint: () => application.Cnumerical_types
		}

		constructor(data:string, $this:Cnumber__type__property__type__attributes) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.root)
				.then(context => {
					const entry = context?.properties.numerical_types.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				constraint: cache((detach:boolean) => {
					const gui_model__gui_node__attributes__type__property__type__number__numerical_type_nval = $this.properties.numerical_type.ref;
					return resolve($this.properties.numerical_type.ref)
						.then(context => {
							const left = resolve(context)
								.then(context => gui_model__gui_node__attributes__type__property__type__number__numerical_type_nval)
								.then(context => context?.key?.ref).result;
							const right = resolve(context)
								.then(() => $this)
								.then(context => context?.inferences.number())
								.then(context => context?.properties.numerical_type?.ref).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'numerical type'`; }
	}
	export class Dsummarize extends StateGroup<
		{ name: 'no', node:Cno__summarize, init:Tno__summarize}|
		{ name: 'total', node:Ctotal, init:Ttotal}> {
		protected initializer(state: 'no'|'total') {
			switch (state) {
				case 'no': return (init:Tno__summarize, parent:Cnumber__type__property__type__attributes) => new Cno__summarize(init, parent);
				case 'total': return (init:Ttotal, parent:Cnumber__type__property__type__attributes) => new Ctotal(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'total') {
			switch (state) {
				case 'no': return finalize_no__summarize;
				case 'total': return finalize_total;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnumber__type__property__type__attributes['summarize'], parent:Cnumber__type__property__type__attributes) {
			super(data, parent, 'summarize');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'summarize'`; }
	}
}
export namespace Cyes__default__number {
	export class Dvalue extends StateGroup<
		{ name: 'expression', node:Cexpression__value, init:Texpression__value}|
		{ name: 'now', node:Cnow__value, init:Tnow__value}|
		{ name: 'one', node:Cone, init:Tone}|
		{ name: 'today', node:Ctoday__value, init:Ttoday__value}|
		{ name: 'zero', node:Czero, init:Tzero}> {
		protected initializer(state: 'expression'|'now'|'one'|'today'|'zero') {
			switch (state) {
				case 'expression': return (init:Texpression__value, parent:Cyes__default__number) => new Cexpression__value(init, parent);
				case 'now': return (init:Tnow__value, parent:Cyes__default__number) => new Cnow__value(init, parent);
				case 'one': return (init:Tone, parent:Cyes__default__number) => new Cone(init, parent);
				case 'today': return (init:Ttoday__value, parent:Cyes__default__number) => new Ctoday__value(init, parent);
				case 'zero': return (init:Tzero, parent:Cyes__default__number) => new Czero(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'expression'|'now'|'one'|'today'|'zero') {
			switch (state) {
				case 'expression': return finalize_expression__value;
				case 'now': return finalize_now__value;
				case 'one': return finalize_one;
				case 'today': return finalize_today__value;
				case 'zero': return finalize_zero;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__default__number['value'], parent:Cyes__default__number) {
			super(data, parent, 'value');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'value'`; }
	}
}
export namespace Cvalidation {
	export class Dhas_maximum extends StateGroup<
		{ name: 'expression', node:Cexpression__has_maximum, init:Texpression__has_maximum}|
		{ name: 'no', node:Cno__has_maximum, init:Tno__has_maximum}|
		{ name: 'yes', node:Cyes__has_maximum, init:Tyes__has_maximum}> {
		protected initializer(state: 'expression'|'no'|'yes') {
			switch (state) {
				case 'expression': return (init:Texpression__has_maximum, parent:Cvalidation) => new Cexpression__has_maximum(init, parent);
				case 'no': return (init:Tno__has_maximum, parent:Cvalidation) => new Cno__has_maximum(init, parent);
				case 'yes': return (init:Tyes__has_maximum, parent:Cvalidation) => new Cyes__has_maximum(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'expression'|'no'|'yes') {
			switch (state) {
				case 'expression': return finalize_expression__has_maximum;
				case 'no': return finalize_no__has_maximum;
				case 'yes': return finalize_yes__has_maximum;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tvalidation['has maximum'], parent:Cvalidation) {
			super(data, parent, 'has maximum');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has maximum'`; }
	}
	export class Dhas_minimum extends StateGroup<
		{ name: 'expression', node:Cexpression__has_minimum, init:Texpression__has_minimum}|
		{ name: 'no', node:Cno__has_minimum, init:Tno__has_minimum}|
		{ name: 'yes', node:Cyes__has_minimum, init:Tyes__has_minimum}> {
		protected initializer(state: 'expression'|'no'|'yes') {
			switch (state) {
				case 'expression': return (init:Texpression__has_minimum, parent:Cvalidation) => new Cexpression__has_minimum(init, parent);
				case 'no': return (init:Tno__has_minimum, parent:Cvalidation) => new Cno__has_minimum(init, parent);
				case 'yes': return (init:Tyes__has_minimum, parent:Cvalidation) => new Cyes__has_minimum(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'expression'|'no'|'yes') {
			switch (state) {
				case 'expression': return finalize_expression__has_minimum;
				case 'no': return finalize_no__has_minimum;
				case 'yes': return finalize_yes__has_minimum;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tvalidation['has minimum'], parent:Cvalidation) {
			super(data, parent, 'has minimum');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has minimum'`; }
	}
}
export namespace Cyes__has_maximum {
	export class Dmaximum extends Cnumber_limit {
		constructor(data:Tyes__has_maximum['maximum'], parent:Cyes__has_maximum) {
			super(data, parent, {
				numerical_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.properties.numerical_type?.ref).result!)
			}, 'maximum')
		}
	}
}
export namespace Cyes__has_minimum {
	export class Dminimum extends Cnumber_limit {
		constructor(data:Tyes__has_minimum['minimum'], parent:Cyes__has_minimum) {
			super(data, parent, {
				numerical_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.properties.numerical_type?.ref).result!)
			}, 'minimum')
		}
	}
}
export namespace Cstate_group__type__property__type__attributes {
	export class Ddefault_ extends StateGroup<
		{ name: 'no', node:Cno__default__state_group, init:Tno__default__state_group}|
		{ name: 'yes', node:Cyes__default__state_group, init:Tyes__default__state_group}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__default__state_group, parent:Cstate_group__type__property__type__attributes) => new Cno__default__state_group(init, parent);
				case 'yes': return (init:Tyes__default__state_group, parent:Cstate_group__type__property__type__attributes) => new Cyes__default__state_group(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__default__state_group;
				case 'yes': return finalize_yes__default__state_group;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tstate_group__type__property__type__attributes['default'], parent:Cstate_group__type__property__type__attributes) {
			super(data, parent, 'default');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'default'`; }
	}
	export class Dhas_states extends StateGroup<
		{ name: 'no', node:Cno__has_states, init:Tno__has_states}|
		{ name: 'yes', node:Cyes__has_states, init:Tyes__has_states}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_states, parent:Cstate_group__type__property__type__attributes) => new Cno__has_states(init, parent);
				case 'yes': return (init:Tyes__has_states, parent:Cstate_group__type__property__type__attributes) => new Cyes__has_states(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_states;
				case 'yes': return finalize_yes__has_states;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tstate_group__type__property__type__attributes['has states'], parent:Cstate_group__type__property__type__attributes) {
			super(data, parent, 'has states');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has states'`; }
	}
	export class Dstates extends AlanTopology<{ node:Cstates__state_group__type__property__type__attributes, init:Tstates__state_group__type__property__type__attributes},Cstate_group__type__property__type__attributes,'view order'> {
		protected graph_iterator(graph:('view order')):(node:Cstates__state_group__type__property__type__attributes) => Cstates__state_group__type__property__type__attributes {
			switch (graph) {
				case 'view order': return (entry:Cstates__state_group__type__property__type__attributes) => resolve(entry)
					.then(context => {
						if (context?.properties.has_successor.state.name === 'yes') {
							return context.properties.has_successor.state.node as gui_model.Cyes__has_successor;
						} else {
							return undefined;
						}
					})
					.then(context => context?.properties.successor.ref).result!
				default: throw new Error(`${graph} is not a valid graph iterator!`);
			}
		}
		protected initialize(parent:Cstate_group__type__property__type__attributes, key:string, entry_init:Tstates__state_group__type__property__type__attributes) { return new Cstates__state_group__type__property__type__attributes(key, entry_init, parent); }
		protected finalize = finalize_states__state_group__type__property__type__attributes
		protected eval_required_keys(detach:boolean = false):void {
			let this_obj = this.parent;
			function do_include(gui_model__gui_node__attributes__type__property__type__state_group__states_key_nval:application.Cstates__state_group):boolean {
				return true;
			};
			resolve(this.parent)
			.then(() => this.parent)
			.then(context => context?.inferences.state_group())
			.then(context => {
				for (let [key,val] of context?.properties.states) {
					let tail_obj = resolve(val).result;
					if (tail_obj !== undefined && do_include(tail_obj)) {
						assert(this.get(key) !== undefined);
					}
				}
				return undefined;
			});
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'states'`; }
		constructor(data:Tstate_group__type__property__type__attributes['states'], parent:Cstate_group__type__property__type__attributes) {
			super(data, parent);
		}
	}
}
export namespace Cyes__default__state_group {
	export class Dsource extends StateGroup<
		{ name: 'state', node:Cstate__source, init:Tstate__source}|
		{ name: 'state switch', node:Cstate_switch__source, init:Tstate_switch__source}> {
		protected initializer(state: 'state'|'state switch') {
			switch (state) {
				case 'state': return (init:Tstate__source, parent:Cyes__default__state_group) => new Cstate__source(init, parent);
				case 'state switch': return (init:Tstate_switch__source, parent:Cyes__default__state_group) => new Cstate_switch__source(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'state'|'state switch') {
			switch (state) {
				case 'state': return finalize_state__source;
				case 'state switch': return finalize_state_switch__source;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__default__state_group['source'], parent:Cyes__default__state_group) {
			super(data, parent, 'source');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'source'`; }
	}
}
export namespace Cstate__source {
	export class Dstate extends Reference<gui_model.Cstates__state_group__type__property__type__attributes,string> {

		constructor(data:string, $this:Cstate__source) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => {
					const entry = context?.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state'`; }
	}
}
export namespace Cyes__has_states {
	export class Dfirst extends Reference<gui_model.Cstates__state_group__type__property__type__attributes,string> {

		constructor(data:string, $this:Cyes__has_states) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => {
					const entry = context?.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'first'`; }
	}
}
export namespace Cstates__state_group__type__property__type__attributes {
	export class Dgui_node extends Cgui_node {
		constructor(data:Tstates__state_group__type__property__type__attributes['gui node'], parent:Cstates__state_group__type__property__type__attributes) {
			super(data, parent, {
				application_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.key?.ref)
					.then(context => context?.properties.node).result!),
				gui_entity: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.gui_entity()).result!),
				location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.gui_node_location).result!),
				member: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.parent)
					.then(context => context?.definitions.member).result!)
			}, 'gui node')
		}
	}
	export class Dhas_successor extends StateGroup<
		{ name: 'no', node:Cno__has_successor, init:Tno__has_successor}|
		{ name: 'yes', node:Cyes__has_successor, init:Tyes__has_successor}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_successor, parent:Cstates__state_group__type__property__type__attributes) => new Cno__has_successor(init, parent);
				case 'yes': return (init:Tyes__has_successor, parent:Cstates__state_group__type__property__type__attributes) => new Cyes__has_successor(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_successor;
				case 'yes': return finalize_yes__has_successor;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tstates__state_group__type__property__type__attributes['has successor'], parent:Cstates__state_group__type__property__type__attributes) {
			super(data, parent, 'has successor');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has successor'`; }
	}
	export class Drules extends Cwhere_rules {
		constructor(data:Tstates__state_group__type__property__type__attributes['rules'], parent:Cstates__state_group__type__property__type__attributes) {
			super(data, parent, {
				definition: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.key?.ref)
					.then(context => context?.properties.rules).result!)
			}, 'rules')
		}
	}
}
export namespace Cyes__has_successor {
	export class Dsuccessor extends Reference<gui_model.Cstates__state_group__type__property__type__attributes,string> {

		constructor(data:string, $this:Cyes__has_successor) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => {
					const entry = context?.parent.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'successor'`; }
	}
}
export namespace Ctext__type__property__type__attributes {
	export class Ddefault_ extends StateGroup<
		{ name: 'no', node:Cno__default__text, init:Tno__default__text}|
		{ name: 'yes', node:Cyes__default__text, init:Tyes__default__text}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__default__text, parent:Ctext__type__property__type__attributes) => new Cno__default__text(init, parent);
				case 'yes': return (init:Tyes__default__text, parent:Ctext__type__property__type__attributes) => new Cyes__default__text(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__default__text;
				case 'yes': return finalize_yes__default__text;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Ttext__type__property__type__attributes['default'], parent:Ctext__type__property__type__attributes) {
			super(data, parent, 'default');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'default'`; }
	}
	export class Dnavigable extends StateGroup<
		{ name: 'no', node:Cno__navigable, init:Tno__navigable}|
		{ name: 'yes', node:Cyes__navigable, init:Tyes__navigable}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__navigable, parent:Ctext__type__property__type__attributes) => new Cno__navigable(init, parent);
				case 'yes': return (init:Tyes__navigable, parent:Ctext__type__property__type__attributes) => new Cyes__navigable(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__navigable;
				case 'yes': return finalize_yes__navigable;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Ttext__type__property__type__attributes['navigable'], parent:Ctext__type__property__type__attributes) {
			super(data, parent, 'navigable');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'navigable'`; }
	}
	export class Dvalidation extends StateGroup<
		{ name: 'custom', node:Ccustom, init:Tcustom}|
		{ name: 'model', node:Cmodel__validation, init:Tmodel__validation}|
		{ name: 'none', node:Cnone__validation, init:Tnone__validation}> {
		protected initializer(state: 'custom'|'model'|'none') {
			switch (state) {
				case 'custom': return (init:Tcustom, parent:Ctext__type__property__type__attributes) => new Ccustom(init, parent);
				case 'model': return (init:Tmodel__validation, parent:Ctext__type__property__type__attributes) => new Cmodel__validation(init, parent);
				case 'none': return (init:Tnone__validation, parent:Ctext__type__property__type__attributes) => new Cnone__validation(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'custom'|'model'|'none') {
			switch (state) {
				case 'custom': return finalize_custom;
				case 'model': return finalize_model__validation;
				case 'none': return finalize_none__validation;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Ttext__type__property__type__attributes['validation'], parent:Ctext__type__property__type__attributes) {
			super(data, parent, 'validation');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'validation'`; }
	}
}
export namespace Cyes__default__text {
	export class Dsource extends StateGroup<
		{ name: 'auto select only candidate', node:Cauto_select_only_candidate, init:Tauto_select_only_candidate}|
		{ name: 'current user', node:Ccurrent_user, init:Tcurrent_user}|
		{ name: 'expression', node:Cexpression__source, init:Texpression__source}|
		{ name: 'guid', node:Cguid, init:Tguid}|
		{ name: 'select source', node:Cselect_source, init:Tselect_source}> {
		protected initializer(state: 'auto select only candidate'|'current user'|'expression'|'guid'|'select source') {
			switch (state) {
				case 'auto select only candidate': return (init:Tauto_select_only_candidate, parent:Cyes__default__text) => new Cauto_select_only_candidate(init, parent);
				case 'current user': return (init:Tcurrent_user, parent:Cyes__default__text) => new Ccurrent_user(init, parent);
				case 'expression': return (init:Texpression__source, parent:Cyes__default__text) => new Cexpression__source(init, parent);
				case 'guid': return (init:Tguid, parent:Cyes__default__text) => new Cguid(init, parent);
				case 'select source': return (init:Tselect_source, parent:Cyes__default__text) => new Cselect_source(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'auto select only candidate'|'current user'|'expression'|'guid'|'select source') {
			switch (state) {
				case 'auto select only candidate': return finalize_auto_select_only_candidate;
				case 'current user': return finalize_current_user;
				case 'expression': return finalize_expression__source;
				case 'guid': return finalize_guid;
				case 'select source': return finalize_select_source;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__default__text['source'], parent:Cyes__default__text) {
			super(data, parent, 'source');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'source'`; }
	}
}
export namespace Cselect_source {
	export class Dgraph extends Reference<gui_model.Cordered_graphs,string> {
		public readonly inferences:{
			is_edge_reference: () => application.Cyes__has_reference
		}

		constructor(data:string, $this:Cselect_source) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.has_ordered_graphs())
				.then(context => {
					const entry = context?.properties.ordered_graphs.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
			this.inferences = {
				is_edge_reference: cache((detach:boolean) => {
					const gui_model__gui_node__attributes__type__property__type__text__default__yes__source__select_source__graph_nval = $this.properties.graph.ref;
					return resolve($this.properties.graph.ref)
						.then(context => {
							const left = resolve(context)
								.then(() => $this)
								.then(context => context?.inferences.reference())
								.then(context => context?.inferences.reference()).result;
							const right = resolve(context)
								.then(context => gui_model__gui_node__attributes__type__property__type__text__default__yes__source__select_source__graph_nval)
								.then(context => context?.inferences.ordered_graph())
								.then(context => context?.properties.ordering_property_path)
								.then(context => context?.properties.ordering_states)
								.then(context => context?.properties.edge_reference?.ref).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'graph'`; }
	}
}
export namespace Cno__navigable {
	export class Dpassword_property extends StateGroup<
		{ name: 'no', node:Cno__password_property, init:Tno__password_property}|
		{ name: 'yes', node:Cyes__password_property, init:Tyes__password_property}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__password_property, parent:Cno__navigable) => new Cno__password_property(init, parent);
				case 'yes': return (init:Tyes__password_property, parent:Cno__navigable) => new Cyes__password_property(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__password_property;
				case 'yes': return finalize_yes__password_property;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tno__navigable['password property'], parent:Cno__navigable) {
			super(data, parent, 'password property');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'password property'`; }
	}
}
export namespace Cyes__navigable {
	export class Dreferenced_node extends Cgui_node_type_path {
		public readonly inferences:{
			referenced_collection: () => gui_model.Ccollection__type__property,
			referenced_node_type: () => application.Cnode__application
		}
		constructor(data:Tyes__navigable['referenced node'], parent:Cyes__navigable) {
			super(data, parent, 'referenced node')
			this.inferences = {
				referenced_collection: cache((detach:boolean) => {
					const gui_model__gui_node__attributes__type__property__type__text__navigable__yes__referenced_node_nval = this;
					return resolve(this)
						.then(context => gui_model__gui_node__attributes__type__property__type__text__navigable__yes__referenced_node_nval)
						.then(context => context?.component_root.output.result_gui_node())
						.then(context => context?.component_root.output.gui_entity())
						.then(context => context?.component_root.output.location())
						.then(context => context?.variant.name === 'collection' ? context.variant.definition as gui_model.Ccollection__type__property : undefined).result!;
				})
				,
				referenced_node_type: cache((detach:boolean) => {
					const gui_model__gui_node__attributes__type__property__type__text__navigable__yes__referenced_node_nval = this;
					return resolve(this)
						.then(context => {
							const left = resolve(context)
								.then(context => gui_model__gui_node__attributes__type__property__type__text__navigable__yes__referenced_node_nval)
								.then(context => context?.component_root.output.result_application_node()).result;
							const right = resolve(context)
								.then(() => parent)
								.then(context => context?.inferences.reference())
								.then(context => context?.properties.expression)
								.then(context => context?.component_root.output.referenced_node_type()).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})

			}
		}
	}
	export class Drules extends Cwhere_rules {
		constructor(data:Tyes__navigable['rules'], parent:Cyes__navigable) {
			super(data, parent, {
				definition: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.inferences.reference())
					.then(context => context?.properties.rules).result!)
			}, 'rules')
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'mandatory', node:Cmandatory, init:Tmandatory}|
		{ name: 'optional', node:Coptional, init:Toptional}> {
		protected initializer(state: 'mandatory'|'optional') {
			switch (state) {
				case 'mandatory': return (init:Tmandatory, parent:Cyes__navigable) => new Cmandatory(init, parent);
				case 'optional': return (init:Toptional, parent:Cyes__navigable) => new Coptional(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'mandatory'|'optional') {
			switch (state) {
				case 'mandatory': return finalize_mandatory;
				case 'optional': return finalize_optional;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__navigable['type'], parent:Cyes__navigable) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
	export class Dvalidate extends StateGroup<
		{ name: 'no', node:Cno__validate__yes, init:Tno__validate__yes}|
		{ name: 'yes', node:Cyes__validate__yes, init:Tyes__validate__yes}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__validate__yes, parent:Cyes__navigable) => new Cno__validate__yes(init, parent);
				case 'yes': return (init:Tyes__validate__yes, parent:Cyes__navigable) => new Cyes__validate__yes(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__validate__yes;
				case 'yes': return finalize_yes__validate__yes;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__navigable['validate'], parent:Cyes__navigable) {
			super(data, parent, 'validate');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'validate'`; }
	}
}
export namespace Ccustom {
	export class Drules extends Ctext_validation {
		constructor(data:Tcustom['rules'], parent:Ccustom) {
			super(data, parent, 'rules')
		}
	}
}
export namespace Cgui_node_type_path {
	export class Dsteps extends Cgui_node_type_path_step {
		constructor(data:Tgui_node_type_path['steps'], parent:Cgui_node_type_path) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.root)
					.then(context => context?.properties.root).result!)
			}, 'steps')
		}
	}
}
export namespace Cgui_node_type_path_step {
	export class Dhas_steps extends StateGroup<
		{ name: 'no', node:Cno__has_steps__gui_node_type_path_step, init:Tno__has_steps__gui_node_type_path_step}|
		{ name: 'yes', node:Cyes__has_steps__gui_node_type_path_step, init:Tyes__has_steps__gui_node_type_path_step}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_steps__gui_node_type_path_step, parent:Cgui_node_type_path_step) => new Cno__has_steps__gui_node_type_path_step(init, parent);
				case 'yes': return (init:Tyes__has_steps__gui_node_type_path_step, parent:Cgui_node_type_path_step) => new Cyes__has_steps__gui_node_type_path_step(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_steps__gui_node_type_path_step;
				case 'yes': return finalize_yes__has_steps__gui_node_type_path_step;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tgui_node_type_path_step['has steps'], parent:Cgui_node_type_path_step) {
			super(data, parent, 'has steps');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has steps'`; }
	}
}
export namespace Cyes__has_steps__gui_node_type_path_step {
	export class Dattribute extends Reference<gui_model.Cattributes,string> {

		constructor(data:string, $this:Cyes__has_steps__gui_node_type_path_step) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.context_node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'attribute'`; }
	}
	export class Dtail extends Cgui_node_type_path_step {
		constructor(data:Tyes__has_steps__gui_node_type_path_step['tail'], parent:Cyes__has_steps__gui_node_type_path_step) {
			super(data, parent, {
				context_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.type?.state.node.output.result_node()).result!)
			}, 'tail')
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'node', node:Cnode__type, init:Tnode__type}|
		{ name: 'state', node:Cstate__type, init:Tstate__type}> {
		protected initializer(state: 'node'|'state') {
			switch (state) {
				case 'node': return (init:Tnode__type, parent:Cyes__has_steps__gui_node_type_path_step) => new Cnode__type(init, parent);
				case 'state': return (init:Tstate__type, parent:Cyes__has_steps__gui_node_type_path_step) => new Cstate__type(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'node'|'state') {
			switch (state) {
				case 'node': return finalize_node__type;
				case 'state': return finalize_state__type;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__has_steps__gui_node_type_path_step['type'], parent:Cyes__has_steps__gui_node_type_path_step) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cstate__type {
	export class Dstate extends Reference<gui_model.Cstates__state_group__type__property__type__attributes,string> {

		constructor(data:string, $this:Cstate__type) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.inferences.state_group())
				.then(context => {
					const entry = context?.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state'`; }
	}
}
export namespace Cgui_static_conditional_path {
	export class Dhas_steps extends StateGroup<
		{ name: 'no', node:Cno__has_steps__gui_static_conditional_path, init:Tno__has_steps__gui_static_conditional_path}|
		{ name: 'yes', node:Cyes__has_steps__gui_static_conditional_path, init:Tyes__has_steps__gui_static_conditional_path}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_steps__gui_static_conditional_path, parent:Cgui_static_conditional_path) => new Cno__has_steps__gui_static_conditional_path(init, parent);
				case 'yes': return (init:Tyes__has_steps__gui_static_conditional_path, parent:Cgui_static_conditional_path) => new Cyes__has_steps__gui_static_conditional_path(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_steps__gui_static_conditional_path;
				case 'yes': return finalize_yes__has_steps__gui_static_conditional_path;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tgui_static_conditional_path['has steps'], parent:Cgui_static_conditional_path) {
			super(data, parent, 'has steps');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has steps'`; }
	}
}
export namespace Cyes__has_steps__gui_static_conditional_path {
	export class Dstep extends StateGroup<
		{ name: 'group', node:Cgroup__step, init:Tgroup__step}|
		{ name: 'state', node:Cstate__step, init:Tstate__step}> {
		protected initializer(state: 'group'|'state') {
			switch (state) {
				case 'group': return (init:Tgroup__step, parent:Cyes__has_steps__gui_static_conditional_path) => new Cgroup__step(init, parent);
				case 'state': return (init:Tstate__step, parent:Cyes__has_steps__gui_static_conditional_path) => new Cstate__step(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'group'|'state') {
			switch (state) {
				case 'group': return finalize_group__step;
				case 'state': return finalize_state__step;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__has_steps__gui_static_conditional_path['step'], parent:Cyes__has_steps__gui_static_conditional_path) {
			super(data, parent, 'step');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'step'`; }
	}
	export class Dtail extends Cgui_static_conditional_path {
		constructor(data:Tyes__has_steps__gui_static_conditional_path['tail'], parent:Cyes__has_steps__gui_static_conditional_path) {
			super(data, parent, {
				node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.step?.state.node.output.node()).result!)
			}, 'tail')
		}
	}
}
export namespace Cgroup__step {
	export class Dgroup extends Reference<gui_model.Cgroup__type__property,string> {

		constructor(data:string, $this:Cgroup__step) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as gui_model.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'group') {
							return context.properties.type.state.node as gui_model.Cgroup__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'group'`; }
	}
}
export namespace Cstate__step {
	export class Dstate extends Reference<gui_model.Cstates__state_group__type__property__type__attributes,string> {

		constructor(data:string, $this:Cstate__step) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.stategroup?.ref)
				.then(context => {
					const entry = context?.properties.states.get(this.entry)!;
					return resolve(entry).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'state'`; }
	}
	export class Dstategroup extends Reference<gui_model.Cstate_group__type__property__type__attributes,string> {

		constructor(data:string, $this:Cstate__step) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.component_root.input.node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as gui_model.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'state group') {
							return context.properties.type.state.node as gui_model.Cstate_group__type__property__type__attributes;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'stategroup'`; }
	}
}
export namespace Cgui_static_singular_path {
	export class Dhas_steps extends StateGroup<
		{ name: 'no', node:Cno__has_steps__gui_static_singular_path, init:Tno__has_steps__gui_static_singular_path}|
		{ name: 'yes', node:Cyes__has_steps__gui_static_singular_path, init:Tyes__has_steps__gui_static_singular_path}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_steps__gui_static_singular_path, parent:Cgui_static_singular_path) => new Cno__has_steps__gui_static_singular_path(init, parent);
				case 'yes': return (init:Tyes__has_steps__gui_static_singular_path, parent:Cgui_static_singular_path) => new Cyes__has_steps__gui_static_singular_path(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_steps__gui_static_singular_path;
				case 'yes': return finalize_yes__has_steps__gui_static_singular_path;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tgui_static_singular_path['has steps'], parent:Cgui_static_singular_path) {
			super(data, parent, 'has steps');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has steps'`; }
	}
	export class Dhead extends Cancestor_node_path {
		constructor(data:Tgui_static_singular_path['head'], parent:Cgui_static_singular_path) {
			super(data, parent, {
				node_type: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.gui_node()).result!)
			}, 'head')
		}
	}
}
export namespace Cyes__has_steps__gui_static_singular_path {
	export class Dtail extends Cgui_static_singular_path {
		constructor(data:Tyes__has_steps__gui_static_singular_path['tail'], parent:Cyes__has_steps__gui_static_singular_path) {
			super(data, parent, {
				gui_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.type?.state.node.output.result_node()).result!)
			}, 'tail')
		}
	}
	export class Dtype extends StateGroup<
		{ name: 'group', node:Cgroup__type__yes, init:Tgroup__type__yes}> {
		protected initializer(state: 'group') {
			switch (state) {
				case 'group': return (init:Tgroup__type__yes, parent:Cyes__has_steps__gui_static_singular_path) => new Cgroup__type__yes(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'group') {
			switch (state) {
				case 'group': return finalize_group__type__yes;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tyes__has_steps__gui_static_singular_path['type'], parent:Cyes__has_steps__gui_static_singular_path) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cgroup__type__yes {
	export class Dgroup extends Reference<gui_model.Cgroup__type__property,string> {

		constructor(data:string, $this:Cgroup__type__yes) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.parent)
				.then(context => context?.parent)
				.then(context => context?.properties.head)
				.then(context => context?.component_root.output.node_type())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as gui_model.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'group') {
							return context.properties.type.state.node as gui_model.Cgroup__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'group'`; }
	}
}
export namespace Cnumber_limit {
	export class Dtype extends StateGroup<
		{ name: 'dynamic', node:Cdynamic, init:Tdynamic}|
		{ name: 'static', node:Cstatic__type__number_limit, init:Tstatic__type__number_limit}> {
		protected initializer(state: 'dynamic'|'static') {
			switch (state) {
				case 'dynamic': return (init:Tdynamic, parent:Cnumber_limit) => new Cdynamic(init, parent);
				case 'static': return (init:Tstatic__type__number_limit, parent:Cnumber_limit) => new Cstatic__type__number_limit(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'dynamic'|'static') {
			switch (state) {
				case 'dynamic': return finalize_dynamic;
				case 'static': return finalize_static__type__number_limit;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnumber_limit['type'], parent:Cnumber_limit) {
			super(data, parent, 'type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'type'`; }
	}
}
export namespace Cdynamic {
	export class Dbase extends StateGroup<
		{ name: 'now', node:Cnow__base, init:Tnow__base}|
		{ name: 'today', node:Ctoday__base, init:Ttoday__base}> {
		protected initializer(state: 'now'|'today') {
			switch (state) {
				case 'now': return (init:Tnow__base, parent:Cdynamic) => new Cnow__base(init, parent);
				case 'today': return (init:Ttoday__base, parent:Cdynamic) => new Ctoday__base(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'now'|'today') {
			switch (state) {
				case 'now': return finalize_now__base;
				case 'today': return finalize_today__base;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tdynamic['base'], parent:Cdynamic) {
			super(data, parent, 'base');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'base'`; }
	}
	export class Dwith_offset extends StateGroup<
		{ name: 'no', node:Cno__with_offset, init:Tno__with_offset}|
		{ name: 'yes', node:Cyes__with_offset, init:Tyes__with_offset}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__with_offset, parent:Cdynamic) => new Cno__with_offset(init, parent);
				case 'yes': return (init:Tyes__with_offset, parent:Cdynamic) => new Cyes__with_offset(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__with_offset;
				case 'yes': return finalize_yes__with_offset;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tdynamic['with offset'], parent:Cdynamic) {
			super(data, parent, 'with offset');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'with offset'`; }
	}
}
export namespace Cyes__with_offset {
	export class Doffset extends AlanInteger {
		constructor(data:Tyes__with_offset['offset'], parent:Cyes__with_offset) {
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'offset'`; }
	}
}
export namespace Cstatic__type__number_limit {
	export class Dlimit extends AlanInteger {
		constructor(data:Tstatic__type__number_limit['limit'], parent:Cstatic__type__number_limit) {
			super(data, parent);}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'limit'`; }
	}
}
export namespace Cobject_expression {
	export class Dproperties extends AlanDictionary<{ node:Cproperties__object_expression, init:Tproperties__object_expression},Cobject_expression> {
		protected initialize(parent:Cobject_expression, key:string, entry_init:Tproperties__object_expression) { return new Cproperties__object_expression(key, entry_init, parent); }
		protected finalize = finalize_properties__object_expression
		protected eval_required_keys(detach:boolean = false):void {
			let this_obj = this.parent;
			function do_include(gui_model__object_expression__properties_key_nval:application.Cproperties__ui_object_expression):boolean {
				return true;
			};
			resolve(this.parent)
			.then(() => this.parent)
			.then(context => context?.component_root.input.object_expression())
			.then(context => {
				for (let [key,val] of context?.properties.properties) {
					let tail_obj = resolve(val).result;
					if (tail_obj !== undefined && do_include(tail_obj)) {
						assert(this.get(key) !== undefined);
					}
				}
				return undefined;
			});
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'properties'`; }
		constructor(data:Tobject_expression['properties'], parent:Cobject_expression) {
			super(data, parent);
		}
	}
}
export namespace Cproperties__object_expression {
	export class Ddefault_ extends Cgui_action_expression {
		constructor(data:Tproperties__object_expression['default'], parent:Cproperties__object_expression) {
			super(data, parent, {
				action: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.key?.ref)
					.then(context => context?.properties.default_).result!),
				gui_action: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.component_root.input.gui_action()).result!)
			}, 'default')
		}
	}
}
export namespace Ctext_validation {
}
export namespace Cwhere_rules {
	export class Drules extends AlanDictionary<{ node:Crules, init:Trules},Cwhere_rules> {
		protected initialize(parent:Cwhere_rules, key:string, entry_init:Trules) { return new Crules(key, entry_init, parent); }
		protected finalize = finalize_rules
		protected eval_required_keys(detach:boolean = false):void {
			let this_obj = this.parent;
			function do_include(gui_model__where_rules__rules_key_nval:application.Crules):boolean {
				const inferences:{
					node: (detach?:boolean) => application.Cnode__application|undefined;} = {

					node: maybe_cache((detach:boolean) => resolve(gui_model__where_rules__rules_key_nval)
						.then(context => gui_model__where_rules__rules_key_nval)
						.then(context => context?.properties.type?.state.node.output.referenced_node_type())?.result)
				}
				const $this = {
					inferences: inferences
				}
				if (inferences.node() === undefined)
					return false;
				return true;
			};
			resolve(this.parent)
			.then(() => this.parent)
			.then(context => context?.component_root.input.definition())
			.then(context => {
				for (let [key,val] of context?.properties.rules) {
					let tail_obj = resolve(val).result;
					if (tail_obj !== undefined && do_include(tail_obj)) {
						assert(this.get(key) !== undefined);
					}
				}
				return undefined;
			});
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'rules'`; }
		constructor(data:Twhere_rules['rules'], parent:Cwhere_rules) {
			super(data, parent);
		}
	}
}
export namespace Crules {
	export class Dreferenced_node extends Cgui_node_type_path {
		public readonly inferences:{
			constraint: () => application.Cnode__application
		}
		constructor(data:Trules['referenced node'], parent:Crules) {
			super(data, parent, 'referenced node')
			this.inferences = {
				constraint: cache((detach:boolean) => {
					const gui_model__where_rules__rules__referenced_node_nval = this;
					return resolve(this)
						.then(context => {
							const left = resolve(context)
								.then(context => gui_model__where_rules__rules__referenced_node_nval)
								.then(context => context?.component_root.output.result_application_node()).result;
							const right = resolve(context)
								.then(() => parent)
								.then(context => context?.inferences.node()).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})

			}
		}
	}
	export class Dvalidate extends StateGroup<
		{ name: 'no', node:Cno__validate__rules, init:Tno__validate__rules}|
		{ name: 'yes', node:Cyes__validate__rules, init:Tyes__validate__rules}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__validate__rules, parent:Crules) => new Cno__validate__rules(init, parent);
				case 'yes': return (init:Tyes__validate__rules, parent:Crules) => new Cyes__validate__rules(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__validate__rules;
				case 'yes': return finalize_yes__validate__rules;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Trules['validate'], parent:Crules) {
			super(data, parent, 'validate');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'validate'`; }
	}
}
export namespace Cgui_model {
	export class Dhas_user_node extends StateGroup<
		{ name: 'no', node:Cno__has_user_node, init:Tno__has_user_node}|
		{ name: 'yes', node:Cyes__has_user_node, init:Tyes__has_user_node}> {
		protected initializer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return (init:Tno__has_user_node, parent:Cgui_model) => new Cno__has_user_node(init, parent);
				case 'yes': return (init:Tyes__has_user_node, parent:Cgui_model) => new Cyes__has_user_node(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'no'|'yes') {
			switch (state) {
				case 'no': return finalize_no__has_user_node;
				case 'yes': return finalize_yes__has_user_node;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tgui_model['has user node'], parent:Cgui_model) {
			super(data, parent, 'has user node');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'has user node'`; }
	}
	export class Dnumerical_types extends AlanDictionary<{ node:Cnumerical_types, init:Tnumerical_types},Cgui_model> {
		protected initialize(parent:Cgui_model, key:string, entry_init:Tnumerical_types) { return new Cnumerical_types(key, entry_init, parent); }
		protected finalize = finalize_numerical_types
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'numerical types'`; }
		constructor(data:Tgui_model['numerical types'], parent:Cgui_model) {
			super(data, parent);
		}
	}
	export class Droot extends Cgui_node {
		constructor(data:Tgui_model['root'], parent:Cgui_model) {
			super(data, parent, {
				application_node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.root.input.application)
					.then(context => context?.properties.root).result!),
				gui_entity: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.properties.root).result!),
				location: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.gui_node_location).result!),
				member: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.definitions.member).result!)
			}, 'root')
		}
	}
}
export namespace Cyes__has_user_node {
	export class Dcontext extends Cgui_static_conditional_path {
		constructor(data:Tyes__has_user_node['context'], parent:Cyes__has_user_node) {
			super(data, parent, {
				node: cache((detach:boolean) => resolve(this)
					.then(() => parent)
					.then(context => context?.parent)
					.then(context => context?.properties.root).result!)
			}, 'context')
		}
	}
	export class Dusers extends Reference<gui_model.Ccollection__type__property,string> {
		public readonly inferences:{
			users_collection: () => application.Cattributes
		}

		constructor(data:string, $this:Cyes__has_user_node) {
			super(data, cache((detach:boolean) => resolve($this)
				.then(() => $this)
				.then(context => context?.properties.context)
				.then(context => context?.component_root.output.node())
				.then(context => {
					const entry = context?.properties.attributes.get(this.entry)!;
					return resolve(entry)
					.then(context => {
						if (context?.properties.type.state.name === 'property') {
							return context.properties.type.state.node as gui_model.Cproperty__type__attributes;
						} else {
							return undefined;
						}
					})
					.then(context => {
						if (context?.properties.type.state.name === 'collection') {
							return context.properties.type.state.node as gui_model.Ccollection__type__property;
						} else {
							return undefined;
						}
					}).result;
				}).result!), $this)
			this.inferences = {
				users_collection: cache((detach:boolean) => {
					const gui_model__has_user_node__yes__users_nval = $this.properties.users.ref;
					return resolve($this.properties.users.ref)
						.then(context => {
							const left = resolve(context)
								.then(context => gui_model__has_user_node__yes__users_nval)
								.then(context => context?.parent)
								.then(context => context?.parent)
								.then(context => context?.key?.ref).result;
							const right = resolve(context)
								.then(() => $this)
								.then(context => context?.inferences.dynamic_users())
								.then(context => context?.properties.users_collection_path)
								.then(context => context?.component_root.output.property())
								.then(context => context?.parent).result;
							return left?.is(right!) ? left : undefined
						}).result!;
				})

			}
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'users'`; }
	}
}
export namespace Cnumerical_types {
	export class Drepresentation_type extends StateGroup<
		{ name: 'HTML date and time', node:CHTML_date_and_time, init:THTML_date_and_time}|
		{ name: 'date', node:Cdate, init:Tdate}|
		{ name: 'date and time', node:Cdate_and_time, init:Tdate_and_time}|
		{ name: 'duration', node:Cduration, init:Tduration}|
		{ name: 'model', node:Cmodel__representation_type, init:Tmodel__representation_type}> {
		protected initializer(state: 'HTML date and time'|'date'|'date and time'|'duration'|'model') {
			switch (state) {
				case 'HTML date and time': return (init:THTML_date_and_time, parent:Cnumerical_types) => new CHTML_date_and_time(init, parent);
				case 'date': return (init:Tdate, parent:Cnumerical_types) => new Cdate(init, parent);
				case 'date and time': return (init:Tdate_and_time, parent:Cnumerical_types) => new Cdate_and_time(init, parent);
				case 'duration': return (init:Tduration, parent:Cnumerical_types) => new Cduration(init, parent);
				case 'model': return (init:Tmodel__representation_type, parent:Cnumerical_types) => new Cmodel__representation_type(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'HTML date and time'|'date'|'date and time'|'duration'|'model') {
			switch (state) {
				case 'HTML date and time': return finalize_HTML_date_and_time;
				case 'date': return finalize_date;
				case 'date and time': return finalize_date_and_time;
				case 'duration': return finalize_duration;
				case 'model': return finalize_model__representation_type;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tnumerical_types['representation type'], parent:Cnumerical_types) {
			super(data, parent, 'representation type');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'representation type'`; }
	}
}
export namespace Cduration {
	export class Dunit extends StateGroup<
		{ name: 'hours', node:Chours, init:Thours}|
		{ name: 'minutes', node:Cminutes, init:Tminutes}|
		{ name: 'seconds', node:Cseconds, init:Tseconds}> {
		protected initializer(state: 'hours'|'minutes'|'seconds') {
			switch (state) {
				case 'hours': return (init:Thours, parent:Cduration) => new Chours(init, parent);
				case 'minutes': return (init:Tminutes, parent:Cduration) => new Cminutes(init, parent);
				case 'seconds': return (init:Tseconds, parent:Cduration) => new Cseconds(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'hours'|'minutes'|'seconds') {
			switch (state) {
				case 'hours': return finalize_hours;
				case 'minutes': return finalize_minutes;
				case 'seconds': return finalize_seconds;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tduration['unit'], parent:Cduration) {
			super(data, parent, 'unit');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'unit'`; }
	}
}
export namespace Cmodel__representation_type {
	export class Dstyle extends StateGroup<
		{ name: 'currency', node:Ccurrency, init:Tcurrency}|
		{ name: 'decimal', node:Cdecimal, init:Tdecimal}|
		{ name: 'engineering', node:Cengineering, init:Tengineering}|
		{ name: 'scientific', node:Cscientific, init:Tscientific}> {
		protected initializer(state: 'currency'|'decimal'|'engineering'|'scientific') {
			switch (state) {
				case 'currency': return (init:Tcurrency, parent:Cmodel__representation_type) => new Ccurrency(init, parent);
				case 'decimal': return (init:Tdecimal, parent:Cmodel__representation_type) => new Cdecimal(init, parent);
				case 'engineering': return (init:Tengineering, parent:Cmodel__representation_type) => new Cengineering(init, parent);
				case 'scientific': return (init:Tscientific, parent:Cmodel__representation_type) => new Cscientific(init, parent);
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		protected finalizer(state: 'currency'|'decimal'|'engineering'|'scientific') {
			switch (state) {
				case 'currency': return finalize_currency;
				case 'decimal': return finalize_decimal;
				case 'engineering': return finalize_engineering;
				case 'scientific': return finalize_scientific;
				default: throw new Error(`Unexpected state ${state}.`);
			}
		}
		constructor(data:Tmodel__representation_type['style'], parent:Cmodel__representation_type) {
			super(data, parent, 'style');
		}
		private _path: string | undefined;
		public get path() { return this._path ??= `${this.parent.path}.'style'`; }
	}
}
function finalize_no__has_steps__ancestor_node_path(obj:Cno__has_steps__ancestor_node_path, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.node_type)(detach) !== undefined || detach);
}
function finalize_yes__has_steps__ancestor_node_path(obj:Cyes__has_steps__ancestor_node_path, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.inferences.parent_node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.node_type)(detach) !== undefined || detach);
	finalize_ancestor_node_path(obj.properties.tail, detach);
}
function finalize_ancestor_node_path(obj:Cancestor_node_path, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.input.node_type)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.entity)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.node_type)(detach) !== undefined || detach);
	switch (obj.properties.has_steps.state.name) {
		case 'no': finalize_no__has_steps__ancestor_node_path(obj.properties.has_steps.state.node, detach); break;
		case 'yes': finalize_yes__has_steps__ancestor_node_path(obj.properties.has_steps.state.node, detach); break;
	}
}
function finalize_collection__type__properties(obj:Ccollection__type__properties, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Ccollection__type__property>obj.inferences.collection)(detach) !== undefined || detach);
}
function finalize_file__type__properties(obj:Cfile__type__properties, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cfile__type__property>obj.inferences.file)(detach) !== undefined || detach);
}
function finalize_group__type__properties(obj:Cgroup__type__properties, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgroup__type__property>obj.inferences.group)(detach) !== undefined || detach);
	finalize_duplication_node_mapping(obj.properties.mapping, detach);
}
function finalize_number__type__properties(obj:Cnumber__type__properties, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cnumber__type__property__type__attributes>obj.inferences.number)(detach) !== undefined || detach);
}
function finalize_states__state_group__type__properties(obj:Cstates__state_group__type__properties, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cstates__state_group__type__property__type__attributes>(obj.key as any).resolve)(detach) !== undefined || detach);
	finalize_duplication_node_mapping(obj.properties.mapping, detach);
}
function finalize_state_group__type__properties(obj:Cstate_group__type__properties, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cstate_group__type__property__type__attributes>obj.inferences.non_verifiable)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cstate_group__type__property__type__attributes>obj.inferences.state_group)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.states) {
		finalize_states__state_group__type__properties(entry, detach);
	}
	if (!detach) {
		(obj.properties.states as any).eval_required_keys(detach);
	}
}
function finalize_text__type__properties(obj:Ctext__type__properties, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Ctext__type__property__type__attributes>obj.inferences.text)(detach) !== undefined || detach);
}
function finalize_properties__duplication_node_mapping(obj:Cproperties__duplication_node_mapping, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cattributes>(obj.key as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cproperty__type__attributes>obj.key.inferences.property)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'collection': finalize_collection__type__properties(obj.properties.type.state.node, detach); break;
		case 'file': finalize_file__type__properties(obj.properties.type.state.node, detach); break;
		case 'group': finalize_group__type__properties(obj.properties.type.state.node, detach); break;
		case 'number': finalize_number__type__properties(obj.properties.type.state.node, detach); break;
		case 'state group': finalize_state_group__type__properties(obj.properties.type.state.node, detach); break;
		case 'text': finalize_text__type__properties(obj.properties.type.state.node, detach); break;
	}
}
function finalize_duplication_node_mapping(obj:Cduplication_node_mapping, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.input.context_node)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.properties) {
		finalize_properties__duplication_node_mapping(entry, detach);
	}
	if (!detach) {
	}
}
function finalize_no__more_entries(obj:Cno__more_entries, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cno__more_entries>obj.inferences.no)(detach) !== undefined || detach);
}
function finalize_yes__more_entries(obj:Cyes__more_entries, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cyes__more_entries>obj.inferences.yes)(detach) !== undefined || detach);
	finalize_entry_expression_list(obj.properties.tail, detach);
}
function finalize_entry_expression_list(obj:Centry_expression_list, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cui_entry_expression_list>obj.input.entry_list)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Caction__type>obj.input.gui_action)(detach) !== undefined || detach);
	finalize_gui_action_expression(obj.properties.expression, detach);
	switch (obj.properties.more_entries.state.name) {
		case 'no': finalize_no__more_entries(obj.properties.more_entries.state.node, detach); break;
		case 'yes': finalize_yes__more_entries(obj.properties.more_entries.state.node, detach); break;
	}
}
function finalize_no__has_steps__file_name_expression(obj:Cno__has_steps__file_name_expression, detach:boolean = false) {
}
function finalize_number__type__property__type__yes(obj:Cnumber__type__property__type__yes, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cnumber__type__property__type__attributes>(obj.properties.number as any).resolve)(detach) !== undefined || detach);
}
function finalize_states__state_group__type__property__type__yes(obj:Cstates__state_group__type__property__type__yes, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cstates__state_group__type__property__type__attributes>(obj.key as any).resolve)(detach) !== undefined || detach);
	finalize_file_name_expression(obj.properties.state_file_name_expression, detach);
}
function finalize_state_group__type__property__type__yes(obj:Cstate_group__type__property__type__yes, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cstate_group__type__property__type__attributes>(obj.properties.state_group as any).resolve)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.states) {
		finalize_states__state_group__type__property__type__yes(entry, detach);
	}
	if (!detach) {
		(obj.properties.states as any).eval_required_keys(detach);
	}
}
function finalize_text__type__property__type__yes(obj:Ctext__type__property__type__yes, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Ctext__type__property__type__attributes>(obj.properties.text as any).resolve)(detach) !== undefined || detach);
}
function finalize_property__type__yes(obj:Cproperty__type__yes, detach:boolean = false) {
	finalize_gui_static_singular_path(obj.properties.path, detach);
	switch (obj.properties.type.state.name) {
		case 'number': finalize_number__type__property__type__yes(obj.properties.type.state.node, detach); break;
		case 'state group': finalize_state_group__type__property__type__yes(obj.properties.type.state.node, detach); break;
		case 'text': finalize_text__type__property__type__yes(obj.properties.type.state.node, detach); break;
	}
}
function finalize_static__type__yes(obj:Cstatic__type__yes, detach:boolean = false) {
}
function finalize_yes__has_steps__file_name_expression(obj:Cyes__has_steps__file_name_expression, detach:boolean = false) {
	finalize_file_name_expression(obj.properties.tail, detach);
	switch (obj.properties.type.state.name) {
		case 'property': finalize_property__type__yes(obj.properties.type.state.node, detach); break;
		case 'static': finalize_static__type__yes(obj.properties.type.state.node, detach); break;
	}
}
function finalize_file_name_expression(obj:Cfile_name_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.input.gui_node)(detach) !== undefined || detach);
	switch (obj.properties.has_steps.state.name) {
		case 'no': finalize_no__has_steps__file_name_expression(obj.properties.has_steps.state.node, detach); break;
		case 'yes': finalize_yes__has_steps__file_name_expression(obj.properties.has_steps.state.node, detach); break;
	}
}
function finalize_no__has_next_action(obj:Cno__has_next_action, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cno__has_next_action>obj.inferences.no)(detach) !== undefined || detach);
}
function finalize_yes__has_next_action(obj:Cyes__has_next_action, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cyes__has_next_action>obj.inferences.yes)(detach) !== undefined || detach);
	finalize_gui_action(obj.properties.action, detach);
}
function finalize_gui_action(obj:Cgui_action, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cui_action>obj.input.action)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Caction__type>obj.input.gui_action)(detach) !== undefined || detach);
	finalize_gui_action_expression(obj.properties.expression, detach);
	switch (obj.properties.has_next_action.state.name) {
		case 'no': finalize_no__has_next_action(obj.properties.has_next_action.state.node, detach); break;
		case 'yes': finalize_yes__has_next_action(obj.properties.has_next_action.state.node, detach); break;
	}
}
function finalize_node__collection_type(obj:Cnode__collection_type, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.inferences.node_collection)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cviews>(obj.properties.bind_view as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.properties.bind_view.inferences.constraint)(detach) !== undefined || detach);
}
function finalize_parameter(obj:Cparameter, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.inferences.parameter_collection)(detach) !== undefined || detach);
}
function finalize_create(obj:Ccreate, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccreate__type__entry__operation__ui_expression>obj.inferences.create)(detach) !== undefined || detach);
	switch (obj.properties.collection_type.state.name) {
		case 'node': finalize_node__collection_type(obj.properties.collection_type.state.node, detach); break;
		case 'parameter': finalize_parameter(obj.properties.collection_type.state.node, detach); break;
	}
	finalize_object_expression(obj.properties.expression, detach);
}
function finalize_delete(obj:Cdelete, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cdelete__type__entry__operation__ui_expression>obj.inferences.delete_)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.inferences.entry_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cviews>(obj.properties.bind_view as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.properties.bind_view.inferences.constraint)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.properties.bind_view.inferences.parent_node)(detach) !== undefined || detach);
}
function finalize_entry(obj:Centry, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Centry__operation__ui_expression>obj.inferences.entry)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'create': finalize_create(obj.properties.type.state.node, detach); break;
		case 'delete': finalize_delete(obj.properties.type.state.node, detach); break;
	}
}
function finalize_entry_list(obj:Centry_list, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Centry_list>obj.inferences.entry_list)(detach) !== undefined || detach);
	finalize_entry_expression_list(obj.properties.entries, detach);
}
function finalize_action__operation(obj:Caction__operation, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Caction>obj.inferences.action)(detach) !== undefined || detach);
	finalize_gui_action(obj.properties.sub_action, detach);
}
function finalize_command__operation(obj:Ccommand__operation, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccommand__type>obj.inferences.command)(detach) !== undefined || detach);
}
function finalize_execute_operation(obj:Cexecute_operation, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cexecute_operation__operation__ui_expression>obj.inferences.execute_operation)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cviews>(obj.properties.bind_view as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.properties.bind_view.inferences.constraint)(detach) !== undefined || detach);
	finalize_object_expression(obj.properties.expression, detach);
	switch (obj.properties.operation.state.name) {
		case 'action': finalize_action__operation(obj.properties.operation.state.node, detach); break;
		case 'command': finalize_command__operation(obj.properties.operation.state.node, detach); break;
	}
}
function finalize_ignore(obj:Cignore, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cignore__operation__ui_expression>obj.inferences.ignore)(detach) !== undefined || detach);
}
function finalize_false_case(obj:Cfalse_case, detach:boolean = false) {
	finalize_gui_action_expression(obj.properties.expression, detach);
}
function finalize_true_case(obj:Ctrue_case, detach:boolean = false) {
	finalize_gui_action_expression(obj.properties.expression, detach);
}
function finalize_cases(obj:Ccases, detach:boolean = false) {
	finalize_false_case(obj.properties.false_case, detach);
	finalize_true_case(obj.properties.true_case, detach);
}
function finalize_node_switch(obj:Cnode_switch, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode_switch__operation__ui_expression>obj.inferences.node_switch)(detach) !== undefined || detach);
	finalize_cases(obj.properties.cases, detach);
}
function finalize_object(obj:Cobject, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cobject__value__produce_value__operation__ui_expression>obj.inferences.object)(detach) !== undefined || detach);
	finalize_object_expression(obj.properties.expression, detach);
}
function finalize_scalar(obj:Cscalar, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cscalar__value__produce_value__operation__ui_expression>obj.inferences.scalar)(detach) !== undefined || detach);
}
function finalize_state_group__value(obj:Cstate_group__value, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cstate__value__produce_value__operation__ui_expression>obj.inferences.state_group)(detach) !== undefined || detach);
	finalize_object_expression(obj.properties.expression, detach);
}
function finalize_produce_value(obj:Cproduce_value, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cproduce_value__operation__ui_expression>obj.inferences.produce_value)(detach) !== undefined || detach);
	switch (obj.properties.value.state.name) {
		case 'object': finalize_object(obj.properties.value.state.node, detach); break;
		case 'scalar': finalize_scalar(obj.properties.value.state.node, detach); break;
		case 'state group': finalize_state_group__value(obj.properties.value.state.node, detach); break;
	}
}
function finalize_states__state_switch(obj:Cstates__state_switch, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cstates__state_switch__operation__ui_expression>(obj.key as any).resolve)(detach) !== undefined || detach);
	finalize_gui_action_expression(obj.properties.expression, detach);
}
function finalize_state_switch__operation(obj:Cstate_switch__operation, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cstate_switch__operation__ui_expression>obj.inferences.state_switch)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.states) {
		finalize_states__state_switch(entry, detach);
	}
	if (!detach) {
		(obj.properties.states as any).eval_required_keys(detach);
	}
}
function finalize_node__target(obj:Cnode__target, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__target__update_properties__operation__ui_expression>obj.inferences.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cviews>(obj.properties.bind_view as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.properties.bind_view.inferences.constraint)(detach) !== undefined || detach);
	finalize_object_expression(obj.properties.expression, detach);
}
function finalize_available(obj:Cavailable, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cviews>(obj.properties.bind_view as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.properties.bind_view.inferences.constraint)(detach) !== undefined || detach);
}
function finalize_unavailable(obj:Cunavailable, detach:boolean = false) {
}
function finalize_property__target(obj:Cproperty__target, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cproperty__target__update_properties__operation__ui_expression>obj.inferences.property)(detach) !== undefined || detach);
	switch (obj.properties.action_view.state.name) {
		case 'available': finalize_available(obj.properties.action_view.state.node, detach); break;
		case 'unavailable': finalize_unavailable(obj.properties.action_view.state.node, detach); break;
	}
	finalize_gui_action_expression(obj.properties.expression, detach);
}
function finalize_update_properties(obj:Cupdate_properties, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cupdate_properties__operation__ui_expression>obj.inferences.update_properties)(detach) !== undefined || detach);
	switch (obj.properties.target.state.name) {
		case 'node': finalize_node__target(obj.properties.target.state.node, detach); break;
		case 'property': finalize_property__target(obj.properties.target.state.node, detach); break;
	}
}
function finalize_walk(obj:Cwalk, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cwalk__operation__ui_expression>obj.inferences.walk)(detach) !== undefined || detach);
	finalize_gui_action_expression(obj.properties.expression, detach);
}
function finalize_gui_action_expression(obj:Cgui_action_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cui_expression>obj.input.action)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Caction__type>obj.input.gui_action)(detach) !== undefined || detach);
	switch (obj.properties.operation.state.name) {
		case 'entry': finalize_entry(obj.properties.operation.state.node, detach); break;
		case 'entry list': finalize_entry_list(obj.properties.operation.state.node, detach); break;
		case 'execute operation': finalize_execute_operation(obj.properties.operation.state.node, detach); break;
		case 'ignore': finalize_ignore(obj.properties.operation.state.node, detach); break;
		case 'node switch': finalize_node_switch(obj.properties.operation.state.node, detach); break;
		case 'produce value': finalize_produce_value(obj.properties.operation.state.node, detach); break;
		case 'state switch': finalize_state_switch__operation(obj.properties.operation.state.node, detach); break;
		case 'update properties': finalize_update_properties(obj.properties.operation.state.node, detach); break;
		case 'walk': finalize_walk(obj.properties.operation.state.node, detach); break;
	}
}
function finalize_gui_context(obj:Cgui_context, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.input.gui_entity)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.gui_entity)(detach) !== undefined || detach);
}
function finalize_views(obj:Cviews, detach:boolean = false) {
	finalize_gui_node_type_path(obj.properties.view_context, detach);
}
function finalize_action__type(obj:Caction__type, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Caction>obj.inferences.action_type)(detach) !== undefined || detach);
	finalize_operation(obj.definitions.operation, detach);
	finalize_gui_action(obj.properties.action, detach);
	finalize_gui_node(obj.properties.parameters, detach);
	for (const [_key, entry] of obj.properties.views) {
		finalize_views(entry, detach);
	}
	if (!detach) {
	}
}
function finalize_component(obj:Ccomponent, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccomponent>obj.inferences.component_type)(detach) !== undefined || detach);
}
function finalize_global(obj:Cglobal, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cglobal>obj.inferences.global_type)(detach) !== undefined || detach);
	finalize_gui_node(obj.properties.parameters, detach);
}
function finalize_command__type(obj:Ccommand__type, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccommand__type>obj.inferences.command_type)(detach) !== undefined || detach);
	finalize_operation(obj.definitions.operation, detach);
	switch (obj.properties.type.state.name) {
		case 'component': finalize_component(obj.properties.type.state.node, detach); break;
		case 'global': finalize_global(obj.properties.type.state.node, detach); break;
	}
}
function finalize_no__default__collection(obj:Cno__default__collection, detach:boolean = false) {
}
function finalize_yes__default__collection(obj:Cyes__default__collection, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cyes__default__ui_collection_property>obj.inferences.model_expression)(detach) !== undefined || detach);
}
function finalize_from_current_context(obj:Cfrom_current_context, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cbase_value_source>obj.inferences.elementary_node)(detach) !== undefined || detach);
	finalize_duplication_node_mapping(obj.properties.mapping, detach);
}
function finalize_none__duplication_mapping(obj:Cnone__duplication_mapping, detach:boolean = false) {
}
function finalize_no__has_ordered_graphs(obj:Cno__has_ordered_graphs, detach:boolean = false) {
}
function finalize_ordered_graphs(obj:Cordered_graphs, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cgraphs>(obj.key as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cordered>obj.key.inferences.ordered_graph)(detach) !== undefined || detach);
}
function finalize_yes__has_ordered_graphs(obj:Cyes__has_ordered_graphs, detach:boolean = false) {
	for (const [_key, entry] of obj.properties.ordered_graphs) {
		finalize_ordered_graphs(entry, detach);
	}
	if (!detach) {
	}
}
function finalize_collection__type__property(obj:Ccollection__type__property, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ccollection__type__property>obj.inferences.collection)(detach) !== undefined || detach);
	finalize_gui_node_location(obj.definitions.gui_node_location, detach);
	switch (obj.properties.default_.state.name) {
		case 'no': finalize_no__default__collection(obj.properties.default_.state.node, detach); break;
		case 'yes': finalize_yes__default__collection(obj.properties.default_.state.node, detach); break;
	}
	switch (obj.properties.duplication_mapping.state.name) {
		case 'from current context': finalize_from_current_context(obj.properties.duplication_mapping.state.node, detach); break;
		case 'none': finalize_none__duplication_mapping(obj.properties.duplication_mapping.state.node, detach); break;
	}
	switch (obj.properties.has_ordered_graphs.state.name) {
		case 'no': finalize_no__has_ordered_graphs(obj.properties.has_ordered_graphs.state.node, detach); break;
		case 'yes': finalize_yes__has_ordered_graphs(obj.properties.has_ordered_graphs.state.node, detach); break;
	}
	finalize_gui_node(obj.properties.node, detach);
	assert((<(detach?:boolean) => gui_model.Cattributes>obj.properties.node.inferences.key_property)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Ctext__type__property__type__attributes>obj.properties.node.inferences.text_key)(detach) !== undefined || detach);
}
function finalize_expression__file_name_source(obj:Cexpression__file_name_source, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cyes__file_name_expression>obj.inferences.expression)(detach) !== undefined || detach);
}
function finalize_generated(obj:Cgenerated, detach:boolean = false) {
	finalize_file_name_expression(obj.properties.file_name_expression, detach);
}
function finalize_no__has_extension_validation(obj:Cno__has_extension_validation, detach:boolean = false) {
}
function finalize_yes__has_extension_validation(obj:Cyes__has_extension_validation, detach:boolean = false) {
	finalize_text_validation(obj.properties.rules, detach);
}
function finalize_file__type__property(obj:Cfile__type__property, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cfile>obj.inferences.file)(detach) !== undefined || detach);
	switch (obj.properties.file_name_source.state.name) {
		case 'expression': finalize_expression__file_name_source(obj.properties.file_name_source.state.node, detach); break;
		case 'generated': finalize_generated(obj.properties.file_name_source.state.node, detach); break;
	}
	switch (obj.properties.has_extension_validation.state.name) {
		case 'no': finalize_no__has_extension_validation(obj.properties.has_extension_validation.state.node, detach); break;
		case 'yes': finalize_yes__has_extension_validation(obj.properties.has_extension_validation.state.node, detach); break;
	}
}
function finalize_group__type__property(obj:Cgroup__type__property, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cgroup__type__property>obj.inferences.group)(detach) !== undefined || detach);
	finalize_gui_node_location(obj.definitions.gui_node_location, detach);
	finalize_gui_node(obj.properties.gui_node, detach);
}
function finalize_no__default__number(obj:Cno__default__number, detach:boolean = false) {
}
function finalize_expression__value(obj:Cexpression__value, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cyes__default__ui_number_property>obj.inferences.model_expression)(detach) !== undefined || detach);
}
function finalize_now__value(obj:Cnow__value, detach:boolean = false) {
}
function finalize_one(obj:Cone, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cpositive__set_type>obj.inferences.positive)(detach) !== undefined || detach);
}
function finalize_today__value(obj:Ctoday__value, detach:boolean = false) {
}
function finalize_zero(obj:Czero, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cinteger__set_type>obj.inferences.integer)(detach) !== undefined || detach);
}
function finalize_yes__default__number(obj:Cyes__default__number, detach:boolean = false) {
	switch (obj.properties.value.state.name) {
		case 'expression': finalize_expression__value(obj.properties.value.state.node, detach); break;
		case 'now': finalize_now__value(obj.properties.value.state.node, detach); break;
		case 'one': finalize_one(obj.properties.value.state.node, detach); break;
		case 'today': finalize_today__value(obj.properties.value.state.node, detach); break;
		case 'zero': finalize_zero(obj.properties.value.state.node, detach); break;
	}
}
function finalize_no__summarize(obj:Cno__summarize, detach:boolean = false) {
}
function finalize_total(obj:Ctotal, detach:boolean = false) {
}
function finalize_expression__has_maximum(obj:Cexpression__has_maximum, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cyes__has_maximum>obj.inferences.model_expression)(detach) !== undefined || detach);
}
function finalize_no__has_maximum(obj:Cno__has_maximum, detach:boolean = false) {
}
function finalize_yes__has_maximum(obj:Cyes__has_maximum, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cbase__type__number>obj.inferences.constrain)(detach) !== undefined || detach);
	finalize_number_limit(obj.properties.maximum, detach);
}
function finalize_expression__has_minimum(obj:Cexpression__has_minimum, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cyes__has_minimum>obj.inferences.model_expression)(detach) !== undefined || detach);
}
function finalize_no__has_minimum(obj:Cno__has_minimum, detach:boolean = false) {
}
function finalize_yes__has_minimum(obj:Cyes__has_minimum, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cbase__type__number>obj.inferences.constrain)(detach) !== undefined || detach);
	finalize_number_limit(obj.properties.minimum, detach);
}
function finalize_validation(obj:Cvalidation, detach:boolean = false) {
	switch (obj.properties.has_maximum.state.name) {
		case 'expression': finalize_expression__has_maximum(obj.properties.has_maximum.state.node, detach); break;
		case 'no': finalize_no__has_maximum(obj.properties.has_maximum.state.node, detach); break;
		case 'yes': finalize_yes__has_maximum(obj.properties.has_maximum.state.node, detach); break;
	}
	switch (obj.properties.has_minimum.state.name) {
		case 'expression': finalize_expression__has_minimum(obj.properties.has_minimum.state.node, detach); break;
		case 'no': finalize_no__has_minimum(obj.properties.has_minimum.state.node, detach); break;
		case 'yes': finalize_yes__has_minimum(obj.properties.has_minimum.state.node, detach); break;
	}
}
function finalize_number__type__property__type__attributes(obj:Cnumber__type__property__type__attributes, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnumber__type__property>obj.inferences.number)(detach) !== undefined || detach);
	switch (obj.properties.default_.state.name) {
		case 'no': finalize_no__default__number(obj.properties.default_.state.node, detach); break;
		case 'yes': finalize_yes__default__number(obj.properties.default_.state.node, detach); break;
	}
	assert((<(detach?:boolean) => gui_model.Cnumerical_types>(obj.properties.numerical_type as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnumerical_types>obj.properties.numerical_type.inferences.constraint)(detach) !== undefined || detach);
	switch (obj.properties.summarize.state.name) {
		case 'no': finalize_no__summarize(obj.properties.summarize.state.node, detach); break;
		case 'total': finalize_total(obj.properties.summarize.state.node, detach); break;
	}
	finalize_validation(obj.properties.validation, detach);
}
function finalize_no__default__state_group(obj:Cno__default__state_group, detach:boolean = false) {
}
function finalize_state__source(obj:Cstate__source, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cstates__state_group__type__property__type__attributes>(obj.properties.state as any).resolve)(detach) !== undefined || detach);
}
function finalize_state_switch__source(obj:Cstate_switch__source, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cyes__default_state>obj.inferences.model_state_switch)(detach) !== undefined || detach);
}
function finalize_yes__default__state_group(obj:Cyes__default__state_group, detach:boolean = false) {
	switch (obj.properties.source.state.name) {
		case 'state': finalize_state__source(obj.properties.source.state.node, detach); break;
		case 'state switch': finalize_state_switch__source(obj.properties.source.state.node, detach); break;
	}
}
function finalize_no__has_states(obj:Cno__has_states, detach:boolean = false) {
}
function finalize_yes__has_states(obj:Cyes__has_states, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cstates__state_group__type__property__type__attributes>(obj.properties.first as any).resolve)(detach) !== undefined || detach);
}
function finalize_no__has_successor(obj:Cno__has_successor, detach:boolean = false) {
}
function finalize_yes__has_successor(obj:Cyes__has_successor, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cstates__state_group__type__property__type__attributes>(obj.properties.successor as any).resolve)(detach) !== undefined || detach);
}
function finalize_states__state_group__type__property__type__attributes(obj:Cstates__state_group__type__property__type__attributes, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cstates__state_group>(obj.key as any).resolve)(detach) !== undefined || detach);
	finalize_gui_node_location(obj.definitions.gui_node_location, detach);
	finalize_gui_node(obj.properties.gui_node, detach);
	switch (obj.properties.has_successor.state.name) {
		case 'no': finalize_no__has_successor(obj.properties.has_successor.state.node, detach); break;
		case 'yes': finalize_yes__has_successor(obj.properties.has_successor.state.node, detach); break;
	}
	finalize_where_rules(obj.properties.rules, detach);
}
function finalize_state_group__type__property__type__attributes(obj:Cstate_group__type__property__type__attributes, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cstate_group>obj.inferences.state_group)(detach) !== undefined || detach);
	switch (obj.properties.default_.state.name) {
		case 'no': finalize_no__default__state_group(obj.properties.default_.state.node, detach); break;
		case 'yes': finalize_yes__default__state_group(obj.properties.default_.state.node, detach); break;
	}
	switch (obj.properties.has_states.state.name) {
		case 'no': finalize_no__has_states(obj.properties.has_states.state.node, detach); break;
		case 'yes': finalize_yes__has_states(obj.properties.has_states.state.node, detach); break;
	}
	for (const [_key, entry] of obj.properties.states) {
		finalize_states__state_group__type__property__type__attributes(entry, detach);
	}
	if (!detach) {
		(obj.properties.states as any).eval_required_keys(detach);
	}
}
function finalize_no__default__text(obj:Cno__default__text, detach:boolean = false) {
}
function finalize_auto_select_only_candidate(obj:Cauto_select_only_candidate, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cyes__navigable>obj.inferences.reference)(detach) !== undefined || detach);
}
function finalize_current_user(obj:Ccurrent_user, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cyes__has_dynamic_users>obj.inferences.has_dynamic_users)(detach) !== undefined || detach);
}
function finalize_expression__source(obj:Cexpression__source, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cyes__default_value>obj.inferences.text_with_default)(detach) !== undefined || detach);
}
function finalize_guid(obj:Cguid, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cno__navigable>obj.inferences.no_constraint)(detach) !== undefined || detach);
}
function finalize_select_source(obj:Cselect_source, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Ccollection__type__property>obj.inferences.collection_entity)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cyes__has_ordered_graphs>obj.inferences.has_ordered_graphs)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cyes__navigable>obj.inferences.reference)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cordered_graphs>(obj.properties.graph as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cyes__has_reference>obj.properties.graph.inferences.is_edge_reference)(detach) !== undefined || detach);
}
function finalize_yes__default__text(obj:Cyes__default__text, detach:boolean = false) {
	switch (obj.properties.source.state.name) {
		case 'auto select only candidate': finalize_auto_select_only_candidate(obj.properties.source.state.node, detach); break;
		case 'current user': finalize_current_user(obj.properties.source.state.node, detach); break;
		case 'expression': finalize_expression__source(obj.properties.source.state.node, detach); break;
		case 'guid': finalize_guid(obj.properties.source.state.node, detach); break;
		case 'select source': finalize_select_source(obj.properties.source.state.node, detach); break;
	}
}
function finalize_no__password_property(obj:Cno__password_property, detach:boolean = false) {
}
function finalize_yes__password_property(obj:Cyes__password_property, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cyes__has_password_authentication>obj.inferences.password)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cattributes>obj.inferences.valid_property)(detach) !== undefined || detach);
}
function finalize_no__navigable(obj:Cno__navigable, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_context>obj.output.context)(detach) !== undefined || detach);
	switch (obj.properties.password_property.state.name) {
		case 'no': finalize_no__password_property(obj.properties.password_property.state.node, detach); break;
		case 'yes': finalize_yes__password_property(obj.properties.password_property.state.node, detach); break;
	}
}
function finalize_mandatory(obj:Cmandatory, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cmandatory__type>obj.inferences.mandatory)(detach) !== undefined || detach);
}
function finalize_optional(obj:Coptional, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Coptional__type>obj.inferences.optional)(detach) !== undefined || detach);
}
function finalize_no__validate__yes(obj:Cno__validate__yes, detach:boolean = false) {
}
function finalize_yes__validate__yes(obj:Cyes__validate__yes, detach:boolean = false) {
}
function finalize_yes__navigable(obj:Cyes__navigable, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cyes__has_reference>obj.inferences.reference)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_context>obj.output.context)(detach) !== undefined || detach);
	finalize_gui_node_type_path(obj.properties.referenced_node, detach);
	assert((<(detach?:boolean) => gui_model.Ccollection__type__property>obj.properties.referenced_node.inferences.referenced_collection)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.properties.referenced_node.inferences.referenced_node_type)(detach) !== undefined || detach);
	finalize_where_rules(obj.properties.rules, detach);
	switch (obj.properties.type.state.name) {
		case 'mandatory': finalize_mandatory(obj.properties.type.state.node, detach); break;
		case 'optional': finalize_optional(obj.properties.type.state.node, detach); break;
	}
	switch (obj.properties.validate.state.name) {
		case 'no': finalize_no__validate__yes(obj.properties.validate.state.node, detach); break;
		case 'yes': finalize_yes__validate__yes(obj.properties.validate.state.node, detach); break;
	}
}
function finalize_custom(obj:Ccustom, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cbase__type__text>obj.inferences.elementary)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cno__has_reference>obj.inferences.unconstrained)(detach) !== undefined || detach);
	finalize_text_validation(obj.properties.rules, detach);
}
function finalize_model__validation(obj:Cmodel__validation, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cyes__has_validation__ui_text_property>obj.inferences.text_with_validation)(detach) !== undefined || detach);
}
function finalize_none__validation(obj:Cnone__validation, detach:boolean = false) {
}
function finalize_text__type__property__type__attributes(obj:Ctext__type__property__type__attributes, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Ctext__type__property>obj.inferences.text)(detach) !== undefined || detach);
	switch (obj.properties.default_.state.name) {
		case 'no': finalize_no__default__text(obj.properties.default_.state.node, detach); break;
		case 'yes': finalize_yes__default__text(obj.properties.default_.state.node, detach); break;
	}
	switch (obj.properties.navigable.state.name) {
		case 'no': finalize_no__navigable(obj.properties.navigable.state.node, detach); break;
		case 'yes': finalize_yes__navigable(obj.properties.navigable.state.node, detach); break;
	}
	switch (obj.properties.validation.state.name) {
		case 'custom': finalize_custom(obj.properties.validation.state.node, detach); break;
		case 'model': finalize_model__validation(obj.properties.validation.state.node, detach); break;
		case 'none': finalize_none__validation(obj.properties.validation.state.node, detach); break;
	}
}
function finalize_property__type__attributes(obj:Cproperty__type__attributes, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cproperty__type__attributes>obj.inferences.property_type)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'collection': finalize_collection__type__property(obj.properties.type.state.node, detach); break;
		case 'file': finalize_file__type__property(obj.properties.type.state.node, detach); break;
		case 'group': finalize_group__type__property(obj.properties.type.state.node, detach); break;
		case 'number': finalize_number__type__property__type__attributes(obj.properties.type.state.node, detach); break;
		case 'state group': finalize_state_group__type__property__type__attributes(obj.properties.type.state.node, detach); break;
		case 'text': finalize_text__type__property__type__attributes(obj.properties.type.state.node, detach); break;
	}
}
function finalize_reference_set(obj:Creference_set, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Creference_set__type__attributes>obj.inferences.reference_set_type)(detach) !== undefined || detach);
}
function finalize_attributes(obj:Cattributes, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cattributes>(obj.key as any).resolve)(detach) !== undefined || detach);
	finalize_member(obj.definitions.member, detach);
	switch (obj.properties.type.state.name) {
		case 'action': finalize_action__type(obj.properties.type.state.node, detach); break;
		case 'command': finalize_command__type(obj.properties.type.state.node, detach); break;
		case 'property': finalize_property__type__attributes(obj.properties.type.state.node, detach); break;
		case 'reference set': finalize_reference_set(obj.properties.type.state.node, detach); break;
	}
}
function finalize_gui_node(obj:Cgui_node, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.input.application_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.input.gui_entity)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node_location>obj.input.location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cmember>obj.input.member)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.application_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.gui_entity)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node_location>obj.output.location)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cmember>obj.output.member)(detach) !== undefined || detach);
	finalize_gui_context(obj.definitions.gui_context, detach);
	finalize_member_parent(obj.definitions.member_parent, detach);
	for (const [_key, entry] of obj.properties.attributes) {
		finalize_attributes(entry, detach);
	}
	if (!detach) {
		(obj.properties.attributes as any).eval_required_keys(detach);
	}
}
function finalize_gui_node_location(obj:Cgui_node_location, detach:boolean = false) {
}
function finalize_gui_node_type_path(obj:Cgui_node_type_path, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnode__application>obj.output.result_application_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.result_gui_node)(detach) !== undefined || detach);
	finalize_gui_node_type_path_step(obj.properties.steps, detach);
}
function finalize_no__has_steps__gui_node_type_path_step(obj:Cno__has_steps__gui_node_type_path_step, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.result_node)(detach) !== undefined || detach);
}
function finalize_node__type(obj:Cnode__type, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.inferences.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Coptional_node>obj.inferences.optional_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.result_node)(detach) !== undefined || detach);
}
function finalize_state__type(obj:Cstate__type, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cstate_group__type__property__type__attributes>obj.inferences.state_group)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.result_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cstates__state_group__type__property__type__attributes>(obj.properties.state as any).resolve)(detach) !== undefined || detach);
}
function finalize_yes__has_steps__gui_node_type_path_step(obj:Cyes__has_steps__gui_node_type_path_step, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.result_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cattributes>(obj.properties.attribute as any).resolve)(detach) !== undefined || detach);
	finalize_gui_node_type_path_step(obj.properties.tail, detach);
	switch (obj.properties.type.state.name) {
		case 'node': finalize_node__type(obj.properties.type.state.node, detach); break;
		case 'state': finalize_state__type(obj.properties.type.state.node, detach); break;
	}
}
function finalize_gui_node_type_path_step(obj:Cgui_node_type_path_step, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.input.context_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.result_node)(detach) !== undefined || detach);
	switch (obj.properties.has_steps.state.name) {
		case 'no': finalize_no__has_steps__gui_node_type_path_step(obj.properties.has_steps.state.node, detach); break;
		case 'yes': finalize_yes__has_steps__gui_node_type_path_step(obj.properties.has_steps.state.node, detach); break;
	}
}
function finalize_no__has_steps__gui_static_conditional_path(obj:Cno__has_steps__gui_static_conditional_path, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.node)(detach) !== undefined || detach);
}
function finalize_group__step(obj:Cgroup__step, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgroup__type__property>(obj.properties.group as any).resolve)(detach) !== undefined || detach);
}
function finalize_state__step(obj:Cstate__step, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cstates__state_group__type__property__type__attributes>(obj.properties.state as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cstate_group__type__property__type__attributes>(obj.properties.stategroup as any).resolve)(detach) !== undefined || detach);
}
function finalize_yes__has_steps__gui_static_conditional_path(obj:Cyes__has_steps__gui_static_conditional_path, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.node)(detach) !== undefined || detach);
	switch (obj.properties.step.state.name) {
		case 'group': finalize_group__step(obj.properties.step.state.node, detach); break;
		case 'state': finalize_state__step(obj.properties.step.state.node, detach); break;
	}
	finalize_gui_static_conditional_path(obj.properties.tail, detach);
}
function finalize_gui_static_conditional_path(obj:Cgui_static_conditional_path, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.input.node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.node)(detach) !== undefined || detach);
	switch (obj.properties.has_steps.state.name) {
		case 'no': finalize_no__has_steps__gui_static_conditional_path(obj.properties.has_steps.state.node, detach); break;
		case 'yes': finalize_yes__has_steps__gui_static_conditional_path(obj.properties.has_steps.state.node, detach); break;
	}
}
function finalize_no__has_steps__gui_static_singular_path(obj:Cno__has_steps__gui_static_singular_path, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.result_node)(detach) !== undefined || detach);
}
function finalize_group__type__yes(obj:Cgroup__type__yes, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.result_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgroup__type__property>(obj.properties.group as any).resolve)(detach) !== undefined || detach);
}
function finalize_yes__has_steps__gui_static_singular_path(obj:Cyes__has_steps__gui_static_singular_path, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.result_node)(detach) !== undefined || detach);
	finalize_gui_static_singular_path(obj.properties.tail, detach);
	switch (obj.properties.type.state.name) {
		case 'group': finalize_group__type__yes(obj.properties.type.state.node, detach); break;
	}
}
function finalize_gui_static_singular_path(obj:Cgui_static_singular_path, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.input.gui_node)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cgui_node>obj.output.result_node)(detach) !== undefined || detach);
	switch (obj.properties.has_steps.state.name) {
		case 'no': finalize_no__has_steps__gui_static_singular_path(obj.properties.has_steps.state.node, detach); break;
		case 'yes': finalize_yes__has_steps__gui_static_singular_path(obj.properties.has_steps.state.node, detach); break;
	}
	finalize_ancestor_node_path(obj.properties.head, detach);
}
function finalize_member(obj:Cmember, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cmember_parent>obj.input.parent)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => gui_model.Cmember_parent>obj.output.parent)(detach) !== undefined || detach);
}
function finalize_member_parent(obj:Cmember_parent, detach:boolean = false) {
}
function finalize_now__base(obj:Cnow__base, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cdate_and_time>obj.inferences.date_and_time_type)(detach) !== undefined || detach);
}
function finalize_today__base(obj:Ctoday__base, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cdate>obj.inferences.date_type)(detach) !== undefined || detach);
}
function finalize_no__with_offset(obj:Cno__with_offset, detach:boolean = false) {
}
function finalize_yes__with_offset(obj:Cyes__with_offset, detach:boolean = false) {
}
function finalize_dynamic(obj:Cdynamic, detach:boolean = false) {
	switch (obj.properties.base.state.name) {
		case 'now': finalize_now__base(obj.properties.base.state.node, detach); break;
		case 'today': finalize_today__base(obj.properties.base.state.node, detach); break;
	}
	switch (obj.properties.with_offset.state.name) {
		case 'no': finalize_no__with_offset(obj.properties.with_offset.state.node, detach); break;
		case 'yes': finalize_yes__with_offset(obj.properties.with_offset.state.node, detach); break;
	}
}
function finalize_static__type__number_limit(obj:Cstatic__type__number_limit, detach:boolean = false) {
}
function finalize_number_limit(obj:Cnumber_limit, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Cnumerical_types>obj.input.numerical_type)(detach) !== undefined || detach);
	switch (obj.properties.type.state.name) {
		case 'dynamic': finalize_dynamic(obj.properties.type.state.node, detach); break;
		case 'static': finalize_static__type__number_limit(obj.properties.type.state.node, detach); break;
	}
}
function finalize_properties__object_expression(obj:Cproperties__object_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cproperties__ui_object_expression>(obj.key as any).resolve)(detach) !== undefined || detach);
	finalize_gui_action_expression(obj.properties.default_, detach);
}
function finalize_object_expression(obj:Cobject_expression, detach:boolean = false) {
	assert((<(detach?:boolean) => gui_model.Caction__type>obj.input.gui_action)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cui_object_expression>obj.input.object_expression)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.properties) {
		finalize_properties__object_expression(entry, detach);
	}
	if (!detach) {
		(obj.properties.properties as any).eval_required_keys(detach);
	}
}
function finalize_operation(obj:Coperation, detach:boolean = false) {
	finalize_gui_node_location(obj.definitions.gui_node_location, detach);
}
function finalize_optional_node(obj:Coptional_node, detach:boolean = false) {
}
function finalize_text_validation(obj:Ctext_validation, detach:boolean = false) {
}
function finalize_no__validate__rules(obj:Cno__validate__rules, detach:boolean = false) {
}
function finalize_yes__validate__rules(obj:Cyes__validate__rules, detach:boolean = false) {
}
function finalize_rules(obj:Crules, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Crules>(obj.key as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.key.inferences.node)(detach) !== undefined || detach);
	finalize_gui_context(obj.definitions.gui_context, detach);
	finalize_gui_node_type_path(obj.properties.referenced_node, detach);
	assert((<(detach?:boolean) => application.Cnode__application>obj.properties.referenced_node.inferences.constraint)(detach) !== undefined || detach);
	switch (obj.properties.validate.state.name) {
		case 'no': finalize_no__validate__rules(obj.properties.validate.state.node, detach); break;
		case 'yes': finalize_yes__validate__rules(obj.properties.validate.state.node, detach); break;
	}
}
function finalize_where_rules(obj:Cwhere_rules, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cwhere_rules_definition>obj.input.definition)(detach) !== undefined || detach);
	for (const [_key, entry] of obj.properties.rules) {
		finalize_rules(entry, detach);
	}
	if (!detach) {
		(obj.properties.rules as any).eval_required_keys(detach);
	}
}
function finalize_no__has_user_node(obj:Cno__has_user_node, detach:boolean = false) {
}
function finalize_yes__has_user_node(obj:Cyes__has_user_node, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cyes__has_dynamic_users>obj.inferences.dynamic_users)(detach) !== undefined || detach);
	finalize_gui_static_conditional_path(obj.properties.context, detach);
	assert((<(detach?:boolean) => gui_model.Ccollection__type__property>(obj.properties.users as any).resolve)(detach) !== undefined || detach);
	assert((<(detach?:boolean) => application.Cattributes>obj.properties.users.inferences.users_collection)(detach) !== undefined || detach);
}
function finalize_HTML_date_and_time(obj:CHTML_date_and_time, detach:boolean = false) {
}
function finalize_date(obj:Cdate, detach:boolean = false) {
}
function finalize_date_and_time(obj:Cdate_and_time, detach:boolean = false) {
}
function finalize_hours(obj:Chours, detach:boolean = false) {
}
function finalize_minutes(obj:Cminutes, detach:boolean = false) {
}
function finalize_seconds(obj:Cseconds, detach:boolean = false) {
}
function finalize_duration(obj:Cduration, detach:boolean = false) {
	switch (obj.properties.unit.state.name) {
		case 'hours': finalize_hours(obj.properties.unit.state.node, detach); break;
		case 'minutes': finalize_minutes(obj.properties.unit.state.node, detach); break;
		case 'seconds': finalize_seconds(obj.properties.unit.state.node, detach); break;
	}
}
function finalize_currency(obj:Ccurrency, detach:boolean = false) {
}
function finalize_decimal(obj:Cdecimal, detach:boolean = false) {
}
function finalize_engineering(obj:Cengineering, detach:boolean = false) {
}
function finalize_scientific(obj:Cscientific, detach:boolean = false) {
}
function finalize_model__representation_type(obj:Cmodel__representation_type, detach:boolean = false) {
	switch (obj.properties.style.state.name) {
		case 'currency': finalize_currency(obj.properties.style.state.node, detach); break;
		case 'decimal': finalize_decimal(obj.properties.style.state.node, detach); break;
		case 'engineering': finalize_engineering(obj.properties.style.state.node, detach); break;
		case 'scientific': finalize_scientific(obj.properties.style.state.node, detach); break;
	}
}
function finalize_numerical_types(obj:Cnumerical_types, detach:boolean = false) {
	assert((<(detach?:boolean) => application.Cnumerical_types>(obj.key as any).resolve)(detach) !== undefined || detach);
	switch (obj.properties.representation_type.state.name) {
		case 'HTML date and time': finalize_HTML_date_and_time(obj.properties.representation_type.state.node, detach); break;
		case 'date': finalize_date(obj.properties.representation_type.state.node, detach); break;
		case 'date and time': finalize_date_and_time(obj.properties.representation_type.state.node, detach); break;
		case 'duration': finalize_duration(obj.properties.representation_type.state.node, detach); break;
		case 'model': finalize_model__representation_type(obj.properties.representation_type.state.node, detach); break;
	}
}
function finalize_gui_model(obj:Cgui_model, detach:boolean = false) {
	finalize_gui_node_location(obj.definitions.gui_node_location, detach);
	finalize_member(obj.definitions.member, detach);
	switch (obj.properties.has_user_node.state.name) {
		case 'no': finalize_no__has_user_node(obj.properties.has_user_node.state.node, detach); break;
		case 'yes': finalize_yes__has_user_node(obj.properties.has_user_node.state.node, detach); break;
	}
	for (const [_key, entry] of obj.properties.numerical_types) {
		finalize_numerical_types(entry, detach);
	}
	if (!detach) {
	}
	finalize_gui_node(obj.properties.root, detach);
}

export namespace Cgui_model {
	export function create(init:Tgui_model, input: {
	'application':application.Capplication}, symbols_file?: SymbolsFile):Cgui_model {
		const instance = new Cgui_model(init, input as any, symbols_file);
		return instance;
	};
}
